import React, { useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import { isMobile } from 'lib/device'
import perms from 'lib/perms'
import { v4 as uuid } from 'uuid'
import QuestControls from 'components/questions/QuestionControls'
import UrlAnswer from '../UrlAnswer'
import PostAnswer from '../PostAnswer'
import { RowGeometryEventsContext } from 'lib/rowGeometryEvents'
import { questUnarchivedAnswerCount } from 'lib/questReplyCount'
import AnswerControls, {
    AnswerShareTray,
    canShareAnswer,
    launchAnswerShare,
} from 'components/answers/AnswerControls'
import Video from '../Video'
import PostEmbed from '../PostEmbed'
import { resolvePostEmbedOpenMode } from '../PostEmbed/postEmbedOpenMode'
import FileEmbed from '../FileEmbed'
import Connector from '../../state/Connector'
import AnswerImage from './AnswerImage'
import AppLightbox from 'components/shared/Lightbox'
import api from 'api/api'
import { getAnswerExternalShareUrl } from 'utils/shareUtils'
import { observeAnonymousAnswerPresence } from 'lib/anonymousPresence'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import toast from 'utils/toast'
import { generateFingerprint } from '../../../utils'
import questModel from 'components/quests/questModel'
import { getValue, getText, getImages } from 'lib/value'
import answerModel, { EDIT_WHITELIST } from 'components/answers/answerModel'
import cn from 'classnames'
import styles from './answer.module.scss'
import SentReplyQuote from 'components/answers/SentReplyQuote'
import MainThreadOriginMarker from 'components/answers/MainThreadOriginMarker'
import {
    quoteMainEnabled,
    resolveQuoteMainReplyLane,
    shouldConnectComposerToRow,
    shouldConnectAdjacentReply,
} from 'lib/quoteMain'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { focus } from '../../../state/imperativeApis/focusApi'
import { blurFocusedNewAnswer } from 'state/imperativeApis/editorApi'
import { peekBottomEditorDockSnapshot, shouldReleaseStaleComposerFocus } from 'lib/bottomEditorDock'
import { shouldBlurNewAnswerBeforeExternalInteraction } from 'components/PanelController/mobileKeyboardFocusBehavior'
import { isEqual, debounce, pick } from 'lodash'
import {
    LinkPreview,
    extractKnovEmbedTarget,
    extractLeaderboardEmbedConfig,
    extractStreamEmbedConfig,
    extractTdpEmbedConfig,
    extractUrls,
    getKnovLinkRegexp,
    getLeaderboardEmbedPreviewKey,
    getStreamLinkRegexp,
    getTdpEmbedPreviewKey,
    getUrlModelYoutubeVideoId,
    twitterRegexp,
} from 'components/shared/LinkPreview'
import KnovLogo from 'public/icon128.png'
import * as cache from 'state/cache'
import {
    answerMatchesKnovEmbedTarget,
    filterEmbedsForKnovEmbedLinks,
    getKnovEmbedTargets,
    getMatchedKnovEmbedTargetKeys,
} from 'components/answers/knovEmbedLinks'
import {
    refreshChildQuestInBackground,
    seedChildQuestFromAnswer,
} from 'refactor/panels/primeChildQuestForOpen'
import openSourceAnswerThread from 'utils/openSourceAnswerThread'
import { getAnswerDisplayText } from 'components/answers/answerDisplayText'
import { draftAnswerStore } from 'state/draftAnswerStore'
import useStore from 'state/knovStore'
import { SWIPE_SPEED_FAST, SWIPE_SPEED_SLOW } from 'state/imperativeApis/swiperApi'
import Debug from 'components/shared/Debug'
import {
    showOrdinalMintErrorToast,
    showOrdinalMintProgressToast,
    showOrdinalMintStartedToast,
} from 'components/shared/OrdinalMintStatusListener'
import { ordinalMintPhaseLabel } from 'components/shared/ordinalMintPresentation'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import AnswerButtons from 'components/answers/AnswerButtons'
import ArtifactBadge, { isArtifactWrapperAnswer } from 'components/answers/ArtifactBadge'
import { shouldNavigateQuestTitle } from 'components/panels/ordinalUpvalueFeature'
import VoteControl from 'components/answers/VoteControl'
import ThreadInfo from 'components/answers/ThreadInfo'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { getSwiperRef, isPanelCenter, isPanelRight } from 'state/imperativeApis/swiperApi'
import JobStatus from 'components/answers/Answer/JobStatus'
import PayloadContainer, { mediaKindFromUrl, payloadRowKind } from './PayloadContainer'
import PaymentReceipts from './PaymentReceipts'
import { buildPayloadImageLightbox, isBrowserDisplayableImage } from './payloadImageLightbox'
import { isSingleActiveStatusPayload, presentRetryingAiRunPayload } from './payloadStatus'
import { buildPayloadMediaDownloadUrl } from './payloadMediaDownloadUrl'
import { suppressMediaPayloadContentClick } from './mediaPayloadClickPropagation'
import useOpenRightPrefetch from 'refactor/hooks/useOpenRightPrefetch'
import { isNativeIOSApp } from 'utils/nativeApp'
import {
    shouldShowFirstAnswerTopBorder,
    shouldShowLastAnswerBottomBorder,
    shouldShowReplySeparatorTopBorder,
    shouldShowTitleAnswerBottomBorder,
} from './answerBorderPolicy'
import AnswerYouTubePlayer from './AnswerYouTubePlayer'
import ImageRetryButton from './ImageRetryButton'
import useAnswerHighlight from 'refactor/hooks/useAnswerHighlight'
import AnswerGate from './AnswerGate'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'
import { answerIsNewSinceQuestVisit } from 'lib/questStreamRenderedAnswers'
import Raffle from '../Raffle'
import StreamEmbed from 'components/shared/StreamEmbed'
import LeaderboardMessageEmbed from 'components/leaderboards/LeaderboardMessageEmbed'
import TdpMessageEmbed from 'components/tdp/TdpMessageEmbed'
import MarketCardEmbed from 'components/market/MarketCardEmbed'
import { firstValidMediaTimestamp, sortMediaByCreation } from 'lib/mediaDisplayOrder'
import { currentUserSnapshot } from 'lib/currentUserSnapshot'

// Market-card announcements carry a body that is ONLY the invisible
// market_embed op (a zero-width space). Rendering it still costs an empty
// paragraph between the byline and the card, so read mode swaps in this empty
// delta instead. Stable reference — an inline literal would defeat downstream
// memoization. Embed extraction reads answer.delta_json directly and is
// unaffected.
const MARKET_CARD_EMPTY_DELTA = { ops: [] }
import DeletedAnswer from '../DeletedAnswer'
import DirectMessageHead from '../DirectMessageHead'
import { isDirectMessageHeadAnswer, isDirectMessageRoom } from 'lib/directMessages'
import { isUserMuted } from 'utils/muteUtils'
import MutedContentPlaceholder from 'components/shared/MutedContentPlaceholder'
import UploadProgress from 'components/shared/UploadProgress'
import PreviewCancelButton from 'components/shared/PreviewCancelButton'
import AnswerUploadManager from '../answerUploadManager'
import PollAnswer from 'components/answers/PollAnswer'
import {
    addSuppressedPreviewUrl,
    areSamePreviewUrl,
    filterSuppressedPreviewUrls,
    getLastUrlPathSegment,
    pruneSuppressedPreviewUrls,
} from '../payloadRemoval'
import { getDeltaSummary } from 'components/answers/largeDelta'
import {
    aiInputDescriptorForMediaItem,
    aiInputDescriptorsForAnswer,
    partitionAiInputMediaItems,
} from 'lib/aiInputMediaClassification'
import AiInputMediaStrip from './AiInputMediaStrip'
import { mediaBucketsEnabled, mobileReplyMediaEstimatedWidth } from 'lib/mediaBuckets'
import { replyMediaEnabled } from 'lib/replyMedia'
import { lightweightStreamCardEnabled } from 'lib/lightweightThreadInStream'
import { isInlineMediaPlaybackHandoffPendingForAnswer } from 'state/mediaPlaybackCoordinator'
import {
    LEADERBOARD_EMBED_WINDOW_PX,
    MARKET_EMBED_WINDOW_PX,
    STREAM_EMBED_WINDOW_PX,
    TDP_EMBED_WINDOW_PX,
    TEXT_LINK_PREVIEW_WINDOW_PX,
} from 'lib/answerPlaceholderHeight'
import { ANSWER_CONTENT_TOP_PADDING_PX } from 'lib/staticGeometryChrome'
import { confirmAsync } from 'components/shared/confirmUtils'
import { SATOSHIS } from 'lib/bsv-util'
import {
    Blocks,
    Disc3,
    Download,
    FileText,
    Film,
    Music,
    Pencil,
    Quote,
    RefreshCw,
    Sparkles,
} from 'lucide-react'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import { dispatchMediaAiActionWhenThreadReady } from 'utils/mediaAiActionDispatcher'
import AiActionPreview from 'components/shared/tiptap/AiActionPreview'
import { aiActionToPreviewProps } from 'components/answers/aiActionPreviewProps'
import { canLaunchExistingPostMediaAction } from 'components/answers/postMediaActions'
import { findModelByActionTag, normalizeActionTag } from 'state/aiModels'
import { DEFAULT_VIDEO_MODEL } from 'state/defaultAiModels'
import { actionProducesGeneratedOutput } from 'utils/actionOutputPolicy'
import { payloadMediaKind } from './payloadMediaKind'
import { combinedMediaEnabled, combinedMediaPair } from 'lib/combinedMedia'
import {
    formatPayloadSats,
    isPayloadMediaMinted,
    isPayloadOwnRecording,
    payloadAttachmentBsvTx,
    payloadWriteOnchainAttachmentIds,
    payloadChainActionLabel,
    payloadOnchainIndicatorForAttachment,
    payloadOrdinalIndicator,
    payloadOrdinalThreadActionLabel,
    payloadWriteOnchainSats,
    payloadWriteOnchainState,
    openPayloadOrdinalThread,
    ORDINAL_THREAD_UNAVAILABLE_MESSAGE,
    WRITE_ONCHAIN_FEATURE,
    WRITE_ONCHAIN_LABEL,
    WRITE_ONCHAIN_MAX_BYTES,
} from './payloadMediaMenu'
import { WRITE_ONCHAIN_WORKFLOW_CLASS, waitForWriteOnchainOutcome } from './writeOnchainOutcome'

const stateSelector = (state, props) => ({
    DEBUG: state.DEBUG,
    //answer: props.answer,
    isSelected:
        state.selectedAnswers[props.panelContext.panel?.panelId]?.answerId === props.answer?.id,
    numPanels: state.numPanels,
    selectedQuote: state.selectedQuote,
    answerJobs: state.answerJobs,
    aiModels: state.aiModels,
})

const getVisibleTailAnswers = answers => {
    if (!Array.isArray(answers)) return null

    return answers.filter(answer => answer && !answer.archive)
}

const getVidStartFromUrl = url => {
    if (!url) return 0
    const match = url.match(/[?&]t=([^&]+)/)
    if (!match) return 0

    const timeStr = match[1]

    // Pure digits (e.g. t=120)
    if (/^\d+$/.test(timeStr)) return parseInt(timeStr, 10)

    // With units (e.g. t=1h2m30s)
    let total = 0
    const h = timeStr.match(/(\d+)h/)
    const m = timeStr.match(/(\d+)m/)
    const s = timeStr.match(/(\d+)s/)

    if (h) total += parseInt(h[1], 10) * 3600
    if (m) total += parseInt(m[1], 10) * 60
    if (s) total += parseInt(s[1], 10)

    return total
}

const MINT_TX_OVERHEAD_BYTES = 600
const MINT_FEE_SAFETY_MARGIN = 1.2
const PAYLOAD_MINT_QUOTE_MAX_AGE_MS = 20 * 60 * 1000
const MINTABLE_VIDEO_MAX_BYTES = 50 * 1024 * 1024

const ANSWER_CONTROLS_POPUP_MARGIN = 12
const ANSWER_CONTROLS_POPUP_TOP_OFFSET = 65
const ANSWER_CONTROLS_POPUP_SIDE_OFFSET = 15

const getInitialLinkPreviews = answer => {
    const deltaSummary = getDeltaSummary(answer?.delta_json)
    if (deltaSummary.oversized) return []

    return extractUrls(answer?.delta_json)?.nonKnovLinks || []
}

const getTwitterLinkPreviews = extractedUrls =>
    (extractedUrls?.nonKnovLinks || []).filter(url => twitterRegexp.test(url))

class Answer extends React.PureComponent {
    // Inline editing swaps in a composer far taller than a static row's frozen read-mode
    // reserve; reporting edit start/stop lets the row provider lift clipping and (v2) resize
    // the reserve, so the composer's save/cancel controls stay reachable.
    static contextType = RowGeometryEventsContext

    constructor(props) {
        super(props)
        this.touched = moment(this.props?.answer?.updated_at).format('MM/DD/YYYY')
        this.selectionCache = null
        this.youtubePlayerRef = React.createRef()
        this.controlsRowRef = React.createRef()

        const answer = this.props.answer
        const vidStart = answer?.vid_start_time || getVidStartFromUrl(answer?.url?.address)
        const youtubeVideoId = answer?.yt_app ? getUrlModelYoutubeVideoId(answer?.url) : null
        this.state = {
            value: answer?.delta_json || getValue(answer),
            editMode: false,
            command: answer?.command,
            vidStart: vidStart,
            vidLoop: answer?.vid_loop_time,
            showControlButtons: false,
            // The pointer is over the stream card's breadcrumb strip (Quest.onCrumbHover).
            pairedHover: false,
            imageDownload: false,
            acted: false,
            showKnovigator: false,
            linkPreviews: getInitialLinkPreviews(answer),
            showTranslation: false,
            translationLoading: false,
            translationError: false,
            // Local fallback for the translate response, in case the merged
            // answer prop hasn't round-tripped through the parent yet.
            translationResult: null,
            userPreferedLanguage: (
                gon?.currentUser?.options?.preferredLanguage || 'en'
            ).toLowerCase(),
            revealMuted: false,
            uploadProgress: { recording: null, files: {} },
            embedsLoading: false,
            embedLoadingPromise: null,
            waitingForEmbeds: false,
            hiddenPreviewUrls: [],
            pendingClearedPreviewUrls: [],
            pendingSuppressedPreviewUrls: [],
            pendingPreviewSaveAtMs: null,
            pendingPreviewAwaitingSaveResponse: false,
            modelPreviewProgress: 0,
            lightboxOpen: false,
            lightboxSlides: [],
            lightboxIndex: 0,
            lightboxBottomNavigation: false,
            showControlsPopup: false,
            answerControlsPopupPosition: null,
            showShareTray: false,
            shareTrayUrl: null,
            shareRewardEnabled: false,
            shareLinkReady: true,
            youtubeLoadedVideoId:
                youtubeVideoId &&
                isInlineMediaPlaybackHandoffPendingForAnswer(
                    answer?.id,
                    `youtube:${youtubeVideoId}`,
                )
                    ? youtubeVideoId
                    : null,
            payloadMintEstimates: {},
            payloadMintUiByAnswerId: {},
        }
        this.payloadMintEstimatePromises = new Map()
        this.spinnerRef = React.createRef()
        this.downloadRef = React.createRef()
        this.containerRef = React.createRef()
        this.answerContentContainerRef = React.createRef()
        this.answerControlsPopupRef = React.createRef()
        this.videoTagRef = React.createRef()
        this.uiId = 'answer-' + Date.now()
        this.uploadManager = new AnswerUploadManager({
            getAnswerId: () => this.getDraftAnswer()?.id,
            setUploadProgress: updater =>
                this.setState(state => ({
                    uploadProgress: updater(
                        state?.uploadProgress || { recording: null, files: {} },
                    ),
                })),
            setWaitingForEmbeds: waiting => this.setState({ waitingForEmbeds: waiting }),
            setModelPreviewProgress: pct => this.setState({ modelPreviewProgress: pct }),
            onUploadError: message => {
                if (typeof alert === 'function') alert(message)
            },
        })
        this.answerControlsPositionRaf = null
        this.answerControlsResizeObserver = null
        this.observedAnswerControlsPopupEl = null
        this.ensureDraftAnswer()
    }

    openLightbox = imageUrl => {
        this.setState({
            lightboxOpen: true,
            lightboxSlides: [{ src: imageUrl }],
            lightboxIndex: 0,
            lightboxBottomNavigation: false,
        })
    }

    openPayloadImageLightbox = (items, clickedItem) => {
        const { slides, index } = buildPayloadImageLightbox(items, clickedItem)
        if (!slides.length) return

        this.setState({
            lightboxOpen: true,
            lightboxSlides: slides,
            lightboxIndex: index,
            lightboxBottomNavigation: slides.length > 1,
        })
    }

    closeLightbox = () => {
        this.setState({ lightboxOpen: false })
    }

    // A viewer flipping the payload between grid and carousel is user intent to resize the row,
    // so the virtualized row may shrink to the (usually shorter) strip instead of preserving
    // the grid's height. The settle callback that follows carries the measurement.
    handlePayloadMediaLayoutChange = () => {
        this.props.inViewCallback?.({ allowShrink: true, userIntent: true })
    }

    handleContentLayoutSettled = () => {
        const notify = () => this.props.inViewCallback?.({ allowShrink: true })

        if (typeof window.requestAnimationFrame !== 'function') {
            notify()
            return
        }

        window.requestAnimationFrame(notify)
    }

    aiRunErrorText = value => {
        if (value == null) return null
        if (typeof value === 'string') {
            const trimmed = value.trim()
            return trimmed.length ? trimmed : null
        }
        if (typeof value === 'number' || typeof value === 'boolean') return String(value)
        if (Array.isArray(value)) {
            const parts = value.map(part => this.aiRunErrorText(part)).filter(Boolean)
            return parts.length ? parts.join(' ') : null
        }
        if (typeof value === 'object') {
            const candidates = [
                value.error,
                value.error_message,
                value.errorMessage,
                value.message,
                value.logs,
                value.detail,
                value.details,
                value.reason,
                value.prediction_response,
                value.predictionResponse,
                value.response,
                value.payload,
            ]
                .map(candidate => this.aiRunErrorText(candidate))
                .filter(Boolean)
            const descriptive = candidates.find(
                candidate => !/^(failed|failure|error|canceled|cancelled)$/i.test(candidate),
            )

            return descriptive || candidates[0] || null
        }
        return null
    }

    aiRunErrorMessage = run => {
        const payload = run?.error_payload || run?.errorPayload || {}
        return (
            this.aiRunErrorText(payload) ||
            this.aiRunErrorText(run?.error_details || run?.errorDetails) ||
            run?.status ||
            'AI generation failed'
        )
    }

    aiRunOutputKind = output => payloadMediaKind(output)

    aiRunOutputUrl = output =>
        output?.url ||
        output?.preview_url ||
        output?.urls?.original ||
        output?.urls?.medium ||
        output?.urls?.small ||
        null

    refreshAiRunOutputUrl = async ({ answerId, attachmentId = null, blobId = null }) => {
        if (!answerId) return null

        const refreshedAnswer = await api.refreshAnswerPayloadMedia(answerId)
        const runs = Array.isArray(refreshedAnswer?.ai_runs) ? refreshedAnswer.ai_runs : []
        const expectedAttachmentId = attachmentId == null ? null : String(attachmentId)
        const expectedBlobId = blobId == null ? null : String(blobId)

        for (const run of runs) {
            const outputs = Array.isArray(run?.output_urls) ? run.output_urls : []
            const output = outputs.find(candidate => {
                const candidateAttachmentId =
                    candidate?.id || candidate?.attachment_id || candidate?.attachmentId
                const candidateBlobId = candidate?.blob_id || candidate?.blobId

                return (
                    (expectedAttachmentId &&
                        candidateAttachmentId != null &&
                        String(candidateAttachmentId) === expectedAttachmentId) ||
                    (expectedBlobId &&
                        candidateBlobId != null &&
                        String(candidateBlobId) === expectedBlobId)
                )
            })

            const refreshedUrl = output ? this.aiRunOutputUrl(output) : null
            if (refreshedUrl) return refreshedUrl
        }

        return null
    }

    isGifPayload = ({ url = null, contentType = null, actionTag = null, label = null } = {}) => {
        const normalizedContentType = String(contentType || '').toLowerCase()
        const normalizedActionTag = String(actionTag || '')
            .replace(/^!+/, '')
            .toLowerCase()
        const normalizedLabel = String(label || '').toLowerCase()
        const normalizedUrl = String(url || '')
            .split(/[?#]/)[0]
            .toLowerCase()
        return (
            normalizedContentType === 'image/gif' ||
            normalizedActionTag === 'gif' ||
            normalizedLabel === 'gif' ||
            normalizedUrl.endsWith('.gif')
        )
    }

    payloadMediaLabel = ({ kind, actionTag = null, fallback = null }) => {
        if (fallback) return fallback
        if (actionTag) return `!${actionTag}`
        if (kind === 'image') return 'Image'
        if (kind === 'video') return 'Video'
        if (kind === 'audio') return 'Audio'
        return 'Media'
    }

    currentUserHasFeature = featureName =>
        window.gon?.currentUser?.features?.some(feature => feature?.name === featureName) ?? false

    isCurrentUserAnswerOwner = answer =>
        Boolean(answer?.user_id && answer.user_id === window.gon?.currentUser?.id)

    payloadCatalogModelForRun = run => {
        const actionPayload = this.payloadActionPayloadForRun(run)
        const aiModels =
            this.props.aiModels?.length > 0
                ? this.props.aiModels
                : useStore.getState().aiModels || []
        const aiModelId =
            run?.ai_model_id ||
            run?.aiModelId ||
            actionPayload?.ai_model_id ||
            actionPayload?.aiModelId ||
            null
        const actionTag = normalizeActionTag(
            run?.action_tag ||
                run?.actionTag ||
                actionPayload?.tag ||
                actionPayload?.action_tag ||
                actionPayload?.actionTag,
        )
        const providerModel =
            run?.provider_model ||
            run?.providerModel ||
            actionPayload?.model_name ||
            actionPayload?.modelName ||
            null

        return (
            (aiModelId &&
                aiModels.find(model => model?.id && String(model.id) === String(aiModelId))) ||
            (actionTag && findModelByActionTag(aiModels, actionTag)) ||
            (providerModel &&
                aiModels.find(
                    model => model?.name === providerModel || model?.humanName === providerModel,
                )) ||
            null
        )
    }

    payloadModelLabelForRun = run => {
        const actionPayload = this.payloadActionPayloadForRun(run)
        const catalogModel = this.payloadCatalogModelForRun(run)
        const displayName = actionPayload?.display_name || actionPayload?.displayName
        const displayNameLooksLikeTag =
            typeof displayName === 'string' && displayName.trim().startsWith('!')

        return (
            run?.ai_model?.human_name ||
            run?.aiModel?.humanName ||
            catalogModel?.humanName ||
            run?.model_name ||
            run?.modelName ||
            actionPayload?.model_human_name ||
            actionPayload?.modelHumanName ||
            actionPayload?.human_name ||
            actionPayload?.humanName ||
            actionPayload?.model_name ||
            actionPayload?.modelName ||
            (!displayNameLooksLikeTag ? displayName : null) ||
            run?.provider_model ||
            run?.providerModel ||
            run?.action_tag ||
            null
        )
    }

    payloadActionPayloadForRun = run => run?.action_payload || run?.actionPayload || {}

    payloadSettingsForRun = run => {
        const actionPayload = this.payloadActionPayloadForRun(run)
        return actionPayload?.settings || {}
    }

    payloadResultPayloadForRun = run => run?.result_payload || run?.resultPayload || {}

    sunoGeneratedLyricsForRun = run => {
        const resultPayload = this.payloadResultPayloadForRun(run)
        const providerResult = resultPayload?.provider_result || resultPayload?.providerResult || {}
        const track = providerResult?.track || {}
        const response = providerResult?.response || {}
        const responseTrack =
            response?.data?.data?.[0] ||
            response?.data?.response?.sunoData?.[0] ||
            response?.data?.sunoData?.[0] ||
            response?.response?.sunoData?.[0] ||
            null
        const lyrics =
            track?.prompt || track?.lyrics || responseTrack?.prompt || responseTrack?.lyrics || null

        return typeof lyrics === 'string' && lyrics.trim() ? lyrics.trim() : null
    }

    sunoLyricsPreview = lyrics => (
        <div className={styles.generatedLyricsPreview}>
            <div className={styles.generatedLyricsPreviewTitle}>Lyrics</div>
            <pre className={styles.generatedLyricsPreviewText}>{lyrics}</pre>
        </div>
    )

    numericPayloadValue = value => {
        const number = Number(value)
        return Number.isFinite(number) && number > 0 ? number : null
    }

    firstNumericPayloadValue = values => {
        for (const value of values) {
            const number = this.numericPayloadValue(value)
            if (number !== null) return number
        }
        return null
    }

    payloadMediaDimensions = media => {
        const metadata = media?.metadata || {}
        const dimensions = media?.dimensions || {}
        const width = this.firstNumericPayloadValue([
            media?.width,
            media?.video_width,
            media?.videoWidth,
            media?.image_width,
            media?.imageWidth,
            metadata?.width,
            metadata?.video_width,
            metadata?.videoWidth,
            metadata?.image_width,
            metadata?.imageWidth,
            dimensions?.width,
            dimensions?.video_width,
            dimensions?.videoWidth,
        ])
        const height = this.firstNumericPayloadValue([
            media?.height,
            media?.video_height,
            media?.videoHeight,
            media?.image_height,
            media?.imageHeight,
            metadata?.height,
            metadata?.video_height,
            metadata?.videoHeight,
            metadata?.image_height,
            metadata?.imageHeight,
            dimensions?.height,
            dimensions?.video_height,
            dimensions?.videoHeight,
        ])

        return width && height ? { width, height } : { width: null, height: null }
    }

    payloadAspectRatioFromDimensions = output => {
        const { width, height } = this.payloadMediaDimensions(output)
        if (!width || !height) return null

        const divisor = (left, right) => {
            let a = Math.round(left)
            let b = Math.round(right)
            while (b) {
                const next = a % b
                a = b
                b = next
            }
            return a || 1
        }
        const gcd = divisor(width, height)
        return `${Math.round(width / gcd)}:${Math.round(height / gcd)}`
    }

    formatPayloadDurationLabel = seconds => {
        const duration = this.numericPayloadValue(seconds)
        if (!duration) return null
        return Number.isInteger(duration) ? `${duration}s` : `${duration.toFixed(1)}s`
    }

    formatPayloadAspectRatioLabel = aspectRatio => {
        const normalized = String(aspectRatio || '').trim()
        if (!normalized) return null
        return `AR ${normalized.replace(/_/g, ' ')}`
    }

    payloadActionMetadataForOutput = ({ run, output, kind }) => {
        const actionPayload = this.payloadActionPayloadForRun(run)
        const settings = this.payloadSettingsForRun(run)
        const generationCount = this.firstNumericPayloadValue([
            settings?.generation_count,
            settings?.generationCount,
            actionPayload?.generation_count,
            actionPayload?.generationCount,
        ])
        const aspectRatio = this.firstTruthyValue([
            settings?.aspect_ratio,
            settings?.aspectRatio,
            actionPayload?.aspect_ratio,
            actionPayload?.aspectRatio,
            actionPayload?.default_aspect_ratio,
            actionPayload?.defaultAspectRatio,
            this.payloadAspectRatioFromDimensions(output),
        ])
        const duration =
            kind === 'video'
                ? this.firstNumericPayloadValue([
                      settings?.duration_seconds,
                      settings?.durationSeconds,
                      actionPayload?.duration_seconds,
                      actionPayload?.durationSeconds,
                      output?.duration,
                      output?.duration_seconds,
                      output?.durationSeconds,
                      actionPayload?.default_duration_seconds,
                      actionPayload?.defaultDurationSeconds,
                  ])
                : null

        return [
            generationCount && generationCount > 1 ? `x${generationCount}` : null,
            this.formatPayloadAspectRatioLabel(aspectRatio),
            this.formatPayloadDurationLabel(duration),
        ].filter(Boolean)
    }

    payloadInputKindLabel = kind => {
        if (kind === 'image') return 'Image'
        if (kind === 'video') return 'Video'
        if (kind === 'audio') return 'Audio'
        return 'Media'
    }

    payloadInputRoleLabel = ({ kind, role, index = null }) => {
        const kindLabel = this.payloadInputKindLabel(kind)
        if (role === 'reference') return `Reference ${kindLabel}`
        return index === null ? `${kindLabel} Input` : `${kindLabel} Input ${index + 1}`
    }

    payloadInputDescriptorForItem = (answer, item) => {
        if (!item?.url || !Array.isArray(answer?.ai_runs)) return null
        return aiInputDescriptorForMediaItem(item, aiInputDescriptorsForAnswer(answer))
    }

    // Media the answer's AI actions consumed is provenance, not content: mark it so the
    // renderer can demote it to the compact input strip instead of a full payload tile.
    applyAiActionInputLabels = (answer, items) =>
        items.map(item => {
            const descriptor = this.payloadInputDescriptorForItem(answer, item)
            return descriptor
                ? {
                      ...item,
                      modelLabel: this.payloadInputRoleLabel(descriptor),
                      aiInputRole: descriptor.role,
                  }
                : item
        })

    payloadMintMediaType = kind => {
        if (kind === 'image') return 'image'
        if (kind === 'video') return 'video'
        if (kind === 'audio') return 'audio'
        return null
    }

    payloadMintEstimateKey = ({ answer, kind, attachmentId = null }) =>
        [
            answer?.id || 'answer',
            this.payloadMintMediaType(kind) || kind,
            attachmentId || 'primary',
        ].join(':')

    payloadMintUiForAnswer = answer => this.state.payloadMintUiByAnswerId?.[answer?.id] || null

    setPayloadMintUiForAnswer = (answer, value) => {
        if (!answer?.id) return
        this.setState(state => ({
            payloadMintUiByAnswerId: {
                ...(state.payloadMintUiByAnswerId || {}),
                [answer.id]: value,
            },
        }))
    }

    payloadMintEstimateFor = ({ answer, kind, attachmentId = null }) =>
        this.state.payloadMintEstimates?.[
            this.payloadMintEstimateKey({ answer, kind, attachmentId })
        ] || null

    loadPayloadMintEstimate = ({ answer, kind, attachmentId = null }) => {
        const mediaType = this.payloadMintMediaType(kind)
        const estimateKey = this.payloadMintEstimateKey({ answer, kind, attachmentId })
        const cachedEstimate = this.state.payloadMintEstimates?.[estimateKey]
        // The signed fee quote expires after 30 minutes on the server; refresh well before.
        if (
            cachedEstimate &&
            Date.now() - cachedEstimate.quotedAt < PAYLOAD_MINT_QUOTE_MAX_AGE_MS
        ) {
            return Promise.resolve(cachedEstimate)
        }

        const inFlightEstimate = this.payloadMintEstimatePromises.get(estimateKey)
        if (inFlightEstimate) return inFlightEstimate

        const estimatePromise = (async () => {
            const mediaInfo = await api.getMintMediaInfo(
                answer.id,
                mediaType,
                attachmentId || undefined,
            )
            const byteSize = Number(mediaInfo?.byte_size || 0)
            if (byteSize <= 0) {
                throw new Error('Unable to determine ' + mediaType + ' size for mint estimate.')
            }
            if (mediaType === 'video' && byteSize > MINTABLE_VIDEO_MAX_BYTES) {
                throw new Error('Video is too large to mint. Max supported size is 50MB.')
            }

            const [exchangeData, feeRateData, mintFeeQuote] = await Promise.all([
                api.getBsvExchangeRate(),
                api.getBsvFeeRate(),
                api.getOrdinalMintFeeQuote(),
            ])
            const bsvExchangeRate = Number(exchangeData?.exchangerate || 0)
            const bsvFeeRateSatsPerByte = Number(feeRateData?.fee_rate || 0.1)
            if (!bsvExchangeRate || bsvExchangeRate <= 0) {
                throw new Error('Unable to fetch BSV exchange rate. Please try again in a moment.')
            }

            // The server signs this fee quote, so the mint start charges this exact
            // amount even if another server process has since repriced.
            const mintPriceSats = Number(mintFeeQuote?.service_fee_satoshis)
            if (
                !Number.isSafeInteger(mintPriceSats) ||
                mintPriceSats <= 0 ||
                !mintFeeQuote?.service_fee_quote
            ) {
                throw new Error(
                    'Unable to quote the Treechat mint fee. Please try again in a moment.',
                )
            }
            const mintPriceUsd = (mintPriceSats / SATOSHIS) * bsvExchangeRate
            const estimatedOnchainWriteSats = Math.ceil(
                (byteSize + MINT_TX_OVERHEAD_BYTES) *
                    bsvFeeRateSatsPerByte *
                    MINT_FEE_SAFETY_MARGIN,
            )
            const estimatedOnchainWriteUsd =
                (estimatedOnchainWriteSats / SATOSHIS) * bsvExchangeRate
            const estimate = {
                mediaInfo,
                byteSize,
                bsvFeeRateSatsPerByte,
                bsvExchangeRate,
                mintPriceUsd,
                mintPriceSats,
                serviceFeeQuote: mintFeeQuote.service_fee_quote,
                quotedAt: Date.now(),
                estimatedOnchainWriteSats,
                estimatedOnchainWriteUsd,
                estimatedTotalUsd: mintPriceUsd + estimatedOnchainWriteUsd,
            }

            this.setState(state => ({
                payloadMintEstimates: {
                    ...(state.payloadMintEstimates || {}),
                    [estimateKey]: estimate,
                },
            }))
            return estimate
        })()

        this.payloadMintEstimatePromises.set(estimateKey, estimatePromise)
        // Settled estimates live in state, where their quote age is checked.
        const forgetEstimatePromise = () => this.payloadMintEstimatePromises.delete(estimateKey)
        estimatePromise.then(forgetEstimatePromise, forgetEstimatePromise)
        return estimatePromise
    }

    prefetchPayloadMintEstimate = options => {
        this.loadPayloadMintEstimate(options).catch(() => {})
    }

    formatPayloadMintBytes = bytes => {
        if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + ' KB'
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    }

    handlePayloadMint =
        ({ answer, kind, attachmentId = null }) =>
        async ev => {
            ev.preventDefault()
            ev.stopPropagation()

            const mediaType = this.payloadMintMediaType(kind)
            if (!answer?.id || !mediaType || !this.isCurrentUserAnswerOwner(answer)) return

            this.setPayloadMintUiForAnswer(answer, { phase: 'preparing' })
            const cachedAnswer = cache.getCachedAnswer(answer.id)
            if (cachedAnswer) {
                cache.cacheAnswer({
                    ...cachedAnswer,
                    mint_failure_message: null,
                    mint_recently_completed: false,
                })
            }

            let estimate
            try {
                estimate = await this.loadPayloadMintEstimate({ answer, kind, attachmentId })
            } catch (error) {
                this.setPayloadMintUiForAnswer(answer, { phase: 'failed' })
                const errorMessage = error instanceof Error ? error.message : String(error)
                showOrdinalMintErrorToast(answer.id, `Unable to prepare mint: ${errorMessage}`)
                return
            }

            const {
                mediaInfo,
                byteSize,
                bsvFeeRateSatsPerByte,
                mintPriceUsd,
                mintPriceSats,
                serviceFeeQuote,
                estimatedOnchainWriteSats,
                estimatedOnchainWriteUsd,
                estimatedTotalUsd,
            } = estimate

            window.logEv?.('BSV_MINT_START', {
                answer_id: answer.id,
                quest_id: answer.quest_id,
                attachment_id: mediaInfo?.attachment_id || attachmentId || undefined,
                media_type: mediaType,
                mint_price_sats: mintPriceSats,
                mint_price_usd: mintPriceUsd,
                media_bytes: byteSize,
                fee_rate_sats_per_byte: bsvFeeRateSatsPerByte,
                estimated_onchain_write_sats: estimatedOnchainWriteSats,
            })

            const { openConfirmTray } = useStore.getState().actions.trayActions
            const onCancel = () => this.setPayloadMintUiForAnswer(answer, null)
            const onConfirm = async () => {
                this.setPayloadMintUiForAnswer(answer, { phase: 'submitting' })
                showOrdinalMintStartedToast(answer.id)

                try {
                    const mintResult = await api.mintOrdinalTemporal(answer.id, {
                        treechat_fee_amount_sats: mintPriceSats,
                        service_fee_quote: serviceFeeQuote,
                        media_type: mediaType,
                        attachment_id: mediaInfo?.attachment_id || attachmentId || undefined,
                    })
                    const workflowId = mintResult?.workflow_id || null
                    const runId = mintResult?.run_id || null
                    if (!workflowId) throw new Error('Failed to start mint ordinal workflow')

                    const progressMessage = 'Creating mint ordinal transaction...'
                    const latestAnswer = cache.getCachedAnswer(answer.id) || answer
                    cache.cacheAnswer({
                        ...latestAnswer,
                        mint_workflow_id: workflowId,
                        mint_run_id: runId,
                        mint_status_message: progressMessage,
                        mint_failure_message: null,
                    })
                    this.setPayloadMintUiForAnswer(answer, null)
                    showOrdinalMintProgressToast(answer.id, progressMessage)
                    return true
                } catch (error) {
                    this.setPayloadMintUiForAnswer(answer, { phase: 'failed' })
                    const errorMessage = error instanceof Error ? error.message : String(error)
                    showOrdinalMintErrorToast(answer.id, 'Failed to mint ordinal: ' + errorMessage)
                    return true
                }
            }

            openConfirmTray(
                <div style={{ color: 'var(--color-text-primary)' }}>
                    <div
                        style={{
                            marginBottom: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            opacity: 0.7,
                        }}
                    >
                        Mint Ordinal
                    </div>
                    <div style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'left' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '5px 0',
                            }}
                        >
                            <span>Platform fee</span>
                            <span>${mintPriceUsd.toFixed(2)}</span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '5px 0',
                            }}
                        >
                            <span>Onchain write · {this.formatPayloadMintBytes(byteSize)}</span>
                            <span>${estimatedOnchainWriteUsd.toFixed(2)}</span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '5px 0',
                                fontWeight: 600,
                            }}
                        >
                            <span>Total</span>
                            <span>${estimatedTotalUsd.toFixed(2)}</span>
                        </div>
                    </div>
                </div>,
                'MINT $' + estimatedTotalUsd.toFixed(2),
                onConfirm,
                {
                    onCancel,
                    hideCancel: true,
                    confirmVariant: 'bsv',
                    primaryActionFirst: true,
                    awaitConfirm: true,
                    confirmPendingLabel: 'Starting…',
                },
            )
        }

    setPayloadWriteOnchainUi = (key, phase) => {
        this.setState(state => ({
            payloadWriteOnchainUi: {
                ...(state.payloadWriteOnchainUi || {}),
                [key]: phase,
            },
        }))
    }

    // Write Onchain is the owner's paid write of one media file (payloadMediaMenu.ts). It is
    // offered once the post itself is onchain: the record points at the post's txid, and only
    // public posts are written. The write funds from the server-held key, so self-custody
    // wallets are left out.
    canPayWriteOnchain = ({ answer, mediaType }) =>
        Boolean(
            mediaType &&
                this.isCurrentUserAnswerOwner(answer) &&
                this.currentUserHasFeature(WRITE_ONCHAIN_FEATURE) &&
                !this.currentUserHasFeature('use_client_signing') &&
                (answer?.bsv_tx?.tx_id || answer?.bsv_tx_id),
        )

    payloadWriteOnchainByteSize = ({ answer, kind, attachmentId, estimate }) => {
        const estimatedBytes = Number(estimate?.byteSize)
        if (estimatedBytes > 0) return estimatedBytes
        // The mint estimate refuses to price a video over the mint ceiling, so read an oversized
        // video's size off the answer instead, but only for the post's own recording tile.
        const videoBytes =
            kind === 'video' && isPayloadOwnRecording(answer, attachmentId)
                ? Number(answer?.mint_video_byte_size)
                : 0
        return videoBytes > 0 ? videoBytes : null
    }

    handlePayloadWriteOnchain =
        ({ answer, kind, attachmentId = null }) =>
        async ev => {
            ev.preventDefault()
            ev.stopPropagation()

            const mediaType = this.payloadMintMediaType(kind)
            if (!answer?.id || !this.canPayWriteOnchain({ answer, mediaType })) return

            const uiKey = this.payloadMintEstimateKey({ answer, kind, attachmentId })
            if (this.state.payloadWriteOnchainUi?.[uiKey]) return

            const tooLargeMessage = 'This media is over the 50 MB onchain write limit.'
            const knownBytes = this.payloadWriteOnchainByteSize({
                answer,
                kind,
                attachmentId,
                estimate: null,
            })
            if (knownBytes && knownBytes > WRITE_ONCHAIN_MAX_BYTES) {
                toast.error(tooLargeMessage)
                return
            }

            let estimate
            try {
                estimate = await this.loadPayloadMintEstimate({ answer, kind, attachmentId })
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error)
                toast.error(`Couldn't price the onchain write: ${errorMessage}`)
                return
            }

            const { mediaInfo, byteSize, bsvFeeRateSatsPerByte, bsvExchangeRate } = estimate
            if (byteSize > WRITE_ONCHAIN_MAX_BYTES) {
                toast.error(tooLargeMessage)
                return
            }
            const writeSats = payloadWriteOnchainSats(byteSize, bsvFeeRateSatsPerByte)
            if (!writeSats) {
                toast.error("Couldn't price the onchain write. Please try again.")
                return
            }
            const writeUsd = bsvExchangeRate > 0 ? (writeSats / SATOSHIS) * bsvExchangeRate : null
            const resolvedAttachmentId = mediaInfo?.attachment_id || attachmentId || null

            window.logEv?.('BSV_WRITE_ONCHAIN_START', {
                answer_id: answer.id,
                quest_id: answer.quest_id,
                attachment_id: resolvedAttachmentId || undefined,
                media_type: mediaType,
                media_bytes: byteSize,
                fee_rate_sats_per_byte: bsvFeeRateSatsPerByte,
                price_sats: writeSats,
            })

            const toastKind = `write-onchain-${uiKey}`
            const onConfirm = async () => {
                this.setPayloadWriteOnchainUi(uiKey, 'writing')
                let started
                try {
                    started = await api.writeAttachmentOnchain(answer.id, {
                        media_type: mediaType,
                        attachment_id: resolvedAttachmentId
                            ? String(resolvedAttachmentId)
                            : undefined,
                        byte_size: byteSize,
                        fee_rate_sats_per_byte: bsvFeeRateSatsPerByte,
                    })
                } catch (error) {
                    this.setPayloadWriteOnchainUi(uiKey, null)
                    const errorMessage = error instanceof Error ? error.message : String(error)
                    toast.error(`Couldn't write onchain: ${errorMessage}`)
                    return true
                }

                if (!started?.workflow_id || !started?.run_id) {
                    // Already in flight: the tile follows the answer's attachment state.
                    this.setPayloadWriteOnchainUi(uiKey, null)
                    toast.info('This media is already being written onchain.')
                    return true
                }

                toast.pending('Writing onchain', {
                    kind: toastKind,
                    detail: formatPayloadSats(writeSats),
                })
                this.watchPayloadWriteOnchain({ uiKey, toastKind, started })
                return true
            }

            const { openConfirmTray } = useStore.getState().actions.trayActions
            openConfirmTray(
                <div style={{ color: 'var(--color-text-primary)' }}>
                    <div
                        style={{
                            marginBottom: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            opacity: 0.7,
                        }}
                    >
                        Write Onchain
                    </div>
                    <div style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'left' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '5px 0',
                            }}
                        >
                            <span>Network fee · {this.formatPayloadMintBytes(byteSize)}</span>
                            <span>{formatPayloadSats(writeSats)}</span>
                        </div>
                        {writeUsd !== null && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '5px 0',
                                    opacity: 0.7,
                                }}
                            >
                                <span>About</span>
                                <span>
                                    {writeUsd < 0.01 ? 'under $0.01' : '$' + writeUsd.toFixed(2)}
                                </span>
                            </div>
                        )}
                        <div style={{ padding: '8px 0 0', fontSize: '12px', opacity: 0.7 }}>
                            Paid from your wallet. The file is written to the chain permanently and
                            publicly.
                        </div>
                    </div>
                </div>,
                'WRITE ' + formatPayloadSats(writeSats).toUpperCase(),
                onConfirm,
                {
                    hideCancel: true,
                    confirmVariant: 'bsv',
                    primaryActionFirst: true,
                    awaitConfirm: true,
                    confirmPendingLabel: 'Starting…',
                },
            )
        }

    watchPayloadWriteOnchain = async ({ uiKey, toastKind, started }) => {
        const outcome = await waitForWriteOnchainOutcome({
            fetchStatus: () =>
                api.getTemporalWorkflowStatus(
                    started.workflow_id,
                    started.run_id,
                    'progress',
                    WRITE_ONCHAIN_WORKFLOW_CLASS,
                    started.access_token || undefined,
                ),
        })

        this.setPayloadWriteOnchainUi(uiKey, null)
        if (outcome.settled && outcome.ok) {
            toast.update(toastKind, 'Written onchain', { variant: 'success', detail: '' })
        } else if (outcome.settled) {
            toast.update(toastKind, outcome.message || 'Writing onchain failed', {
                variant: 'error',
                detail: '',
            })
        } else {
            toast.update(toastKind, 'Still writing onchain', {
                variant: 'info',
                detail: 'The media menu shows Onchain once it lands.',
            })
        }
    }

    videoAspectRatio = answer => {
        const width = Number(answer?.video_width || 0)
        const height = Number(answer?.video_height || 0)
        return width && height ? `${width}:${height}` : null
    }

    dispatchAiActionFromPayload =
        ({
            tag,
            kind,
            url,
            label,
            answer,
            thumbnailUrl,
            attachmentId,
            blobId,
            runId,
            actionId,
            previewTitle = null,
        }) =>
        ev => {
            ev.preventDefault()
            ev.stopPropagation()
            if (!url) return

            const detail = {
                tag,
                questId: answer?.quest_id || this.props.quest?.id,
                childQuestId: answer?.child_quests?.[0]?.id || this.props.quest?.id || null,
                previewTitle,
            }
            if (kind === 'video') {
                detail.video = {
                    url,
                    previewImageUrl: thumbnailUrl || answer?.recording_thumbnail_url || null,
                    label: label || 'Video input',
                    answerId: answer?.id,
                    attachmentId,
                    blobId,
                    runId,
                    actionId,
                }
                detail.aspectRatio = this.videoAspectRatio(answer)
            } else if (kind === 'audio') {
                detail.audio = {
                    url,
                    label: label || 'Audio input',
                    answerId: answer?.id,
                    attachmentId,
                    blobId,
                    runId,
                    actionId,
                }
            } else {
                detail.image = {
                    url,
                    label: label || answer?.user?.name || 'Answer image',
                    answerId: answer?.id,
                    attachmentId,
                    blobId,
                    runId,
                    actionId,
                }
            }

            dispatchMediaAiActionWhenThreadReady({
                detail,
                openRight: this.props.openRight,
                sourcePanel: this.props.panelContext?.panel,
            })
        }

    dispatchAiActionRemix =
        ({ answer, aiAction }) =>
        ev => {
            ev.preventDefault()
            ev.stopPropagation()
            if (!aiAction) return

            dispatchMediaAiActionWhenThreadReady({
                detail: {
                    questId: answer?.quest_id || this.props.quest?.id,
                    childQuestId: answer?.child_quests?.[0]?.id || this.props.quest?.id || null,
                    aiAction,
                    remix: true,
                },
                openRight: this.props.openRight,
                sourcePanel: this.props.panelContext?.panel,
            })
        }

    // Answer-controls menu Illustrate/Film: hands this post's contents to the composer as a
    // simple post-media action that references this post as its source.
    dispatchPostMediaActionFromAnswer = kind => {
        const answer = this.props.answer
        if (!canLaunchExistingPostMediaAction(answer, window.gon?.currentUser?.id)) return

        const postText = getAnswerDisplayText(answer)?.trim()
        if (!postText) return

        dispatchMediaAiActionWhenThreadReady({
            detail: {
                questId: answer?.quest_id || this.props.quest?.id,
                childQuestId: answer?.child_quests?.[0]?.id || this.props.quest?.id || null,
                postMediaSource: {
                    kind,
                    answerId: answer?.id || null,
                },
            },
            openRight: this.props.openRight,
            sourcePanel: this.props.panelContext?.panel,
        })
    }

    firstTruthyValue = values => values.find(value => value != null && String(value).trim() !== '')

    remixedSourceAnswerIdForRun = (run, answer) => {
        const actionPayload = run?.action_payload || run?.actionPayload || {}
        const remixSource = actionPayload?.remix_source || actionPayload?.remixSource || {}
        const inputs = actionPayload?.inputs || {}
        const mediaInputs = [
            inputs?.image,
            inputs?.video,
            inputs?.audio,
            ...(Array.isArray(inputs?.videos) ? inputs.videos : []),
            ...(Array.isArray(inputs?.references?.images) ? inputs.references.images : []),
            ...(Array.isArray(inputs?.references?.videos) ? inputs.references.videos : []),
            ...(Array.isArray(inputs?.references?.audios) ? inputs.references.audios : []),
        ].filter(Boolean)

        const answerId = this.firstTruthyValue([
            remixSource?.answer_id,
            remixSource?.answerId,
            ...mediaInputs.flatMap(input => [
                input?.answer_id,
                input?.answerId,
                input?.source_answer_id,
                input?.sourceAnswerId,
            ]),
        ])

        if (!answerId) return null
        const normalizedAnswerId = String(answerId)
        return normalizedAnswerId === String(answer?.id || '') ? null : normalizedAnswerId
    }

    openRemixedSourceChildQuest =
        ({
            sourceAnswerId,
            missingNotice = 'Unable to find the remixed source answer.',
            missingThreadNotice = 'No child thread found for the remixed media.',
        }) =>
        async ev => {
            ev.preventDefault()
            ev.stopPropagation()

            await openSourceAnswerThread({
                answerId: sourceAnswerId,
                openRight: this.props.openRight,
                missingNotice,
                missingThreadNotice,
            })
        }

    // Source post referenced by a simple Illustrate/Film action launched from another
    // answer's controls menu (distinct from media remix provenance).
    postMediaSourceAnswerIdForRun = (run, answer) => {
        const actionPayload = run?.action_payload || run?.actionPayload || {}
        const context = actionPayload?.craft_context || actionPayload?.craftContext || {}
        const answerId = context?.source_answer_id || context?.sourceAnswerId || null
        if (!answerId) return null
        const normalizedAnswerId = String(answerId)
        return normalizedAnswerId === String(answer?.id || '') ? null : normalizedAnswerId
    }

    ordinalTransactionId = ordinal => {
        const record = Object(ordinal || {})
        const origin = record.origin || ''
        return (
            record?.mint_tx?.tx_id ||
            record?.mint_tx_tx_id ||
            record?.tx_id ||
            origin.split(/[_:]/)[0] ||
            null
        )
    }

    bsvTxExplorerUrlForTxId = txId => bsvTxExplorerUrl(txId) || null

    imageBsvAttachmentForPayload = (answer, image) => {
        const attachmentId = image?.attachment_id
        return answer?.bsv_attachments?.find(
            att =>
                (attachmentId && att.attachment_id === attachmentId) ||
                att.attachment_url === answer?.answer_image_url ||
                answer?.image_urls?.some(img => img.attachment_id === att.attachment_id),
        )
    }

    videoBsvAttachmentForPayload = answer => {
        const attachmentId = answer?.recording_info?.attachment_id
        return answer?.bsv_attachments?.find(
            att => attachmentId && att.attachment_id === attachmentId,
        )
    }

    fileBsvAttachmentForPayload = (answer, attachmentId) => {
        if (!attachmentId) return null
        return answer?.bsv_attachments?.find(
            attachment => String(attachment?.attachment_id) === String(attachmentId),
        )
    }

    blockchainUrlForPayload = ({ answer, kind, bsvAttachment, bsvOrdinal = null }) => {
        const attachmentOrdinalTxId = this.ordinalTransactionId(bsvOrdinal)
        if (attachmentOrdinalTxId) return this.bsvTxExplorerUrlForTxId(attachmentOrdinalTxId)
        if (kind === 'image') {
            const ordinalTxId = this.ordinalTransactionId(answer?.image_bsv_ordinal)
            return this.bsvTxExplorerUrlForTxId(ordinalTxId || bsvAttachment?.bsv_tx?.tx_id)
        }
        if (kind === 'video') {
            const ordinalTxId = this.ordinalTransactionId(answer?.video_bsv_ordinal)
            return this.bsvTxExplorerUrlForTxId(ordinalTxId || bsvAttachment?.bsv_tx?.tx_id)
        }
        return this.bsvTxExplorerUrlForTxId(bsvAttachment?.bsv_tx?.tx_id)
    }

    chainIndicatorForPayload = ({ answer, kind, bsvAttachment, bsvOrdinal = null }) => {
        const ordinal =
            (bsvOrdinal?.id ? bsvOrdinal : null) ||
            (kind === 'image' ? answer?.image_bsv_ordinal : null) ||
            (kind === 'video' ? answer?.video_bsv_ordinal : null)
        const ordinalIndicator = payloadOrdinalIndicator(ordinal)
        if (ordinalIndicator) return ordinalIndicator
        return payloadOnchainIndicatorForAttachment(bsvAttachment)
    }

    openPayloadOrdinalThread = indicator => async event => {
        event?.preventDefault?.()
        event?.stopPropagation?.()

        await openPayloadOrdinalThread(indicator, {
            isSignedIn: Boolean(gon?.currentUser),
            ensureOrdinalThread: api.ensureOrdinalThread,
            openAuthModal: () =>
                useStore
                    .getState()
                    .actions.modalActions.openAuthModal?.('signup', 'ordinal_thread'),
            openRight: target =>
                this.props.openRight({ questId: target.questId, answerId: target.answerId }),
            onFailure: (outcome, error) => {
                console.error('Error opening ordinal thread:', outcome, error)
                toast.error(ORDINAL_THREAD_UNAVAILABLE_MESSAGE)
            },
        })
    }

    payloadMintPresentationAnswer = answer => cache.getCachedAnswer(answer?.id) || answer

    payloadMintingFailed = answer =>
        Boolean(
            this.payloadMintPresentationAnswer(answer)?.mint_failure_message ||
                this.payloadMintUiForAnswer(answer)?.phase === 'failed',
        )

    payloadMintingInProgress = answer => {
        const presentationAnswer = this.payloadMintPresentationAnswer(answer)
        if (
            presentationAnswer?.mint_failure_message ||
            presentationAnswer?.mint_recently_completed
        ) {
            return false
        }
        const localPhase = this.payloadMintUiForAnswer(answer)?.phase
        return Boolean(
            ['preparing', 'submitting', 'running'].includes(localPhase) ||
                presentationAnswer?.mint_workflow_id ||
                presentationAnswer?.mint_run_id,
        )
    }

    payloadMintingLabel = answer => {
        if (this.payloadMintingFailed(answer)) return 'Mint failed'
        const presentationAnswer = this.payloadMintPresentationAnswer(answer)
        if (presentationAnswer?.mint_status_message) {
            return ordinalMintPhaseLabel(presentationAnswer.mint_status_message)
        }

        const localPhase = this.payloadMintUiForAnswer(answer)?.phase
        if (localPhase === 'preparing') return 'Preparing'
        if (localPhase === 'submitting') return 'Starting'
        return 'Minting'
    }

    payloadMintedRecently = answer =>
        Boolean(this.payloadMintPresentationAnswer(answer)?.mint_recently_completed)

    aiActionForRun = (run, answer) => {
        const runAction = run?.action_payload || run?.actionPayload || null
        const actions = Array.isArray(answer?.action_requests) ? answer.action_requests : []
        const actionTag = normalizeActionTag(
            run?.action_tag ||
                run?.actionTag ||
                runAction?.tag ||
                runAction?.action_tag ||
                runAction?.actionTag,
        )
        const actionIndex = Number(run?.action_index ?? run?.actionIndex)
        const actionId =
            runAction?.id ||
            runAction?.client_id ||
            runAction?.ai_action_id ||
            run?.action_id ||
            run?.actionId ||
            null
        const tagMatchedActions = actionTag
            ? actions.filter(action => normalizeActionTag(action?.tag) === actionTag)
            : []

        return (
            (actionId &&
                actions.find(
                    action =>
                        action?.id === actionId ||
                        action?.client_id === actionId ||
                        action?.ai_action_id === actionId,
                )) ||
            (Number.isInteger(actionIndex) &&
            actionIndex >= 0 &&
            actionIndex < actions.length &&
            (!actionTag || normalizeActionTag(actions[actionIndex]?.tag) === actionTag)
                ? actions[actionIndex]
                : null) ||
            (tagMatchedActions.length === 1 ? tagMatchedActions[0] : null) ||
            runAction
        )
    }

    aiActionIndexForRun = (run, answer, action = null) => {
        const actions = Array.isArray(answer?.action_requests) ? answer.action_requests : []
        const actionTag = normalizeActionTag(run?.action_tag || run?.actionTag || action?.tag)
        const actionId =
            action?.id ||
            action?.client_id ||
            action?.ai_action_id ||
            run?.action_payload?.id ||
            run?.action_payload?.client_id ||
            run?.actionPayload?.id ||
            run?.actionPayload?.client_id ||
            run?.action_id ||
            run?.actionId ||
            null
        const actionIndex = Number(run?.action_index ?? run?.actionIndex)

        if (actionId) {
            const index = actions.findIndex(
                candidate =>
                    candidate?.id === actionId ||
                    candidate?.client_id === actionId ||
                    candidate?.ai_action_id === actionId,
            )
            if (index >= 0) return index
        }

        if (
            Number.isInteger(actionIndex) &&
            actionIndex >= 0 &&
            actionIndex < actions.length &&
            (!actionTag || normalizeActionTag(actions[actionIndex]?.tag) === actionTag)
        ) {
            return actionIndex
        }

        const tagMatchedActionIndexes = actionTag
            ? actions
                  .map((candidate, index) =>
                      normalizeActionTag(candidate?.tag) === actionTag ? index : -1,
                  )
                  .filter(index => index >= 0)
            : []

        return tagMatchedActionIndexes.length === 1 ? tagMatchedActionIndexes[0] : -1
    }

    getAiActionRefId = attrsOrAction =>
        attrsOrAction?.id ||
        attrsOrAction?.client_id ||
        attrsOrAction?.ai_action_id ||
        attrsOrAction?.actionId ||
        null

    handleActionTokenPromptChange = payload => {
        if (!this.editMode()) return

        const draftAnswer = this.ensureDraftAnswer()
        if (!draftAnswer?.id) return

        const draftActionRequests = draftAnswer?.['action_requests']
        const sourceActions = Array.isArray(draftActionRequests)
            ? draftActionRequests
            : Array.isArray(this.props.answer?.action_requests)
            ? this.props.answer.action_requests
            : []
        if (!sourceActions.length) return

        const attrs = payload?.attrs || {}
        const actionId = this.getAiActionRefId(attrs)
        const payloadIndex = Number.isInteger(payload?.index) ? payload.index : -1
        const actionIndex = actionId
            ? sourceActions.findIndex(action => this.getAiActionRefId(action) === actionId)
            : payloadIndex
        const nextActionIndex = actionIndex >= 0 ? actionIndex : payloadIndex
        if (nextActionIndex < 0 || nextActionIndex >= sourceActions.length) return

        const nextActionRequests = payload?.removed
            ? sourceActions.filter((action, index) => {
                  if (actionId) return this.getAiActionRefId(action) !== actionId
                  return index !== nextActionIndex
              })
            : sourceActions.map((action, index) => {
                  if (index !== nextActionIndex) return action

                  const promptContent =
                      typeof payload?.promptContent === 'string' ? payload.promptContent.trim() : ''
                  const inputs = action?.inputs || {}
                  const textInput = inputs?.text || {}
                  return {
                      ...action,
                      prompt: promptContent,
                      prompt_placement: attrs.promptPlacement || attrs.prompt_placement || null,
                      prompt_is_default: false,
                      sync_with_draft: false,
                      inputs: {
                          ...inputs,
                          text: {
                              ...textInput,
                              value: promptContent,
                          },
                      },
                  }
              })

        cache.updateCachedAnswer(draftAnswer.id, {
            action_requests: nextActionRequests,
        })
    }

    updateFailedAiRunAction = (answer, run, action) => {
        const sourceAnswer =
            this.props.answer?.id === answer?.id ? this.props.answer : answer || this.props.answer
        if (!sourceAnswer?.id || !this.isCurrentUserAnswerOwner(sourceAnswer)) return

        const currentActions = Array.isArray(sourceAnswer.action_requests)
            ? sourceAnswer.action_requests
            : []
        const currentAction = this.aiActionForRun(run, sourceAnswer)
        const actionIndex = this.aiActionIndexForRun(run, sourceAnswer, currentAction)

        if (actionIndex < 0 || actionIndex >= currentActions.length) {
            useStore.getState().showGlobalNotice('Unable to find the AI action to edit.', {
                variant: 'error',
                durationMs: 5000,
            })
            return
        }

        const runId = run?.id || run?.ai_run_id || null
        const previousAction = currentActions[actionIndex] || {}
        const nextAction = {
            ...previousAction,
            ...action,
        }

        if (runId && !nextAction.ai_run_id && !nextAction.aiRunId) {
            nextAction.ai_run_id = runId
        }
        if (runId && !nextAction.ai_run_ids && !nextAction.aiRunIds) {
            nextAction.ai_run_ids = previousAction.ai_run_ids || previousAction.aiRunIds || [runId]
        }

        const nextActionRequests = currentActions.map((candidate, index) =>
            index === actionIndex ? nextAction : candidate,
        )
        const previousAnswer = sourceAnswer

        cache.cacheAnswer({
            ...sourceAnswer,
            action_requests: nextActionRequests,
        })

        api.updateAnswer(sourceAnswer.id, {
            action_requests: nextActionRequests,
        }).catch(error => {
            cache.cacheAnswer(previousAnswer)
            console.error('Failed to update AI action request:', error)
            useStore.getState().showGlobalNotice('Failed to update the AI action.', {
                variant: 'error',
                durationMs: 5000,
            })
        })
    }

    failedAiRunActionEditProps = (run, answer, previewProps) => {
        if (!this.isCurrentUserAnswerOwner(answer)) return {}

        const action = this.aiActionForRun(run, answer)
        if (!action) return {}

        const updateAiAction = nextAction => this.updateFailedAiRunAction(answer, run, nextAction)
        const dispatchEditActionRequest = (detail, event = null) => {
            const editSourceElement =
                typeof Element !== 'undefined' && event?.currentTarget instanceof Element
                    ? event.currentTarget
                    : null
            window.dispatchEvent(
                new CustomEvent('knov:edit-action-request', {
                    detail: {
                        aiAction: action,
                        editSourceElement,
                        updateAiAction,
                        ...detail,
                    },
                }),
            )
        }

        return {
            onEdit: event => dispatchEditActionRequest({ directSettingsEdit: true }, event),
            onEditInput: (inputKind, inputSlotIndex, event) =>
                dispatchEditActionRequest(
                    {
                        focusedInputKind: inputKind,
                        inputSlotIndex,
                        directInputEdit: true,
                    },
                    event,
                ),
            onEditModel: event =>
                dispatchEditActionRequest(
                    {
                        openModelPicker: true,
                        modelPickerModelType: previewProps?.modelType,
                    },
                    event,
                ),
            onEditExtendModel: event => dispatchEditActionRequest({ openExtendModel: true }, event),
            onEditReferences: event => dispatchEditActionRequest({ openReferences: true }, event),
        }
    }

    aiRunActionPreview = (run, answer, editable = false) => {
        const action = this.aiActionForRun(run, answer)
        const previewProps = aiActionToPreviewProps({
            action,
            run,
            aiModels: this.props.aiModels || [],
            quest: this.props.quest,
        })

        return previewProps ? (
            <AiActionPreview
                {...previewProps}
                {...(editable ? this.failedAiRunActionEditProps(run, answer, previewProps) : {})}
            />
        ) : null
    }

    failedAiRunActionPreview = (run, answer) => this.aiRunActionPreview(run, answer, true)

    payloadMediaMenuActions = ({
        kind,
        url,
        label,
        answer,
        thumbnailUrl = null,
        blockchainUrl = null,
        chainIndicator = null,
        attachmentId = null,
        blobId = null,
        runId = null,
        actionId = null,
        sourceAiAction = null,
        sourceAnswerId = null,
        remixedSourceAnswerId = null,
        postMediaSourceAnswerId = null,
        canMint = false,
        isGif = false,
    }) => {
        if (!url) return []
        const mintMediaType = this.payloadMintMediaType(kind)
        const isMinted = isPayloadMediaMinted({
            kind,
            chainKind: chainIndicator?.kind,
            hasAnswerOrdinal: Boolean(answer?.has_ordinal),
            hasImageOrdinal: Boolean(answer?.image_bsv_ordinal?.id),
            hasVideoOrdinal: Boolean(answer?.video_bsv_ordinal?.id),
        })
        const isMinting = this.payloadMintingInProgress(answer)
        const isPreparing = this.payloadMintUiForAnswer(answer)?.phase === 'preparing'
        const isMintFailed = this.payloadMintingFailed(answer)
        const mintEstimate = this.payloadMintEstimateFor({ answer, kind, attachmentId })

        const actions = /** @type {any[]} */ ([])

        // The file leaves the app by its poster's hand: Download is an owner's
        // row, which a knov_admin also gets for video. (Minted media offers
        // none either — the inscription is the copy.)
        const canDownload =
            this.isCurrentUserAnswerOwner(answer) || (kind === 'video' && perms.amKnovAdmin())
        if (!isMinted && canDownload) {
            const downloadUrl = buildPayloadMediaDownloadUrl({
                url,
                answerId: sourceAnswerId || answer?.id,
                attachmentId,
                blobId,
            })
            actions.push({
                label: 'Download',
                icon: <Download size={14} />,
                href: downloadUrl || url,
                download: true,
                downloadName: label,
                external: false,
                onClick: null,
            })
        }

        if (canMint && mintMediaType && !isMinted) {
            actions.push({
                label: isPreparing
                    ? 'Preparing…'
                    : isMinting
                    ? 'Minting…'
                    : isMintFailed
                    ? 'Retry mint'
                    : 'Mint',
                icon: isMinting ? (
                    <i className="fa fa-spinner fa-spin" />
                ) : isMintFailed ? (
                    <RefreshCw size={14} />
                ) : (
                    <OrdinalsIcon size={14} />
                ),
                detail:
                    mintEstimate && !isMinting
                        ? '$' + mintEstimate.estimatedTotalUsd.toFixed(2)
                        : null,
                href: null,
                download: false,
                external: false,
                disabled: isMinting,
                working: isMinting,
                keepOpenWhilePending: true,
                onMenuOpen: () => this.prefetchPayloadMintEstimate({ answer, kind, attachmentId }),
                onClick: isMinting
                    ? null
                    : this.handlePayloadMint({
                          answer,
                          kind,
                          attachmentId,
                      }),
            })
        }

        const writeOnchainState = payloadWriteOnchainState({
            canWrite: this.canPayWriteOnchain({ answer, mediaType: mintMediaType }),
            isMinted,
            bsvTx: payloadAttachmentBsvTx(
                answer?.bsv_attachments,
                payloadWriteOnchainAttachmentIds(answer, attachmentId),
            ),
            isWriting: Boolean(
                this.state.payloadWriteOnchainUi?.[
                    this.payloadMintEstimateKey({ answer, kind, attachmentId })
                ],
            ),
            byteSize: this.payloadWriteOnchainByteSize({
                answer,
                kind,
                attachmentId,
                estimate: mintEstimate,
            }),
        })
        if (writeOnchainState) {
            const writeOnchainSats = payloadWriteOnchainSats(
                mintEstimate?.byteSize,
                mintEstimate?.bsvFeeRateSatsPerByte,
            )
            actions.push({
                label: WRITE_ONCHAIN_LABEL,
                icon:
                    writeOnchainState === 'writing' ? (
                        <i className="fa fa-spinner fa-spin" />
                    ) : (
                        <Blocks size={14} />
                    ),
                detail:
                    writeOnchainState === 'writing'
                        ? 'Writing…'
                        : writeOnchainState === 'too_large'
                        ? 'Over 50 MB'
                        : writeOnchainSats
                        ? formatPayloadSats(writeOnchainSats)
                        : null,
                href: null,
                download: false,
                external: false,
                disabled: writeOnchainState !== 'available',
                working: writeOnchainState === 'writing',
                keepOpenWhilePending: true,
                onMenuOpen: () => this.prefetchPayloadMintEstimate({ answer, kind, attachmentId }),
                onClick:
                    writeOnchainState === 'available'
                        ? this.handlePayloadWriteOnchain({ answer, kind, attachmentId })
                        : null,
            })
        }

        if (kind === 'image' && !isGif) {
            actions.push(
                {
                    label: 'Edit',
                    icon: <Pencil size={14} />,
                    href: null,
                    download: false,
                    external: false,
                    onClick: this.dispatchAiActionFromPayload({
                        tag: 'nb',
                        kind,
                        url,
                        label,
                        answer,
                        thumbnailUrl,
                        attachmentId,
                        blobId,
                        runId,
                        actionId,
                        previewTitle: 'Edit Image',
                    }),
                },
                {
                    label: 'Animate',
                    icon: <Sparkles size={14} />,
                    href: null,
                    download: false,
                    external: false,
                    onClick: this.dispatchAiActionFromPayload({
                        tag: DEFAULT_VIDEO_MODEL.actionTag,
                        kind,
                        url,
                        label,
                        answer,
                        thumbnailUrl,
                        attachmentId,
                        blobId,
                        runId,
                        actionId,
                        previewTitle: 'Animate Image',
                    }),
                },
            )
        } else if (kind === 'video') {
            actions.push(
                {
                    label: 'GIF',
                    icon: <Film size={14} />,
                    href: null,
                    download: false,
                    external: false,
                    onClick: this.dispatchAiActionFromPayload({
                        tag: 'gif',
                        kind,
                        url,
                        label,
                        answer,
                        thumbnailUrl,
                        attachmentId,
                        blobId,
                        runId,
                        actionId,
                    }),
                },
                {
                    label: 'Extend',
                    icon: <Film size={14} />,
                    href: null,
                    download: false,
                    external: false,
                    onClick: this.dispatchAiActionFromPayload({
                        tag: 'extend',
                        kind,
                        url,
                        label,
                        answer,
                        thumbnailUrl,
                        attachmentId,
                        blobId,
                        runId,
                        actionId,
                    }),
                },
            )
        }

        // While the owner's Write Onchain row stands for a file that is not onchain yet, it
        // replaces the chain row, which would only repeat the pending or failed status.
        if ((blockchainUrl || chainIndicator) && !writeOnchainState) {
            const isOrdinal = chainIndicator?.kind === 'ordinal'
            actions.push({
                label: isOrdinal
                    ? payloadOrdinalThreadActionLabel()
                    : payloadChainActionLabel(chainIndicator?.kind),
                icon: null,
                chainIndicator,
                href: isOrdinal ? null : blockchainUrl,
                download: false,
                external: !isOrdinal,
                onClick: isOrdinal ? this.openPayloadOrdinalThread(chainIndicator) : null,
            })
        }

        if (remixedSourceAnswerId) {
            actions.push({
                label: 'Remixed',
                icon: <Disc3 size={14} />,
                href: null,
                download: false,
                external: false,
                onClick: this.openRemixedSourceChildQuest({
                    sourceAnswerId: remixedSourceAnswerId,
                }),
            })
        }

        if (postMediaSourceAnswerId) {
            actions.push({
                label: 'Source Post',
                icon: <Quote size={14} />,
                href: null,
                download: false,
                external: false,
                onClick: this.openRemixedSourceChildQuest({
                    sourceAnswerId: postMediaSourceAnswerId,
                    missingNotice: 'Unable to find the referenced post.',
                    missingThreadNotice: 'No thread found for the referenced post.',
                }),
            })
        }

        // The post's owner (or a knov_admin) adds its media to Music, which
        // puts it on Treechat Radio, or removes it. Only for the post's own
        // media, since the flag lives on that post.
        if (
            ['video', 'audio'].includes(kind) &&
            answer?.id &&
            (!sourceAnswerId || String(sourceAnswerId) === String(answer.id)) &&
            (this.isCurrentUserAnswerOwner(answer) || perms.amKnovAdmin())
        ) {
            const inMusic = answer.is_music === true
            actions.push({
                label: inMusic ? 'Remove from Music' : 'Add to Music',
                icon: <Music size={14} />,
                href: null,
                download: false,
                external: false,
                onClick: this.handleToggleIsMusic(answer, !inMusic),
            })
        }

        if (
            ['image', 'video', 'audio'].includes(kind) &&
            sourceAiAction &&
            typeof sourceAiAction === 'object'
        ) {
            actions.push({
                label: 'Remix',
                icon: <Disc3 size={14} />,
                href: null,
                download: false,
                external: false,
                onClick: this.dispatchAiActionRemix({
                    answer,
                    aiAction: sourceAiAction,
                }),
            })
        }

        return actions
    }

    handleToggleIsMusic = (answer, isMusic) => async () => {
        try {
            const result = await api.setAnswerIsMusic(answer.id, isMusic)
            const merged = {
                ...answer,
                ...(result?.answer || {}),
                is_music: isMusic,
            }
            cache.cacheAnswer(merged)
            this.props.updateAnswer?.(merged)
            toast.success(isMusic ? 'Added to Music' : 'Removed from Music')
        } catch (error) {
            console.error('Failed to update music flag:', error)
            toast.error("Couldn't update the music flag.")
        }
    }

    buildAiRunPayloadItems = answer => {
        const runs = Array.isArray(answer?.ai_runs) ? answer.ai_runs : []
        const runGenerationKey = run =>
            [
                run?.action_index ?? run?.actionIndex ?? 0,
                run?.generation_index ?? run?.generationIndex ?? 0,
            ].join(':')
        const completedGenerationKeys = new Set(
            runs
                .filter(run => Array.isArray(run?.output_urls) && run.output_urls.length > 0)
                .map(runGenerationKey),
        )

        return runs.flatMap(run => {
            const outputs = Array.isArray(run?.output_urls) ? run.output_urls : []
            const actionTag = (run?.action_tag || run?.actionTag || '').replace(/^!+/, '')
            if (!actionProducesGeneratedOutput(actionTag)) return []

            const label = actionTag ? `!${actionTag}` : 'AI action'
            const modelLabel = this.payloadModelLabelForRun(run)
            if (outputs.length) {
                return outputs.map((output, outputIndex) => {
                    const kind = this.aiRunOutputKind(output)
                    const url = this.aiRunOutputUrl(output)
                    const previewUrls = output.preview_urls || output.previewUrls || null
                    const thumbnailUrl =
                        output.preview_url ||
                        output.thumbnailUrl ||
                        output.previewUrl ||
                        previewUrls?.['1x1'] ||
                        previewUrls?.medium ||
                        previewUrls?.small ||
                        null
                    const itemLabel = this.payloadMediaLabel({ kind, actionTag })
                    const isGif = this.isGifPayload({
                        url,
                        contentType: output.mime || output.content_type || output.contentType,
                        actionTag,
                        label: itemLabel,
                    })
                    const metadata = this.payloadActionMetadataForOutput({ run, output, kind })
                    const dimensions = this.payloadMediaDimensions(output)
                    const generatedLyrics =
                        actionTag === 'suno' && kind === 'audio'
                            ? this.sunoGeneratedLyricsForRun(run)
                            : null
                    const attachmentId = output.id || null
                    const blobId = output.blob_id || output.blobId || null
                    const sourceAnswerId = output.source_answer_id || answer?.id || null
                    const bsvAttachment = this.fileBsvAttachmentForPayload(answer, attachmentId)
                    const bsvOrdinal = output?.bsv_ordinal || null
                    const blockchainUrl = this.blockchainUrlForPayload({
                        answer,
                        kind,
                        bsvAttachment,
                        bsvOrdinal,
                    })
                    const chainIndicator = this.chainIndicatorForPayload({
                        answer,
                        kind,
                        bsvAttachment,
                        bsvOrdinal,
                    })
                    return {
                        id: `${run.id || run.ai_run_id || actionTag}-${
                            attachmentId || outputIndex
                        }`,
                        runId: run.id || run.ai_run_id,
                        generated: true,
                        actionId:
                            output.source_action_id ||
                            run?.action_payload?.client_id ||
                            run?.actionPayload?.client_id ||
                            null,
                        attachmentId,
                        blobId,
                        sourceAnswerId,
                        sourceQuestId: answer?.quest_id || null,
                        refreshMediaUrl: () =>
                            this.refreshAiRunOutputUrl({
                                answerId: sourceAnswerId,
                                attachmentId,
                                blobId,
                            }),
                        actionTag,
                        label: itemLabel,
                        tagLabel: label,
                        modelLabel,
                        metadata,
                        width: dimensions.width,
                        height: dimensions.height,
                        createdAt: firstValidMediaTimestamp(
                            output.created_at,
                            output.createdAt,
                            run.created_at,
                            run.createdAt,
                        ),
                        attachedAt: output.attached_at || output.attachedAt || null,
                        blobCreatedAt: output.blob_created_at || output.blobCreatedAt || null,
                        status: run.status || 'succeeded',
                        kind,
                        url,
                        thumbnailUrl,
                        previewUrls,
                        contentType: output.mime || output.content_type || output.contentType,
                        isMinting: this.payloadMintingInProgress(answer),
                        mintingLabel: this.payloadMintingLabel(answer),
                        mintingFailed: this.payloadMintingFailed(answer),
                        mintedRecently: this.payloadMintedRecently(answer),
                        chainIndicator,
                        menuActions: this.payloadMediaMenuActions({
                            kind,
                            url,
                            label: itemLabel,
                            answer,
                            sourceAnswerId,
                            thumbnailUrl,
                            blockchainUrl,
                            chainIndicator,
                            isGif,
                            attachmentId: output.id,
                            blobId: output.blob_id || output.blobId || null,
                            runId: run.id || run.ai_run_id,
                            actionId:
                                output.source_action_id ||
                                run?.action_payload?.client_id ||
                                run?.actionPayload?.client_id ||
                                null,
                            sourceAiAction:
                                run?.action_payload || run?.actionPayload
                                    ? {
                                          ...(run?.action_payload || run?.actionPayload),
                                          remix_ai_run_id: run.id || run.ai_run_id || null,
                                          root_remix_ai_run_id:
                                              run?.root_remix_ai_run_id ||
                                              run?.rootRemixAiRunId ||
                                              run?.remix_ai_run_id ||
                                              run?.remixAiRunId ||
                                              run.id ||
                                              run.ai_run_id ||
                                              null,
                                          remix_kind: 'remix',
                                          remix_source: {
                                              ...((run?.action_payload || run?.actionPayload)
                                                  ?.remix_source ||
                                                  (run?.action_payload || run?.actionPayload)
                                                      ?.remixSource ||
                                                  {}),
                                              ai_run_id: run.id || run.ai_run_id || null,
                                              root_remix_ai_run_id:
                                                  run?.root_remix_ai_run_id ||
                                                  run?.rootRemixAiRunId ||
                                                  run?.remix_ai_run_id ||
                                                  run?.remixAiRunId ||
                                                  run.id ||
                                                  run.ai_run_id ||
                                                  null,
                                              answer_id: answer?.id || null,
                                              attachment_id: output.id || null,
                                          },
                                          tag:
                                              run?.action_payload?.tag ||
                                              run?.actionPayload?.tag ||
                                              run?.action_payload?.action_tag ||
                                              run?.actionPayload?.action_tag ||
                                              run?.action_payload?.actionTag ||
                                              run?.actionPayload?.actionTag ||
                                              run?.action_tag ||
                                              run?.actionTag ||
                                              null,
                                      }
                                    : null,
                            remixedSourceAnswerId: this.remixedSourceAnswerIdForRun(run, answer),
                            postMediaSourceAnswerId: this.postMediaSourceAnswerIdForRun(
                                run,
                                answer,
                            ),
                            canMint: this.isCurrentUserAnswerOwner(answer),
                        }).concat(
                            generatedLyrics
                                ? [
                                      {
                                          label: 'Lyrics',
                                          icon: <FileText size={14} />,
                                          href: null,
                                          download: false,
                                          external: false,
                                          onClick: null,
                                          preview: this.sunoLyricsPreview(generatedLyrics),
                                      },
                                  ]
                                : [],
                        ),
                        onClick:
                            kind === 'image' && url
                                ? ev => {
                                      ev.preventDefault()
                                      ev.stopPropagation()
                                      this.openLightbox(url)
                                  }
                                : undefined,
                        errorMessage: this.aiRunErrorMessage(run),
                    }
                })
            }

            if (
                completedGenerationKeys.has(runGenerationKey(run)) &&
                run.status !== 'failed' &&
                run.status !== 'canceled'
            ) {
                return []
            }

            const action = this.aiActionForRun(run, answer)
            const actionSettings = action?.settings || {}
            const retryActionTag =
                normalizeActionTag(
                    action?.tag ||
                        action?.action_tag ||
                        action?.actionTag ||
                        run?.action_tag ||
                        run?.actionTag,
                ) || actionTag
            const retryDurationSeconds = this.firstNumericPayloadValue([
                actionSettings?.duration_seconds,
                actionSettings?.durationSeconds,
                action?.duration_seconds,
                action?.durationSeconds,
            ])
            const retryLabel = retryActionTag ? `!${retryActionTag}` : label

            return [
                {
                    id: run.id || run.ai_run_id || `${actionTag}-${run.generation_index || 0}`,
                    runId: run.id || run.ai_run_id,
                    generated: true,
                    actionTag: retryActionTag,
                    action,
                    durationSeconds: retryDurationSeconds,
                    label: retryLabel,
                    tagLabel: retryLabel,
                    createdAt: run.created_at,
                    status: run.status || 'pending',
                    kind: 'run',
                    tagPreview: this.aiRunActionPreview(run, answer),
                    message:
                        run.status === 'failed'
                            ? 'Failed'
                            : retryActionTag
                            ? `Generating ${retryLabel}`
                            : 'Generating',
                    errorMessage: this.aiRunErrorMessage(run),
                    creatorFailurePreview:
                        run.status === 'failed' ? this.failedAiRunActionPreview(run, answer) : null,
                },
            ]
        })
    }

    payloadDisplayImageUrlFromImageV2 = image =>
        image?.preview_url ||
        image?.urls?.medium ||
        image?.urls?.small ||
        image?.urls?.large ||
        image?.urls?.original ||
        null

    payloadLightboxImageUrl = (image, originalUrl) =>
        isBrowserDisplayableImage({
            contentType: image?.image_content_type,
            filename: image?.image_filename,
        })
            ? originalUrl
            : image?.urls?.large || image?.preview_urls?.large || null

    payloadOriginalImageUrlFromImageV2 = image =>
        image?.urls?.original ||
        image?.urls?.large ||
        image?.preview_url ||
        image?.urls?.medium ||
        image?.urls?.small ||
        null

    payloadDisplayImageUrlFromLegacyImage = image =>
        image?.preview_url ||
        image?.preview_urls?.medium ||
        image?.preview_urls?.small ||
        image?.preview_urls?.large ||
        image?.image_url ||
        null

    payloadOriginalImageUrlFromLegacyImage = image =>
        image?.image_url ||
        image?.preview_urls?.original ||
        image?.preview_url ||
        image?.preview_urls?.large ||
        image?.preview_urls?.medium ||
        image?.preview_urls?.small ||
        null

    buildLegacyPayloadItems = ({
        fileAnswer,
        showVideo,
        showImageCancel,
        showRecordingCancel,
        showFileCancel,
        videoUrl,
        excludedUrls = [],
    }) => {
        const items = []
        const seenUrls = new Set(excludedUrls.filter(Boolean))
        const addItem = item => {
            if (!item?.url || seenUrls.has(item.url)) return
            seenUrls.add(item.url)
            items.push(item)
        }

        const imagesV2 = Array.isArray(fileAnswer?.images_v2) ? fileAnswer.images_v2 : []
        imagesV2.forEach((image, index) => {
            const url = this.payloadDisplayImageUrlFromImageV2(image)
            const originalUrl = this.payloadOriginalImageUrlFromImageV2(image) || url
            const lightboxUrl = this.payloadLightboxImageUrl(image, originalUrl)
            const dimensions = this.payloadMediaDimensions(image)
            const bsvAttachment = this.imageBsvAttachmentForPayload(fileAnswer, image)
            const blockchainUrl = this.blockchainUrlForPayload({
                answer: fileAnswer,
                kind: 'image',
                bsvAttachment,
            })
            const thumbnailUrl =
                image?.preview_url || image?.urls?.medium || image?.urls?.small || null
            addItem({
                id: `answer-image-${image?.attachment_id || image?.blob_id || index}`,
                attachmentId: image?.attachment_id || null,
                blobId: image?.blob_id || null,
                sourceAnswerId: fileAnswer?.id || null,
                sourceQuestId: fileAnswer?.quest_id || null,
                label: 'Image',
                modelLabel: null,
                width: dimensions.width,
                height: dimensions.height,
                createdAt: image?.created_at || null,
                attachedAt: image?.attached_at || null,
                blobCreatedAt: image?.blob_created_at || null,
                status: 'succeeded',
                kind: 'image',
                url,
                inputUrl: lightboxUrl,
                thumbnailUrl,
                isMinting: this.payloadMintingInProgress(fileAnswer),
                mintingLabel: this.payloadMintingLabel(fileAnswer),
                mintingFailed: this.payloadMintingFailed(fileAnswer),
                mintedRecently: this.payloadMintedRecently(fileAnswer),
                menuActions: this.payloadMediaMenuActions({
                    kind: 'image',
                    url: originalUrl,
                    label: 'Image',
                    answer: fileAnswer,
                    sourceAnswerId: fileAnswer?.id || null,
                    thumbnailUrl,
                    isGif: this.isGifPayload({
                        url,
                        contentType: image?.image_content_type,
                        label: 'Image',
                    }),
                    blockchainUrl,
                    chainIndicator: this.chainIndicatorForPayload({
                        answer: fileAnswer,
                        kind: 'image',
                        bsvAttachment,
                    }),
                    attachmentId: image?.attachment_id,
                    blobId: image?.blob_id || null,
                    actionId: null,
                    runId: null,
                    canMint: this.isCurrentUserAnswerOwner(fileAnswer),
                }),
                chainIndicator: this.chainIndicatorForPayload({
                    answer: fileAnswer,
                    kind: 'image',
                    bsvAttachment,
                }),
                controls:
                    showImageCancel && index === 0 ? (
                        <PreviewCancelButton
                            title="Remove image"
                            ariaLabel="Remove image"
                            testId="edit-answer-image-remove-button"
                            onClick={this.cancelDraftImage}
                        />
                    ) : null,
                onClick: url
                    ? ev => {
                          ev.preventDefault()
                          ev.stopPropagation()
                          this.openLightbox(lightboxUrl || url)
                      }
                    : undefined,
            })
        })

        if (!imagesV2.length) {
            const fallbackImageUrl =
                fileAnswer?.local_answer_image_url || fileAnswer?.answer_image_url
            const fallbackImage = fileAnswer?.image_urls?.find(image =>
                [
                    image?.image_url,
                    image?.preview_url,
                    image?.preview_urls?.original,
                    image?.preview_urls?.large,
                    image?.preview_urls?.medium,
                    image?.preview_urls?.small,
                ].some(url => areSamePreviewUrl(url, fallbackImageUrl)),
            )
            const displayImageUrl =
                this.payloadDisplayImageUrlFromLegacyImage(fallbackImage) || fallbackImageUrl
            const originalImageUrl =
                this.payloadOriginalImageUrlFromLegacyImage(fallbackImage) || fallbackImageUrl
            const lightboxImageUrl = this.payloadLightboxImageUrl(fallbackImage, originalImageUrl)
            const fallbackImageV2 = fileAnswer?.images_v2?.find(image =>
                Object.values(image?.urls || {}).some(url =>
                    areSamePreviewUrl(url, fallbackImageUrl),
                ),
            )
            const dimensions = this.payloadMediaDimensions({
                width:
                    fallbackImage?.image_width ||
                    fallbackImageV2?.image_width ||
                    fileAnswer?.image_width,
                height:
                    fallbackImage?.image_height ||
                    fallbackImageV2?.image_height ||
                    fileAnswer?.image_height,
            })
            const bsvAttachment = this.imageBsvAttachmentForPayload(fileAnswer)
            const blockchainUrl = this.blockchainUrlForPayload({
                answer: fileAnswer,
                kind: 'image',
                bsvAttachment,
            })
            addItem({
                id: 'answer-image',
                attachmentId: fallbackImage?.attachment_id || null,
                blobId: fallbackImageV2?.blob_id || null,
                sourceAnswerId: fileAnswer?.id || null,
                sourceQuestId: fileAnswer?.quest_id || null,
                label: 'Image',
                modelLabel: null,
                createdAt:
                    fallbackImage?.created_at ||
                    fallbackImageV2?.created_at ||
                    fileAnswer?.updated_at,
                status: 'succeeded',
                kind: 'image',
                url: displayImageUrl,
                inputUrl: lightboxImageUrl,
                width: dimensions.width,
                height: dimensions.height,
                isMinting: this.payloadMintingInProgress(fileAnswer),
                mintingLabel: this.payloadMintingLabel(fileAnswer),
                mintingFailed: this.payloadMintingFailed(fileAnswer),
                mintedRecently: this.payloadMintedRecently(fileAnswer),
                menuActions: this.payloadMediaMenuActions({
                    kind: 'image',
                    url: originalImageUrl,
                    label: 'Image',
                    answer: fileAnswer,
                    sourceAnswerId: fileAnswer?.id || null,
                    isGif: this.isGifPayload({
                        url: fallbackImageUrl,
                        contentType:
                            fileAnswer?.image_content_type ||
                            fallbackImage?.image_content_type ||
                            fallbackImageV2?.image_content_type,
                        label: 'Image',
                    }),
                    blockchainUrl,
                    chainIndicator: this.chainIndicatorForPayload({
                        answer: fileAnswer,
                        kind: 'image',
                        bsvAttachment,
                    }),
                    attachmentId: fallbackImage?.attachment_id || null,
                    blobId: fallbackImageV2?.blob_id || null,
                    canMint: this.isCurrentUserAnswerOwner(fileAnswer),
                }),
                chainIndicator: this.chainIndicatorForPayload({
                    answer: fileAnswer,
                    kind: 'image',
                    bsvAttachment,
                }),
                controls: showImageCancel ? (
                    <PreviewCancelButton
                        title="Remove image"
                        ariaLabel="Remove image"
                        testId="edit-answer-image-remove-button"
                        onClick={this.cancelDraftImage}
                    />
                ) : null,
                onClick: fallbackImageUrl
                    ? ev => {
                          ev.preventDefault()
                          ev.stopPropagation()
                          this.openLightbox(lightboxImageUrl || displayImageUrl || fallbackImageUrl)
                      }
                    : undefined,
            })
        }

        if (showVideo) {
            const bsvAttachment = this.videoBsvAttachmentForPayload(fileAnswer)
            const blockchainUrl = this.blockchainUrlForPayload({
                answer: fileAnswer,
                kind: 'video',
                bsvAttachment,
            })
            const thumbnailUrl = fileAnswer?.recording_thumbnail_url || null
            const videoAttachmentId =
                fileAnswer?.mp4_recording_attachment_id ||
                fileAnswer?.recording_attachment_id ||
                fileAnswer?.recording_info?.attachment_id ||
                null
            const videoBlobId =
                fileAnswer?.mp4_recording_blob_id ||
                fileAnswer?.recording_blob_id ||
                fileAnswer?.recording_info?.blob_id ||
                null
            const videoIdentityKey =
                fileAnswer?.recording_key || fileAnswer?.recording_created_at || null
            const videoWidth = this.numericPayloadValue(fileAnswer?.video_width)
            const videoHeight = this.numericPayloadValue(fileAnswer?.video_height)
            addItem({
                id: 'answer-video',
                label: 'Video',
                modelLabel: null,
                attachmentId: videoAttachmentId,
                blobId: videoBlobId,
                sourceAnswerId: fileAnswer?.id || null,
                sourceQuestId: fileAnswer?.quest_id || null,
                mediaIdentityKey: videoIdentityKey,
                createdAt: fileAnswer?.recording_created_at || fileAnswer?.updated_at,
                status: 'succeeded',
                kind: 'video',
                url: showVideo,
                thumbnailUrl,
                width: videoWidth,
                height: videoHeight,
                isMinting: this.payloadMintingInProgress(fileAnswer),
                mintingLabel: this.payloadMintingLabel(fileAnswer),
                mintingFailed: this.payloadMintingFailed(fileAnswer),
                mintedRecently: this.payloadMintedRecently(fileAnswer),
                menuActions: this.payloadMediaMenuActions({
                    kind: 'video',
                    url: showVideo,
                    label: 'Video',
                    answer: fileAnswer,
                    sourceAnswerId: fileAnswer?.id || null,
                    thumbnailUrl,
                    blockchainUrl,
                    chainIndicator: this.chainIndicatorForPayload({
                        answer: fileAnswer,
                        kind: 'video',
                        bsvAttachment,
                    }),
                    attachmentId: videoAttachmentId,
                    blobId: videoBlobId,
                    canMint: this.isCurrentUserAnswerOwner(fileAnswer),
                }),
                chainIndicator: this.chainIndicatorForPayload({
                    answer: fileAnswer,
                    kind: 'video',
                    bsvAttachment,
                }),
                controls:
                    showRecordingCancel && videoUrl ? (
                        <PreviewCancelButton
                            title="Cancel video upload"
                            ariaLabel="Cancel video upload"
                            testId="edit-answer-video-remove-button"
                            onClick={this.cancelDraftRecording}
                        />
                    ) : null,
            })
        }

        const fileUrls = Array.isArray(fileAnswer?.file_urls) ? fileAnswer.file_urls : []
        fileUrls.forEach((file, index) => {
            const url = file?.url
            const kind = this.aiRunOutputKind(file)
            // A New Music song is titled by its author, not by its file name.
            const musicTitle =
                kind === 'audio' || kind === 'video' ? fileAnswer?.music_title?.trim() : null
            const label =
                musicTitle ||
                file?.name ||
                file?.filename ||
                file?.original_filename ||
                this.payloadMediaLabel({ kind })
            const attachmentId = file?.id || file?.attachment_id || null
            const blobId = file?.blob_id || file?.blobId || null
            const bsvAttachment = this.fileBsvAttachmentForPayload(fileAnswer, attachmentId)
            const bsvOrdinal = file?.bsv_ordinal || null
            const blockchainUrl = this.blockchainUrlForPayload({
                answer: fileAnswer,
                kind,
                bsvAttachment,
                bsvOrdinal,
            })
            const chainIndicator = this.chainIndicatorForPayload({
                answer: fileAnswer,
                kind,
                bsvAttachment,
                bsvOrdinal,
            })
            const dimensions = this.payloadMediaDimensions(file)
            const previewUrls = file?.preview_urls || file?.previewUrls || null
            const previewUrl =
                file?.preview_url ||
                file?.previewUrl ||
                previewUrls?.['1x1'] ||
                previewUrls?.medium ||
                previewUrls?.small ||
                previewUrls?.original ||
                null
            addItem({
                id: `answer-file-${attachmentId || blobId || index}`,
                attachmentId,
                blobId,
                sourceAnswerId: fileAnswer?.id || null,
                sourceQuestId: fileAnswer?.quest_id || null,
                label,
                modelLabel: null,
                createdAt: file?.created_at || file?.createdAt || fileAnswer?.updated_at,
                status: 'succeeded',
                kind,
                url,
                width: dimensions.width,
                height: dimensions.height,
                contentType:
                    file?.content_type || file?.contentType || file?.mime || file?.type || null,
                isMinting: this.payloadMintingInProgress(fileAnswer),
                mintingLabel: this.payloadMintingLabel(fileAnswer),
                mintingFailed: this.payloadMintingFailed(fileAnswer),
                mintedRecently: this.payloadMintedRecently(fileAnswer),
                chainIndicator,
                bsvOrdinal,
                thumbnailUrl: previewUrl,
                previewUrls,
                menuActions: this.payloadMediaMenuActions({
                    kind,
                    url,
                    label,
                    answer: fileAnswer,
                    sourceAnswerId: fileAnswer?.id || null,
                    isGif: this.isGifPayload({
                        url,
                        contentType:
                            file?.content_type || file?.contentType || file?.mime || file?.type,
                        label,
                    }),
                    thumbnailUrl: previewUrl,
                    blockchainUrl,
                    chainIndicator,
                    attachmentId,
                    blobId,
                    canMint:
                        this.isCurrentUserAnswerOwner(fileAnswer) &&
                        Boolean(this.payloadMintMediaType(kind)),
                }),
                onClick:
                    kind === 'file' && url
                        ? ev => {
                              ev.preventDefault()
                              ev.stopPropagation()
                              window.open(url, '_blank', 'noopener,noreferrer')
                          }
                        : null,
                controls:
                    showFileCancel && index === 0 ? (
                        <PreviewCancelButton
                            title="Cancel file upload"
                            ariaLabel="Cancel file upload"
                            testId="edit-answer-file-remove-button"
                            onClick={this.cancelDraftFile}
                        />
                    ) : null,
            })
        })

        return items
    }

    ensureDraftAnswer = () => {
        const answer = this.props.answer
        if (answer) {
            // Memoized rows can still hold props from before the draft was linked.
            const draftAnswerId =
                cache.getCachedAnswer(answer.id)?.draft_answer_id || answer.draft_answer_id
            /** @type {import('../../../types/answers').AnswerOrDraft | null} */
            let draftAnswer = cache.getCachedAnswer(draftAnswerId)
            //console.log('ensureDraftAnswer', answer.id, draftAnswer?.id)
            if (!draftAnswer) {
                draftAnswer = answerModel.getDraft(answer)
                cache.cacheAnswers([{ ...answer, draft_answer_id: draftAnswer.id }, draftAnswer])
            }
            return draftAnswer
        } else {
            return null
        }
    }

    getDraftAnswer = () => {
        return this.props.answer?.draft_answer_id
            ? cache.getCachedAnswer(this.props.answer?.draft_answer_id)
            : null
    }

    componentDidMount = () => {
        // This is meant to run hover if a new element is added.
        // Probably similar to what I deleted in PostEmbed TODO
        var myNode = this.containerRef && this.containerRef.current
        setTimeout(
            function () {
                if (myNode && myNode.parentElement?.querySelector(':hover') === myNode) {
                    this.onMouseEnter()
                }
            }.bind(this),
            0,
        )
        this.startAnonymousPresenceTracking()
        // A stream card's breadcrumb strip clicks through this handler too
        // (Quest.registerHeadClick), so it shares the popup/edit guards and
        // the CLICK_ANSWER_GATE event with the post body.
        this.unregisterHeadClick = this.props.registerHeadClick?.(this.onClickTitle)
        this.unregisterHeadPairedHover = this.props.registerHeadPairedHover?.(this.setPairedHover)
    }

    componentDidUpdate = async (prevProps, prevState) => {
        this.ensureDraftAnswer()

        if (prevProps.answer?.id !== this.props.answer?.id) {
            this.startAnonymousPresenceTracking()
        }

        const previousDeltaSummary = getDeltaSummary(prevProps.answer?.delta_json)
        const currentDeltaSummary = getDeltaSummary(this.props.answer?.delta_json)
        const deltaChanged =
            previousDeltaSummary.oversized || currentDeltaSummary.oversized
                ? prevProps.answer?.delta_json !== this.props.answer?.delta_json
                : !isEqual(
                      prevProps.answer?.delta_json?.ops || [],
                      this.props.answer?.delta_json?.ops || [],
                  )

        if (prevProps.answer?.content != this.props.answer?.content || deltaChanged) {
            this.setState({
                value: this.props.answer?.delta_json || getValue(this.props.answer),
                linkPreviews: getInitialLinkPreviews(this.props.answer),
            })
        }

        // Check if we've just exited edit mode and need to update with saved content
        if (prevProps.answer?.updated_at !== this.props.answer?.updated_at) {
            // The answer has been updated, refresh the value
            this.setState({
                value: this.props.answer?.delta_json || getValue(this.props.answer),
                linkPreviews: getInitialLinkPreviews(this.props.answer),
                vidStart:
                    this.props.answer?.vid_start_time ||
                    getVidStartFromUrl(this.props.answer?.url?.address),
            })
        }

        if (
            this.state.pendingPreviewSaveAtMs &&
            !this.state.pendingPreviewAwaitingSaveResponse &&
            (prevProps.answer?.updated_at !== this.props.answer?.updated_at ||
                prevProps.answer?.url?.address !== this.props.answer?.url?.address ||
                prevProps.answer?.delta_json !== this.props.answer?.delta_json)
        ) {
            this.maybeFinalizePendingPreviewState()
        }

        if (!prevState?.showControlsPopup && this.state.showControlsPopup) {
            this.startAnswerControlsPositionTracking()
        } else if (prevState?.showControlsPopup && !this.state.showControlsPopup) {
            this.stopAnswerControlsPositionTracking()
        }

        if (this.state.showControlsPopup) {
            this.observeAnswerControlsPopup()
        }
    }

    editMode = () => this.state.editMode

    showDownload = async newAnswer => {
        const videoTime = this.videoTagRef?.current?.currentTime
        const isPlaying =
            this.videoTagRef?.current?.currentTime > 0 && !this.videoTagRef.current.ended

        if (!isPlaying) {
            //console.log('show download not playing')
            this.videoTagRef?.current?.setAttribute(
                'src',
                `${newAnswer?.mp4_recording_url}#t=${videoTime > 0.1 ? videoTime : 0.1}`,
            )
        } else if (this.videoTagRef?.current) {
            //console.log('show download playing')
            this.videoTagRef.current.onended = () => {
                //console.log('show download onend')
                this.videoTagRef.current.setAttribute(
                    'src',
                    `${newAnswer?.mp4_recording_url}#t=${videoTime > 0.1 ? videoTime : 0.1}`,
                )
            }
        }

        this.spinnerRef?.current?.setAttribute('style', 'display: none;')
        if (this.downloadRef?.current?.firstChild)
            this.downloadRef.current.firstChild.setAttribute('href', newAnswer?.mp4_recording_url)
        this.downloadRef?.current?.setAttribute('style', 'display: block;')
    }

    setEmbeds = embeds => {
        const draftAnswer = this.ensureDraftAnswer()
        if (draftAnswer) {
            const nextEmbeds = this.filterEmbedsForCurrentVisibility(embeds)
            cache.updateCachedAnswer(draftAnswer.id, { embeds: nextEmbeds })
        }
        this.setState({ embedsLoading: false, embedLoadingPromise: null })
    }

    setEmbedsLoading = (loading, promise = null) => {
        this.setState({ embedsLoading: loading, embedLoadingPromise: promise })
    }

    setLinkPreviews = linkPreviews => {
        // Filter out stream URLs before storing in state
        const filteredLinkPreviews = (linkPreviews || []).filter(lp => {
            const isStreamUrl = lp?.match(getStreamLinkRegexp()) && extractStreamEmbedConfig(lp)
            return !isStreamUrl // Only keep non-stream URLs
        })

        this.setState({ linkPreviews: filteredLinkPreviews })
        this.pruneHiddenPreviewUrls()
    }

    componentWillUnmount = () => {
        this.unregisterHeadClick?.()
        this.unregisterHeadPairedHover?.()
        clearInterval(window.answerScrollTimer)
        this.uploadManager?.dispose?.()
        this.stopAnswerControlsPositionTracking()
        this.stopAnonymousPresenceTracking?.()
        // A row unmounting mid-edit (filter change, rowset mutation) must release its editing
        // reserve/clip-lift, or the row stays permanently oversized and unclipped.
        if (this.state.editMode) this.notifyRowEditingChange(false)
    }

    // static contextType leaves this.context typed unknown under checkJs.
    notifyRowEditingChange = editing => {
        const rowGeometryEvents = /** @type {any} */ (this.context)
        rowGeometryEvents?.notifyAnswerEditingChange?.({
            answerId: this.props.answer?.id,
            editing,
        })
    }

    getDomContent = () => {
        return this.postAnswer?.current?.content?.content?.innerText
    }

    // Re-derive from the address so stale cached answers with a bogus
    // video_id for non-video YouTube URLs (playlists) don't mount the player.
    getYouTubeVideoId = () =>
        this.props?.answer?.yt_app ? getUrlModelYoutubeVideoId(this.props.answer?.url) : null
    isYouTube = () => !!this.getYouTubeVideoId()

    getYtPlayer = () => this.youtubePlayerRef.current?.getYtPlayer?.()

    loadAndPlay = () => {
        const videoId = this.getYouTubeVideoId()
        if (videoId) this.setState({ youtubeLoadedVideoId: videoId })
        this.youtubePlayerRef.current?.loadAndPlay?.()
    }

    handleYouTubeLoadedChange = (videoId, isLoaded) => {
        this.setState(state => {
            const nextLoadedVideoId = isLoaded ? videoId : null
            if (state.youtubeLoadedVideoId === nextLoadedVideoId) return null
            return { youtubeLoadedVideoId: nextLoadedVideoId }
        })
    }

    toggleEdit = evOrOptions => {
        const draftAnswer = this.getDraftAnswer()
        const options = evOrOptions && !evOrOptions.preventDefault ? evOrOptions : {}
        const preserveDraft = !!options.preserveDraft
        const nextEditMode = !this.state.editMode
        this.notifyRowEditingChange(nextEditMode)
        this.setState(
            state => {
                // Hide the jump-to-latest chip when editing.
                this.props.panelContext.setExternalHideBottomChip?.(!state.editMode)

                return {
                    editMode: !state.editMode,
                    showBottomControls: !state.showBottomControls,
                    showControlsPopup: false,
                    value: getValue(this.props.answer),
                    hiddenPreviewUrls: nextEditMode
                        ? this.getInitialHiddenPreviewUrlsForEdit(draftAnswer)
                        : this.props.answer?.suppressed_preview_urls || [],
                    pendingClearedPreviewUrls: nextEditMode ? state.pendingClearedPreviewUrls : [],
                    pendingSuppressedPreviewUrls: nextEditMode
                        ? state.pendingSuppressedPreviewUrls
                        : [],
                    pendingPreviewAwaitingSaveResponse:
                        nextEditMode || preserveDraft
                            ? state.pendingPreviewAwaitingSaveResponse
                            : false,
                }
            },
            () => {
                if (
                    nextEditMode &&
                    draftAnswer?.id &&
                    this.props.answer?.url &&
                    !draftAnswer?.url
                ) {
                    cache.updateCachedAnswer(draftAnswer.id, {
                        url: this.props.answer.url,
                        url_id: this.props.answer.url_id,
                    })
                }
                if (!nextEditMode && draftAnswer?.id && !preserveDraft) {
                    cache.cacheAnswer(
                        {
                            ...answerModel.getDraft(this.props.answer),
                            id: draftAnswer.id,
                        },
                        true,
                    )
                }
            },
        )
    }

    getInitialHiddenPreviewUrlsForEdit = draftAnswer => {
        const baseHiddenUrls = draftAnswer?.suppressed_preview_urls || []
        const attachedUrlAddress = this.props.answer?.url?.address
        if (attachedUrlAddress) return baseHiddenUrls

        const extractedUrls = extractUrls(this.props.answer?.delta_json || {})
        return (extractedUrls.nonKnovLinks || []).reduce(
            (hiddenUrls, url) => addSuppressedPreviewUrl(hiddenUrls, url),
            baseHiddenUrls,
        )
    }

    toggleArchiveConfirm = ev => {
        this.setState(state => {
            return {
                archiveConfirm: !state.archiveConfirm,
                showBottomControls: !state.showBottomControls,
                showControlsPopup: false,
            }
        })
    }

    toggleDeleteConfirm = ev => {
        this.setState(state => {
            return {
                deleteConfirm: !state.deleteConfirm,
                showBottomControls: !state.showBottomControls,
                showControlsPopup: false,
            }
        })
    }

    getEditor = () => this.postAnswer?.current?.getEditor()
    getDeltaJson = () => this.getEditor()?.getContents()
    getContent = () => getText(this.getDeltaJson() || {}).trim()

    copyContent = () => getText(this.props.answer?.delta_json)

    getPublicLink = async () => {
        const fallbackUrl = getAnswerExternalShareUrl(this.props.answer)
        if (!gon?.currentUser || !this.props.answer?.id) return fallbackUrl

        try {
            const result = await api.getAnonymousShareLink(this.props.answer.id)
            this.setState({ shareRewardEnabled: result?.reward_enabled === true })
            return result?.url || fallbackUrl
        } catch {
            this.setState({ shareRewardEnabled: false })
            return fallbackUrl
        }
    }

    startAnonymousPresenceTracking = () => {
        this.stopAnonymousPresenceTracking?.()
        this.stopAnonymousPresenceTracking = observeAnonymousAnswerPresence(
            this.containerRef?.current,
            this.props.answer?.id,
        )
    }

    hasFiles = () => !!this.state.files?.length
    hasVideo = () => !!this.state.video
    hasImage = () => !!this.state.imgPreview

    maybeFinalizePendingPreviewState = () => {
        if (!this.state.pendingPreviewSaveAtMs || this.state.pendingPreviewAwaitingSaveResponse) {
            return
        }

        const answerUpdatedAtMs = this.props.answer?.updated_at
            ? new Date(this.props.answer.updated_at).getTime()
            : 0
        const serverHasCaughtUp = answerUpdatedAtMs >= this.state.pendingPreviewSaveAtMs
        const currentUrlAddress = this.props.answer?.url?.address || null
        const persistedSuppressedUrls = this.props.answer?.suppressed_preview_urls || []
        const extractedUrls = extractUrls(this.props.answer?.delta_json || {})
        const currentEditorUrls = [
            ...(extractedUrls.nonKnovLinks || []),
            ...(extractedUrls.knovEmbedLinks || []),
            ...(extractedUrls.streamEmbedLinks || []),
        ]
        const pendingClearSettled = (this.state.pendingClearedPreviewUrls || []).every(url => {
            return !areSamePreviewUrl(currentUrlAddress, url)
        })
        const pendingSuppressionSettled = (this.state.pendingSuppressedPreviewUrls || []).every(
            url =>
                !currentEditorUrls.some(currentEditorUrl =>
                    areSamePreviewUrl(currentEditorUrl, url),
                ),
        )

        if (serverHasCaughtUp && pendingClearSettled && pendingSuppressionSettled) {
            this.setState({
                pendingClearedPreviewUrls: [],
                pendingSuppressedPreviewUrls: [],
                pendingPreviewSaveAtMs: null,
            })
        }
    }

    savePost = async () => {
        const content = this.getContent()
        const deltaJson = this.getDeltaJson()
        const draftAnswer = this.ensureDraftAnswer()
        const hasPendingRecording =
            draftAnswer?.recording && this.isPendingUploadValue(draftAnswer.recording)
        const hasPendingFiles =
            Array.isArray(draftAnswer?.files) &&
            draftAnswer.files.some(file => this.isPendingUploadValue(file))
        const uploadProgress =
            /** @type {{ recording: number | null, files: Record<string, number> }} */ (
                this.state.uploadProgress || { recording: null, files: {} }
            )
        const hasActiveProgress = (() => {
            const recPct = uploadProgress.recording
            if (recPct != null && recPct < 100) return true
            const files = uploadProgress.files || {}
            return Object.values(files).some(p => p != null && /** @type {number} */ (p) < 100)
        })()
        if (hasPendingRecording || hasPendingFiles || hasActiveProgress) {
            useStore
                .getState()
                .showGlobalNotice('Please wait for uploads to finish before saving.', {
                    variant: 'error',
                    durationMs: 4000,
                })
            return
        }
        if (!draftAnswer) {
            useStore.getState().showGlobalNotice('Unable to prepare this edit. Please try again.', {
                variant: 'error',
                durationMs: 5000,
            })
            return
        }

        if (this.validContent()) {
            const now = new Date()

            const answer = this.props.answer
            const hiddenPreviewUrls = answerModel.mergeHiddenPreviewUrls(
                this.getHiddenPreviewUrls(),
                this.getPersistedSuppressedPreviewUrls(),
                this.state.pendingClearedPreviewUrls || [],
                this.state.pendingSuppressedPreviewUrls || [],
            )
            const visibleKnovEmbedLinks = await answerModel.waitForVisibleEmbeds({
                deltaJson: deltaJson || {},
                hiddenPreviewUrls,
                isEmbedsLoading: () => this.state.embedsLoading,
                getEmbedLoadingPromise: () => this.state.embedLoadingPromise,
                setWaitingForEmbeds: waiting => this.setState({ waitingForEmbeds: waiting }),
            })
            const ensuredEmbeds = await this.ensureEmbedsForLinks(visibleKnovEmbedLinks)
            if (ensuredEmbeds == null) return
            const attachmentState = answerModel.getComposeAttachmentState({
                answer,
                draftAnswer,
                isEditing: true,
                extractedUrls: extractUrls(deltaJson || {}),
                hiddenPreviewUrls,
                resolveEmbedAnswer: embed => cache.getCachedAnswer(embed?.id) || embed,
            })
            const resolvedUrlAttachment = await answerModel.ensureUrlAttachmentForSubmit(
                attachmentState,
            )
            if (resolvedUrlAttachment === null) {
                alert('Unable to attach the URL right now. Please try again.')
                return
            }
            const persistedSuppressedPreviewUrls = this.pruneHiddenPreviewUrls()
            const nextEmbeds = ensuredEmbeds || []
            const clearEmbeds = !!answer?.embeds?.length && !nextEmbeds.length
            const clearFiles =
                !!answer?.file_urls?.length &&
                !(draftAnswer.files || []).length &&
                !(draftAnswer.file_urls || []).length
            const clearRecording =
                !!(answer?.recording_url || answer?.mp4_recording_url) &&
                !draftAnswer.recording &&
                !draftAnswer.recording_url &&
                !draftAnswer.mp4_recording_url
            const clearImage =
                !!answer?.answer_image_url &&
                !draftAnswer.answer_image &&
                !draftAnswer.answer_image_url &&
                !draftAnswer.local_answer_image_url
            const clearUrl = !!answer?.url?.address && !resolvedUrlAttachment.url_id
            const pendingClearedPreviewUrls =
                clearUrl && answer?.url?.address ? [answer.url.address] : []
            const pendingSuppressedPreviewUrls = []
            const editableDraftFields = pick(draftAnswer, EDIT_WHITELIST)
            const uploadFields = this.answerUpdateUploadFields(draftAnswer)
            const newAnswer = {
                ...answer,
                ...editableDraftFields,
                id: answer.id,
                is_draft: false,
                content: content,
                delta_json: deltaJson,
                embeds: nextEmbeds,
                updated_at: now,
                url: resolvedUrlAttachment.url,
                url_id: resolvedUrlAttachment.url_id,
                ...(clearUrl ? { url: null, url_id: null } : {}),
            }

            cache.cacheAnswer(newAnswer)
            // Bring draft into parity with answer to be published.
            cache.cacheAnswer(
                {
                    ...answerModel.getDraft(newAnswer),
                    id: draftAnswer.id,
                    suppressed_preview_urls: persistedSuppressedPreviewUrls,
                },
                true,
            )
            this.toggleEdit({ preserveDraft: true })

            // Update component state with the new content
            this.setState({
                value: deltaJson,
                linkPreviews: extractUrls(deltaJson)?.nonKnovLinks || [],
                pendingClearedPreviewUrls,
                pendingSuppressedPreviewUrls,
                pendingPreviewSaveAtMs: now.getTime(),
                pendingPreviewAwaitingSaveResponse: true,
            })

            api.updateAnswer(answer?.id, {
                updated_at: now,
                content,
                delta_json: deltaJson,
                url_id: newAnswer.url_id,
                embeds: newAnswer.embeds,
                ...uploadFields,
                action_requests: newAnswer.action_requests,
                clear_embeds: clearEmbeds,
                clear_files: clearFiles,
                clear_recording: clearRecording,
                clear_image: clearImage,
                clear_url: clearUrl,
            })
                .then(() => {
                    this.setState(
                        {
                            pendingPreviewAwaitingSaveResponse: false,
                        },
                        this.maybeFinalizePendingPreviewState,
                    )
                })
                .catch(error => {
                    useStore
                        .getState()
                        .showGlobalNotice(
                            api.getReadableApiErrorMessage(
                                error,
                                'Unable to update answer. Please try again.',
                            ),
                            { variant: 'error', durationMs: 6500 },
                        )
                    this.setState({
                        pendingClearedPreviewUrls: [],
                        pendingSuppressedPreviewUrls: [],
                        pendingPreviewSaveAtMs: null,
                        pendingPreviewAwaitingSaveResponse: false,
                    })
                })
        }

        if (!this.validContent()) {
            useStore.getState().showGlobalNotice('Add text or media before saving.', {
                variant: 'error',
                durationMs: 4000,
            })
        }
    }

    updatePost = async () => {
        const content = this.getContent()
        const deltaJson = this.getDeltaJson()

        if (this.validContent()) {
            try {
                const answer = await api.updateAnswer(this.props.answer?.id, {
                    content: content,
                    delta_json: deltaJson,
                    url_id: this.props.answer?.url_id,
                })
                return answer
            } catch (error) {
                useStore
                    .getState()
                    .showGlobalNotice(
                        api.getReadableApiErrorMessage(
                            error,
                            'Unable to update answer. Please try again.',
                        ),
                        { variant: 'error', durationMs: 6500 },
                    )
                return null
            }
        } else {
            return null
        }
    }

    squashEventInEditMode = ev => {
        if (this.editMode()) {
            ev.preventDefault()
            ev.stopPropagation()
        }
    }

    squashEvent = ev => {
        ev.preventDefault()
        ev.stopPropagation()
    }

    onExternalInteractionStart = ev => {
        if (
            !shouldBlurNewAnswerBeforeExternalInteraction({
                isMobile,
                interactionInsideComposer: false,
            })
        ) {
            return
        }
        // The reply press that lands in the composer on screen is the one
        // action here that wants the composer's focus kept: this capture
        // handler runs before the button's own touch handling, so the blur
        // here would take the keyboard down under the reader's thumbs (and
        // fold the pill) a moment before the press put the quote in it and
        // the keyboard came back.
        if (this.isPreservedReplyPress(ev)) return

        // bottom_editor_dock, in a docked thread: with a keyboard up, a touch
        // here may be the start of a scroll under it, and scrolling must not
        // take the keyboard down. Dismissal waits for a completed tap
        // (onExternalTap). Only the stale state below — composer focused, no
        // keyboard — is still released now.
        const dock = peekBottomEditorDockSnapshot()
        if (
            dock.docked &&
            !shouldReleaseStaleComposerFocus({
                composerFocused: true,
                keyboardPresent: dock.keyboardPresent,
            })
        ) {
            return
        }

        // Release stale contenteditable focus before a post action handles the gesture.
        // Otherwise mobile browsers can restore the keyboard instead of delivering the action.
        blurFocusedNewAnswer()
    }

    // bottom_editor_dock: a completed tap on a docked thread with a keyboard
    // up is the reader's "done". It runs on the row's click, after the tapped
    // control's own handling (browsers synthesize no click for a scroll), and
    // leaves alone the reply press that keeps the composer's focus.
    onExternalTap = ev => {
        if (!isMobile) return
        const dock = peekBottomEditorDockSnapshot()
        if (!dock.docked || !dock.keyboardPresent) return
        if (this.isPreservedReplyPress(ev)) return
        blurFocusedNewAnswer()
    }

    isPreservedReplyPress = ev => {
        if (!this.replyPreservesFocus()) return false
        const target = ev?.target
        return !!(
            target instanceof Element && target.closest('[data-testid="answer-reply-button"]')
        )
    }

    isAnswerInteractiveTarget = ev => {
        try {
            const target = ev?.target
            const el =
                target && target.nodeType === Node.ELEMENT_NODE
                    ? target
                    : target?.parentElement || null
            return Boolean(
                el &&
                    typeof el.closest === 'function' &&
                    el.closest('[data-answer-interactive="true"]'),
            )
        } catch {
            return false
        }
    }

    validContent = () => {
        const draftAnswer = this.getDraftAnswer()
        const embeds = draftAnswer?.embeds
        const content = this.getContent()
        const deltaJson = this.getDeltaJson()
        const hasFile = draftAnswer?.files?.length > 0
        const hasVideo = draftAnswer?.recording_url || draftAnswer?.mp4_recording_url
        const hasImage = draftAnswer?.answer_image_url || getImages(deltaJson)

        return (
            content.length ||
            getText(deltaJson) ||
            hasVideo ||
            hasImage ||
            embeds?.length ||
            // TODO how is this calculated? should this be in the draft quest?
            this.props.answer?.url ||
            hasFile
        )
    }

    isUploadValue = value => {
        if (!value) return false
        if (typeof File !== 'undefined' && value instanceof File) return true
        if (typeof Blob !== 'undefined' && value instanceof Blob) return true
        if (typeof value !== 'string') return false

        return !/^(?:https?:)?\/\//i.test(value)
    }

    isPendingUploadValue = value => {
        if (!value) return false
        if (typeof File !== 'undefined' && value instanceof File) return true
        if (typeof Blob !== 'undefined' && value instanceof Blob) return true
        return typeof value === 'object'
    }

    answerUpdateUploadFields = draftAnswer => {
        const files = Array.isArray(draftAnswer?.files)
            ? draftAnswer.files.filter(this.isUploadValue)
            : []

        return {
            ...(files.length ? { files } : {}),
            ...(this.isUploadValue(draftAnswer?.recording)
                ? { recording: draftAnswer.recording }
                : {}),
            ...(this.isUploadValue(draftAnswer?.answer_image)
                ? { answer_image: draftAnswer.answer_image }
                : {}),
        }
    }

    insertAnswer = async ev => {
        ev.preventDefault()
        ev.stopPropagation()
        this.props.insertAnswer(this.props.answer?.id)
    }

    isUserSignedIn = () => !!gon.currentUser
    isOwner = () => gon.currentUser && gon.currentUser.id === this.props.answer?.user_id

    showInsert = () => {
        if (typeof this.props.showInsert === 'undefined')
            return this.props.showAll && this.isUserSignedIn() && this.isOwner()
        else return this.props.showInsert
    }

    setImageDownload = imageDownload => {
        this.setState({ imageDownload })
    }

    setControlButtons = show => {}

    canHoverCard = () => {
        if (!this.isSystemUser()) return true

        const answer = this.props.answer
        const quest = this.props.quest
        if (!isArtifactWrapperAnswer(answer)) return false

        return shouldNavigateQuestTitle({
            isDraft: Boolean(quest?.is_draft),
            isEditing: Boolean(this.editMode()),
            isSystem: true,
            leaderboardAnnouncement: Boolean(this.isLeaderboardAnnouncement()),
            radioPlay: this.isRadioPlayAnswer(),
            mediaAnnouncement: this.isMediaAnnouncementAnswer(),
            messageType: answer?.message_type,
        })
    }

    // A Treechat Radio play card (its own room's head, or the card in a
    // stream): system-authored, but the room behaves like any other thread.
    isRadioPlayAnswer = () =>
        Boolean(
            this.props.answer?.action_tags?.radio_play ||
                this.props.quest?.parent?.action_tags?.radio_play,
        )

    isMediaAnnouncementAnswer = () =>
        Boolean(
            this.props.answer?.action_tags?.media_announcement ||
                this.props.quest?.parent?.action_tags?.media_announcement,
        )

    // Whether the post reads as hovered. Its own box is one source; a stream
    // card's breadcrumb strip is the other half of the head post's surface and
    // arrives as pairedHover (Quest.onCrumbHover), under the same gate.
    isHovered = () => this.state.hover || (this.state.pairedHover && !this.hoverBlocked())

    setPairedHover = hovered => {
        if (this.state.pairedHover !== hovered) this.setState({ pairedHover: hovered })
    }

    // The thin controls' money verbs take the pointer's attention from the post:
    // while one carries it, the post drops its hover highlight (its hover
    // controls stay, the pointer is still on the post).
    setMoneyControlHover = moneyControlHover => {
        if (this.state.moneyControlHover !== moneyControlHover) this.setState({ moneyControlHover })
    }

    onMouseEnter = ev => {
        if (this.hoverBlocked()) return

        this.setState({
            hover: true,
            showControlButtons: true,
        })
    }
    onMouseLeave = ev => {
        if (this.hoverBlocked()) return

        this.setState({
            hover: false,
            showControlButtons: false,
        })
    }

    hoverBlocked = () => !this.canHoverCard() || isMobile || this.props.isEmbed

    isSelected = () => {
        if (this.props.isTitle) return false
        const panel = this.props.panelContext.panel
        if (!panel) return false

        return useStore.getState().selectedAnswers[panel.panelId]?.answerId === this.props.answer.id
    }

    hasValidChildPanel = () => {
        const panel = this.props.panelContext.panel
        if (!panel) return false

        const panelToRight = useStore.getState().panels.getPanelToRight(panel.panelId)
        const childQuest = this.props.answer.child_quests?.[0]

        return (
            panelToRight &&
            !panelToRight.empty &&
            childQuest &&
            panelToRight.filter?.questId === childQuest.id
        )
    }

    toggleSelectedAnswer = async ev => {
        if (this.props.isTitle) {
            this.onClickTitle(ev)
            return
        }

        // If the click originated from the drag grip, ignore it (do not toggle selection).
        // This prevents accidental selection when starting a drag without needing stopPropagation on the handle.
        try {
            if (this.isAnswerInteractiveTarget(ev)) return
            if (ev?.defaultPrevented) return
            const t = ev?.target
            const el =
                t && t.nodeType === Node.ELEMENT_NODE
                    ? t
                    : t && t.parentElement
                    ? t.parentElement
                    : null
            if (el && typeof el.closest === 'function' && el.closest('[data-dnd-handle="true"]')) {
                return
            }
        } catch {}

        // A press that began on one of the row's controls (the quote-reply
        // pill, the value meter) belongs to that control. Its click can still
        // reach this row, retargeted here when the press blurs the composer
        // (toggling a live quote) and the row shifts under the pointer before
        // it lifts. That is not a branch. A click in the gaps between the
        // controls is a click on the post, and branches.
        const pressStartedOnControl = this.pressStartedOnControl
        this.pressStartedOnControl = false
        if (pressStartedOnControl || this.isControlsRowControl(ev?.target)) return

        if (!this.canToggle(ev)) return

        ev.preventDefault()
        ev.stopPropagation()

        const { answer, panelContext, openRight } = this.props
        const panel = panelContext.panel
        const panelId = panel.panelId

        // Only make API call if we don't have child_quests data
        let childQuestId
        if (answer.child_quests?.[0]?.id) {
            childQuestId = answer.child_quests[0].id
        } else {
            const commentQuest = await api.getCommentQuest(answer.id)
            childQuestId = commentQuest?.id
        }

        if (childQuestId) {
            seedChildQuestFromAnswer(answer, childQuestId)

            // Mark as selected in current panel for UI state
            useStore.getState().set(state => {
                state.selectedAnswers[panelId] = { answerId: answer.id }
            })

            await openRight(
                { questId: childQuestId, answerId: answer.id },
                { metricInputEvent: ev },
            )
            refreshChildQuestInBackground(childQuestId)
        }
    }

    // The controls row's own controls: its buttons, the value meter, the
    // upvalue group. The row's padding and the gaps between them are the post.
    isControlsRowControl = target => {
        const el = target?.nodeType === Node.ELEMENT_NODE ? target : target?.parentElement
        const control = el?.closest?.('button, a[href], [role="button"], [data-answer-row-control]')
        return Boolean(control && this.controlsRowRef.current?.contains(control))
    }

    isTitleSelected = () => {
        if (!this.props.isTitle) return false
        const panel = this.props.panelContext.panel
        const panelToLeft = useStore.getState().panels.getPanelToLeft(panel.panelId)
        if (useStore.getState().selectedAnswers[panelToLeft.panelId]) {
            return (
                useStore.getState().selectedAnswers[panelToLeft.panelId].answerId ===
                this.props.answer.id
            )
        } else {
            return false
        }
    }

    deleteEmbed = embedId => {
        if (this.props.deleteEmbed && this.props.quest) {
            useStore.getState().deleteEmbed(this.props.quest, this.props.answer, embedId)
            const newEmbeds = this.props.answer.embeds.filter(e => e.id !== embedId)
            this.setEmbeds({ embeds: newEmbeds })
        }
    }

    clearSelection = () => {
        const selection = window.getSelection()
        const hasTextSelection = selection.toString().length > 0
        // If there is a text selection we want to clear it instead of toggling selected answer.
        if (hasTextSelection) {
            let anchor = selection.anchorNode
            if (anchor.nodeType !== Node.ELEMENT_NODE) anchor = anchor.parentElement
            const anchorInSelection = !!(
                /** @type {Element} */ (anchor)?.closest('.with-selection-comp')
            )
            if (!anchorInSelection || this.clickOutsideSelection) {
                selection.removeAllRanges()
            }
            this.clickOutsideSelection = null
            return true
        } else return false
    }

    canToggle = ev => {
        // Get className as string, handling SVG elements
        const className =
            typeof ev?.target?.className === 'string'
                ? ev.target.className
                : ev?.target?.className?.baseVal || ''

        return (
            !this.state.showControlsPopup &&
            !this.state.showBottomControls &&
            !this.isSystemUser() &&
            // Make sure no text is selected, clear it if there is.
            !this.clearSelection() &&
            !this.isAnswerInteractiveTarget(ev) &&
            !this.props.isEmbed &&
            !window.isPanelScrolling &&
            // Catch event from wierd hidden form for image upload.
            !className.includes('image-upload-control') &&
            this.props.quest
        )
    }

    childCount = () => {
        const answer = this.props.answer
        if (this.props.isTitle) {
            let count = questUnarchivedAnswerCount(this.props.quest)
            // quote_main: the branch's own title counts the union too — its
            // guest half arrives via Quest's quoteAnswers fetch (threaded down
            // as headQuoteCount; 0 whenever the fetch hasn't run, e.g. stream
            // cards).
            if (quoteMainEnabled()) count += this.props.headQuoteCount || 0
            return count
        }
        let count = 0
        if (answer?.child_answer_count) {
            count = answer?.child_answer_count
        } else {
            const childQuests = this.props?.answer?.child_quests
            if (childQuests && childQuests.length > 0) {
                const childQuest = childQuests.find(quest => quest?.side_quest)
                if (childQuest) {
                    const quest = useStore.getState().quests[childQuest?.quest_id] || childQuest
                    count = questUnarchivedAnswerCount(quest)
                }
            }
        }
        // quote_main: the branch is the union, so the pill counts the
        // main-thread quotes that will appear there too. Quotes of this
        // answer are counted once by the rendering container and passed as a
        // reactive prop (titles already count them as plain room replies).
        if (quoteMainEnabled()) count += this.props.quoteCount || 0
        return count
    }

    showGate = () => {
        const isActiveTitle = this.props.isActive && this.props.isTitle
        if (isActiveTitle) return false // Hide gate on active quest titles

        const hover = true //this.state.hover && !isActiveTitle
        const isTitleInStream = !this.props.isActive && this.props.isTitle
        const selectedOrChildCount =
            !this.props.isTitle && (this.isSelected() || this.childCount() > 0)
        return !this.isSystemUser() && (hover || isTitleInStream || selectedOrChildCount)
    }

    postAnswer = React.createRef()
    title = React.createRef()

    toggleShowKnovigator = () => {
        if (gon && gon.currentUser && gon.currentUser.id === this.props.answer?.user.id)
            this.setState(state => {
                return { showKnovigator: !state.showKnovigator }
            })
    }

    deleteFile = (attachmentId, answerId) => {
        fetch(`${window.knovApiUrl}/answers/delete_file`, {
            method: 'POST',
            body: JSON.stringify({
                attachment_id: attachmentId,
                answer_id: answerId,
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        })
    }

    fileHandler = evOrFile => {
        const draftId = this.getDraftAnswer()?.id
        if (!draftId) return
        evOrFile?.preventDefault?.()
        evOrFile?.stopPropagation?.()
        this.uploadManager.handleFileSelection(evOrFile)
        if (evOrFile?.target?.value != null) {
            try {
                evOrFile.target.value = ''
            } catch (e) {}
        }
    }

    videoHandler = video => {
        const draftId = this.getDraftAnswer()?.id
        if (!draftId || !video) return
        this.uploadManager.handleFileSelection(video)
    }

    startVideoUploadIfPending = async () => {
        const draftId = this.getDraftAnswer()?.id
        if (!draftId) return
        await this.uploadManager.startPendingVideoUpload(draftId)
    }

    cancelDraftRecording = () => {
        const draftAnswer = this.getDraftAnswer()
        if (!draftAnswer?.id) return
        this.uploadManager.cancelRecordingUpload()
        const recId = draftAnswer.recording
        if (typeof recId === 'string' && recId) {
            try {
                api.purgePreuploadedBlob(recId)
            } catch (e) {}
        }
        try {
            const url = draftAnswer.recording_url
            if (url && typeof url === 'string' && url.startsWith('blob:')) {
                URL.revokeObjectURL(url)
            }
        } catch (e) {}
        cache.updateCachedAnswer(draftAnswer.id, {
            recording: null,
            recording_url: null,
            mp4_recording: null,
            mp4_recording_url: null,
        })
        this.setState(state => ({
            uploadProgress: { ...state.uploadProgress, recording: null },
        }))
    }

    cancelDraftImage = () => {
        const draftAnswer = this.getDraftAnswer()
        if (!draftAnswer?.id) return
        try {
            const url = draftAnswer.local_answer_image_url || draftAnswer.answer_image_url
            if (url && typeof url === 'string' && url.startsWith('blob:')) {
                URL.revokeObjectURL(url)
            }
        } catch (e) {}
        cache.updateCachedAnswer(draftAnswer.id, {
            answer_image: null,
            answer_image_url: null,
            local_answer_image_url: null,
        })
        this.forceUpdate()
    }

    cancelDraftFile = () => {
        const draftAnswer = this.getDraftAnswer()
        if (!draftAnswer?.id) return
        const firstFile = draftAnswer.files?.[0]
        const fileApiAvailable = typeof File !== 'undefined'
        const firstName =
            draftAnswer.file_urls?.[0]?.name ||
            (fileApiAvailable && firstFile instanceof File ? firstFile.name : null) ||
            null
        this.uploadManager.cancelFileUpload(firstName || undefined)
        const signedIds = Array.isArray(draftAnswer.files)
            ? draftAnswer.files.filter(
                  /** @param {string | File} id */ id => typeof id === 'string',
              )
            : []
        signedIds.forEach(
            /** @param {string} id */ id => {
                try {
                    api.purgePreuploadedBlob(id)
                } catch (e) {}
            },
        )
        try {
            const urls = (draftAnswer.file_urls || []).map(u => u?.url).filter(Boolean)
            urls.forEach(url => {
                if (typeof url === 'string' && url.startsWith('blob:')) {
                    try {
                        URL.revokeObjectURL(url)
                    } catch (e) {}
                }
            })
        } catch (e) {}
        cache.updateCachedAnswer(draftAnswer.id, {
            files: [],
            file_urls: [],
            file_previews: [],
        })
        this.setState(state => {
            const next = { ...(state.uploadProgress?.files || {}) }
            if (firstName) delete next[firstName]
            return {
                uploadProgress: { ...state.uploadProgress, files: next },
                waitingForEmbeds: false,
                modelPreviewProgress: 0,
            }
        })
    }

    getHiddenPreviewUrls = () => this.state.hiddenPreviewUrls || []

    getPersistedSuppressedPreviewUrls = () => {
        const draftAnswer = this.getDraftAnswer()
        return draftAnswer?.suppressed_preview_urls || []
    }

    getVisibleKnovEmbedTargets = (deltaJson = this.getDeltaJson() || {}) => {
        return getKnovEmbedTargets(
            answerModel.getVisibleKnovEmbedLinks(deltaJson, this.getHiddenPreviewUrls()),
        )
    }

    filterEmbedsForCurrentVisibility = (embeds = [], deltaJson = this.getDeltaJson() || {}) => {
        const visibleTargets = this.getVisibleKnovEmbedTargets(deltaJson)
        return (embeds || []).filter(embed => {
            const resolved = cache.getCachedAnswer(embed?.id) || embed
            return visibleTargets.some(target => answerMatchesKnovEmbedTarget(resolved, target))
        })
    }

    setHiddenPreviewUrls = nextUrls => {
        this.setState({ hiddenPreviewUrls: [...new Set((nextUrls || []).filter(Boolean))] })
    }

    setPersistedSuppressedPreviewUrls = nextUrls => {
        const draftAnswer = this.ensureDraftAnswer()
        if (!draftAnswer?.id) return
        cache.updateCachedAnswer(draftAnswer.id, {
            suppressed_preview_urls: [...new Set((nextUrls || []).filter(Boolean))],
        })
    }

    pruneHiddenPreviewUrls = () => {
        const allowedUrls = answerModel.getPreviewUrls(this.getDeltaJson() || {})
        const currentHiddenUrls = this.getHiddenPreviewUrls()
        const nextHiddenUrls = pruneSuppressedPreviewUrls(currentHiddenUrls, allowedUrls)
        if (nextHiddenUrls.length !== currentHiddenUrls.length) {
            this.setHiddenPreviewUrls(nextHiddenUrls)
        }
        const currentPersistedUrls = this.getPersistedSuppressedPreviewUrls()
        const nextPersistedUrls = pruneSuppressedPreviewUrls(currentPersistedUrls, allowedUrls)
        if (nextPersistedUrls.length !== currentPersistedUrls.length) {
            this.setPersistedSuppressedPreviewUrls(nextPersistedUrls)
        }
        return nextPersistedUrls
    }

    hidePreviewUrl = targetUrl => {
        if (!targetUrl) return
        const nextHiddenUrls = addSuppressedPreviewUrl(this.getHiddenPreviewUrls(), targetUrl)
        const nextPersistedUrls = addSuppressedPreviewUrl(
            this.getPersistedSuppressedPreviewUrls(),
            targetUrl,
        )
        this.setHiddenPreviewUrls(nextHiddenUrls)
        this.setPersistedSuppressedPreviewUrls(nextPersistedUrls)
        this.setState({
            embedsLoading: false,
            embedLoadingPromise: null,
            waitingForEmbeds: false,
        })
    }

    clearAttachedUrl = targetUrl => {
        if (!targetUrl) return

        const draftAnswer = this.ensureDraftAnswer()
        if (!draftAnswer?.id) return
        answerModel.appendUrlToEditorText(this.getEditor(), targetUrl)

        const nextHiddenUrls = addSuppressedPreviewUrl(this.getHiddenPreviewUrls(), targetUrl)
        const nextPersistedUrls = addSuppressedPreviewUrl(
            this.getPersistedSuppressedPreviewUrls(),
            targetUrl,
        )
        const nextPendingClearedPreviewUrls = addSuppressedPreviewUrl(
            this.state.pendingClearedPreviewUrls || [],
            targetUrl,
        )

        this.setState(
            {
                hiddenPreviewUrls: nextHiddenUrls,
                pendingClearedPreviewUrls: nextPendingClearedPreviewUrls,
                embedsLoading: false,
                embedLoadingPromise: null,
                waitingForEmbeds: false,
            },
            () => {
                this.setPersistedSuppressedPreviewUrls(nextPersistedUrls)
                cache.updateCachedAnswer(draftAnswer.id, {
                    url: null,
                    url_id: null,
                })
            },
        )
    }

    handleAttachedUrlPreviewClick = targetUrl => {
        if (!targetUrl || answerModel.answerContainsUrlText(this.getDeltaJson() || {}, targetUrl)) {
            return
        }

        const didAppend = answerModel.appendUrlToEditorText(this.getEditor(), targetUrl)
        if (didAppend) {
            return false
        }
    }

    ensureEmbedsForLinks = async knovEmbedLinks => {
        return answerModel.ensureEmbedsForLinks({
            knovEmbedLinks,
            filterEmbeds: embeds =>
                filterEmbedsForKnovEmbedLinks(
                    embeds,
                    knovEmbedLinks,
                    embed => cache.getCachedAnswer(embed?.id) || embed,
                ),
            onEmbedsResolved: nextEmbeds => {
                const draftAnswer = this.ensureDraftAnswer()
                if (draftAnswer?.id) {
                    cache.updateCachedAnswer(draftAnswer.id, { embeds: nextEmbeds })
                }
            },
            onError: () => {
                alert('Unable to create embed. Please try again once it loads.')
            },
            setWaitingForEmbeds: waiting => this.setState({ waitingForEmbeds: waiting }),
        })
    }

    handleDraftModelPreviewProgress = pct => {
        this.setState({ modelPreviewProgress: Math.round(pct || 0) })
    }

    handleDraftModelPreviewCaptureStart = () => {
        this.setState({
            waitingForEmbeds: true,
            modelPreviewProgress: 0,
        })
    }

    handleDraftModelPreviewCaptureEnd = () => {
        this.setState({
            waitingForEmbeds: false,
            modelPreviewProgress: 100,
        })
    }

    handleDraftModelPreviewGenerated = (blob, dataUrl, key = '1x1') => {
        const draftAnswer = this.getDraftAnswer()
        if (!draftAnswer?.id) return
        try {
            const cur = cache.getCachedAnswer(draftAnswer.id)
            const curUrls = Array.isArray(cur?.file_urls) ? cur.file_urls : []
            const newUrls = curUrls.map((u, idx) => {
                if (idx !== 0) return u
                const nextPreviewUrls = { ...(u.preview_urls || {}) }
                nextPreviewUrls[key] = dataUrl
                const fallback = nextPreviewUrls['1x1'] || dataUrl
                return {
                    ...u,
                    preview_urls: nextPreviewUrls,
                    preview_url: fallback,
                }
            })
            const previews = key === '1x1' ? [blob] : cur?.file_previews || []
            cache.updateCachedAnswer(draftAnswer.id, {
                file_urls: newUrls,
                file_previews: previews,
            })
        } catch (e) {
            console.warn('Failed to update file preview in cache:', e)
        }
        this.setState({
            waitingForEmbeds: false,
            modelPreviewProgress: 100,
        })
    }

    clearEdits = () => {
        this.setState({
            files: null,
            video: null,
            videoUrl: null,
            imgPreview: null,
            imgUrl: null,
        })
    }

    // Whether a click on this head post opens its thread. Also stamped on the
    // selectable wrapper (data-title-navigable) so the cursor over a stream
    // card's body text follows the same predicate as the click.
    titleNavigates = () =>
        shouldNavigateQuestTitle({
            isDraft: Boolean(this.props.quest?.is_draft),
            isEditing: Boolean(this.editMode()),
            isSystem: Boolean(this.isSystemUser()),
            leaderboardAnnouncement: Boolean(this.isLeaderboardAnnouncement()),
            radioPlay: this.isRadioPlayAnswer(),
            mediaAnnouncement: this.isMediaAnnouncementAnswer(),
            marketCard: this.isMarketCardAnnouncement(),
            directMessage: isDirectMessageRoom(this.props.quest),
            messageType: this.props.answer?.message_type,
        })

    onClickTitle = ev => {
        const answer = this.props.answer
        const quest = this.props.quest
        if (this.state.showControlsPopup || !this.titleNavigates()) return
        ev.preventDefault()
        ev.stopPropagation()

        // Track clicking on answer gate to open replies
        const tailAnswers = this.props.tailAnswers || []

        window.logEv('CLICK_ANSWER_GATE', {
            answer_id: answer?.id,
            quest_id: quest?.id || answer?.quest_id,
            answer_count: tailAnswers.length,
            has_new_answers: tailAnswers.some(a => answerIsNewSinceQuestVisit(a, quest)),
            has_value_signals: !!(answer?.bsv_locks?.length || answer?.bsv_upvalues?.length),
        })

        this.clearSelection()
        this?.props?.titleAnswerProps?.onClickTitle(ev)
    }

    getAnswerControlsPopupPosition = () => {
        const anchor = this.answerContentContainerRef?.current
        if (!anchor || typeof window === 'undefined') return null

        const rect = anchor.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const width = Math.max(rect.width - ANSWER_CONTROLS_POPUP_SIDE_OFFSET * 2, 0)
        const popupHeight =
            this.answerControlsPopupRef?.current?.getBoundingClientRect()?.height || 0

        let left = rect.left + ANSWER_CONTROLS_POPUP_SIDE_OFFSET
        const maxLeft = Math.max(
            ANSWER_CONTROLS_POPUP_MARGIN,
            viewportWidth - ANSWER_CONTROLS_POPUP_MARGIN - width,
        )
        left = Math.min(Math.max(left, ANSWER_CONTROLS_POPUP_MARGIN), maxLeft)

        const preferredTop = rect.top + ANSWER_CONTROLS_POPUP_TOP_OFFSET
        const maxTop =
            popupHeight > 0
                ? viewportHeight - ANSWER_CONTROLS_POPUP_MARGIN - popupHeight
                : viewportHeight - ANSWER_CONTROLS_POPUP_MARGIN
        const top = Math.max(ANSWER_CONTROLS_POPUP_MARGIN, Math.min(preferredTop, maxTop))

        return {
            top,
            left,
            width,
        }
    }

    updateAnswerControlsPopupPosition = () => {
        if (!this.state.showControlsPopup) return
        const nextPosition = this.getAnswerControlsPopupPosition()
        if (!nextPosition) return

        this.setState(prevState => {
            const prev = prevState.answerControlsPopupPosition
            if (
                prev &&
                Math.abs(prev.top - nextPosition.top) < 1 &&
                Math.abs(prev.left - nextPosition.left) < 1 &&
                Math.abs(prev.width - nextPosition.width) < 1
            ) {
                return null
            }

            return { answerControlsPopupPosition: nextPosition }
        })
    }

    scheduleAnswerControlsPositionUpdate = () => {
        if (!this.state.showControlsPopup) return
        if (this.answerControlsPositionRaf) return

        this.answerControlsPositionRaf = window.requestAnimationFrame(() => {
            this.answerControlsPositionRaf = null
            this.updateAnswerControlsPopupPosition()
        })
    }

    observeAnswerControlsPopup = () => {
        if (typeof ResizeObserver === 'undefined') return

        const popupEl = this.answerControlsPopupRef?.current
        if (!popupEl || this.observedAnswerControlsPopupEl === popupEl) return

        this.answerControlsResizeObserver?.disconnect()
        this.answerControlsResizeObserver = new ResizeObserver(() => {
            this.scheduleAnswerControlsPositionUpdate()
        })
        this.answerControlsResizeObserver.observe(popupEl)
        this.observedAnswerControlsPopupEl = popupEl
    }

    startAnswerControlsPositionTracking = () => {
        this.updateAnswerControlsPopupPosition()
        this.observeAnswerControlsPopup()
        window.addEventListener('resize', this.scheduleAnswerControlsPositionUpdate)
        window.addEventListener('scroll', this.scheduleAnswerControlsPositionUpdate, true)
    }

    stopAnswerControlsPositionTracking = () => {
        window.removeEventListener('resize', this.scheduleAnswerControlsPositionUpdate)
        window.removeEventListener('scroll', this.scheduleAnswerControlsPositionUpdate, true)
        if (this.answerControlsPositionRaf) {
            window.cancelAnimationFrame(this.answerControlsPositionRaf)
            this.answerControlsPositionRaf = null
        }
        this.answerControlsResizeObserver?.disconnect()
        this.answerControlsResizeObserver = null
        this.observedAnswerControlsPopupEl = null
    }

    toggleControlsPopup = () =>
        this.setState(state => {
            const show = !state.showControlsPopup
            return {
                showControlsPopup: show,
                ...(show ? {} : { answerControlsPopupPosition: null }),
            }
        })
    openShareTray = (shareTrayUrl, { preserveClosed = false, resolved = false } = {}) =>
        this.setState(state => {
            if (preserveClosed && !state.showShareTray) return null

            return {
                showShareTray: true,
                shareTrayUrl,
                shareLinkReady: resolved || !gon?.currentUser,
                showControlsPopup: false,
                answerControlsPopupPosition: null,
            }
        })
    closeShareTray = () =>
        this.setState({
            showShareTray: false,
            shareTrayUrl: null,
            shareRewardEnabled: false,
            shareLinkReady: true,
        })
    // Share launcher for in-content report embeds (TDP/sentiment/share-rewards,
    // daily leaders): shares the host post through the standard answer share flow.
    launchEmbedShare = () =>
        launchAnswerShare({
            answer: this.props.answer,
            copyContent: this.copyContent,
            getPublicLink: this.getPublicLink,
            openShareTray: this.openShareTray,
        })
    // Guests who land on a shared report see the same wide button as a join
    // call instead of a share they can't earn from.
    launchEmbedJoin = () =>
        useStore.getState().actions.modalActions.openAuthModal('signup', 'daily_report_join')
    toggleBottomControls = () =>
        this.setState(state => ({ showBottomControls: !state.showBottomControls }))

    isSystemUser = () => {
        const answer = this.props.answer
        return answer?.is_system || answer?.user?.system || answer?.user_id === gon.SYSTEM_USER
    }

    isLeaderboardAnnouncement = () => !!this.props.answer?.leaderboard_announcement

    // Market cards (Ordinals::MarketAnnouncementService) keep the full
    // controls row: upvalues on them route to the card's beneficiary
    // server-side (listing → seller, offer → bidder, sale → buyer).
    // Trust the server-computed flag, which requires a system-authored answer
    // and a persisted market receipt. `message_type` and delta attributes are
    // client-writable and cannot authenticate a financial card on their own.
    isMarketCardAnnouncement = () => this.props.answer?.market_card === true

    blocksSystemInteractions = () =>
        this.isSystemUser() && !this.isLeaderboardAnnouncement() && !this.isMarketCardAnnouncement()

    isSystemQuoteNoti = () => this.isSystemUser() && this.props.answer.action_tags?.quoted

    // Market cards carry their own share pill (the top controls are hidden on
    // system answers in some surfaces); same launcher AnswerTopControls uses.
    launchMarketCardShare = () =>
        launchAnswerShare({
            answer: this.props.answer,
            copyContent: this.copyContent,
            getPublicLink: this.getPublicLink,
            openShareTray: this.openShareTray,
        })

    answerImageRef = React.createRef()

    // The translated text for the viewer's language, from the answer payload or
    // the local translate response (whichever arrived first).
    getTranslationText = () => {
        const lang = this.state.userPreferedLanguage
        return (
            this.props.answer?.content_translated?.[lang] ??
            this.state.translationResult?.[lang] ??
            null
        )
    }

    handleToggleTranslation = () => {
        if (this.state.showTranslation) {
            // The body swap changes row height, so nudge the virtualizer either way.
            this.setState(
                { showTranslation: false, translationLoading: false },
                this.handleContentLayoutSettled,
            )
            return
        }
        this.handleShowTranslation()
    }

    handleShowTranslation = async () => {
        const { answer } = this.props
        const targetLanguage = this.state.userPreferedLanguage

        if (this.getTranslationText()) {
            this.setState(
                { showTranslation: true, translationLoading: false, translationError: false },
                this.handleContentLayoutSettled,
            )
            return
        }

        this.setState({ showTranslation: true, translationLoading: true, translationError: false })
        try {
            const result = await api.translateAnswer(answer.id, targetLanguage)
            if (!result?.success || !result.translated_content) {
                throw new Error(result?.error || 'translation_failed')
            }

            // Merge the response into the cached answer directly instead of
            // waiting on the answer-update broadcast round trip.
            const merged = {
                ...answer,
                content_translated: result.translated_content,
                ...(result.detected_lang ? { detected_lang: result.detected_lang } : {}),
                ...(result.updated_at ? { updated_at: result.updated_at } : {}),
            }
            cache.cacheAnswer(merged)
            this.props.updateAnswer?.(merged)

            this.setState(
                { translationLoading: false, translationResult: result.translated_content },
                this.handleContentLayoutSettled,
            )
        } catch (e) {
            console.error('Failed to translate answer', answer?.id, e)
            this.setState({
                showTranslation: false,
                translationLoading: false,
                translationError: true,
            })
        }
    }

    // Every reply lands in a quest main thread: the rendered quest for a title,
    // or the answer's child quest for a regular post. Each quest can carry its
    // own reply policy, so resolve the actual target before exposing the editor.
    replyTargetQuest = () =>
        questModel.replyTargetQuestForAnswer(
            this.props.answer,
            this.props.quest,
            this.props.isTitle,
        )

    replyLane = () => {
        const { answer, quest, isTitle } = this.props
        return resolveQuoteMainReplyLane({
            enabled: quoteMainEnabled(),
            isTitle: !!isTitle,
            isQuoteGuest: !!this.props.isQuoteGuest,
            // Titles quote too: the edge names the room's own head.
            isRoomHead:
                !!isTitle &&
                String(quest?.parent_id ?? quest?.parent?.id ?? '') === String(answer?.id ?? ''),
            answerQuestId: answer?.quest_id,
            renderedQuestId: quest?.id,
            canReplyInMainThread: questModel.canUserReplyToAnswer(
                answer,
                quest,
                true,
                gon?.currentUser,
            ),
            canReplyInLegacyBranch: questModel.canUserReplyToAnswer(
                answer,
                quest,
                isTitle,
                gon?.currentUser,
            ),
        })
    }

    canReplyToThisAnswer = () => !!this.replyLane()

    handleReplyClick = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        const replyLane = this.replyLane()
        if (!replyLane) return

        if (!gon?.currentUser) {
            useStore.getState().actions.modalActions.openAuthModal('signup', 'reply_button')
            return
        }

        const { answer, quest, panelContext } = this.props

        window.logEv('CLICK_REPLY_BUTTON', {
            answer_id: answer?.id,
            quest_id: answer?.quest_id,
            thread_depth: answer?.depth || 0,
        })

        // quote_main: the reply pill quotes into the MAIN thread — the reply
        // lands in the room carrying the aloud edge — instead of into the
        // branch. Titles quote too (the edge names the room's own head); the
        // lane only applies when the rendering quest is the room actually
        // containing this answer, headed by it, or importing it as a quote.
        if (replyLane === 'quote-main') {
            // The trailing message's reply already has a composer directly
            // below it. Opening a modal over the thread to quote the row you
            // can still see is ceremony; put the quote in the composer, draw
            // the line between the two, and let the reader type.
            if (this.canQuoteInComposer()) {
                this.props.onQuoteInPlace(answer)
                return
            }
            this.openQuoteMainComposer(answer, quest, panelContext)
            return
        }

        // Both titles and regular messages reply to child quests
        let targetQuest = null

        if (answer.child_quests && answer.child_quests.length > 0) {
            // This answer already has a child quest. Use the same resolved target
            // whose policy was checked before the reply control was rendered.
            targetQuest = this.replyTargetQuest()

            // Ensure the quest has sorted_answers initialized
            if (targetQuest && !targetQuest.sorted_answers) {
                targetQuest = { ...targetQuest, sorted_answers: [] }
            }
        } else {
            // This shouldn't happen in normal flow, but handle it gracefully
            console.warn('Answer has no child quest for reply')
            return
        }

        // Check if a draft already exists for this quest (e.g., from in-thread compose or previous modal)
        const draftId = draftAnswerStore.getDraftId(targetQuest.id)
        /** @type {import('../../../types/answers').AnswerOrDraft | null} */
        let draftAnswer = draftId ? cache.getCachedAnswer(draftId) : null

        if (!draftAnswer) {
            // No existing draft, create a new one
            draftAnswer = answerModel.newAnswer({
                quest_id: targetQuest.id,
                space_id: targetQuest.space_id || quest.space_id,
                is_draft: true,
            })

            // Cache the draft answer so it can be hydrated
            cache.cacheAnswer(draftAnswer)

            // Store draft ID in store
            draftAnswerStore.setDraft(targetQuest.id, draftAnswer.id)
        }

        // Open the modal with NewAnswer component
        useStore.setState({
            isModalOpen: true,
            modalParams: {
                type: 'NewAnswer',
                noHeader: true,
                quest: targetQuest,
                // The modal's crumb names the thread the reader is in, not the
                // replied-to message's branch the reply posts into: that
                // branch's head is the quote below.
                crumbQuest: quest,
                newAnswer: draftAnswer, // Pass the draft answer
                parentAnswer: answer,
                showReplyTo: true,
                user: currentUserSnapshot(),
                showUser: true,
                showUsername: false,
                // Pass a callback to handle answer addition
                onAnswerPosted: newAnswer => {
                    // Close the modal immediately
                    useStore.setState({ isModalOpen: false })
                    if (!newAnswer?.id) return

                    // Replying to a message from inside its thread still opens
                    // the branch it just created — going there IS the reply's
                    // point. Everywhere else posting is not a navigation
                    // intent, so the panel offers the trip instead: a reveal in
                    // place for a title reply, "View reply" from a stream card
                    // where there is nothing here to scroll to.
                    if (this.props.isActive && !this.props.isTitle) {
                        this.props.openRight({ questId: targetQuest.id, answerId: newAnswer.id })
                        return
                    }
                    panelContext?.offerAnswerJump?.(newAnswer.id, targetQuest?.id)
                },
                customStyles: {
                    width: '50dvw',
                    minWidth: '600px',
                    maxWidth: '1000px',
                    maxHeight: '80dvh',
                    left: '50%',
                    transform: 'translate(-50%, 0%)',
                },
                mobileCustomStyles: {
                    width: '95dvw',
                    maxWidth: '95dvw',
                    minWidth: 'unset',
                    left: '50%',
                    transform: 'translate(-50%, 0%)',
                    top: '2%',
                },
            },
        })
    }

    // quote_main: the reply modal aimed at the MAIN thread with the aloud
    // edge pre-attached. Mirrors the legacy branch-reply modal — same draft
    // store, same reply-to preview — but the target quest is the room itself
    // and the send carries reply_to_answer_id.
    openQuoteMainComposer = (answer, quest, panelContext) => {
        const draftId = draftAnswerStore.getDraftId(quest.id)
        /** @type {import('../../../types/answers').AnswerOrDraft | null} */
        let draftAnswer = draftId ? cache.getCachedAnswer(draftId) : null

        if (!draftAnswer) {
            draftAnswer = answerModel.newAnswer({
                quest_id: quest.id,
                space_id: quest.space_id,
                is_draft: true,
            })
            cache.cacheAnswer(draftAnswer)
            draftAnswerStore.setDraft(quest.id, draftAnswer.id)
        }

        useStore.setState({
            isModalOpen: true,
            modalParams: {
                type: 'NewAnswer',
                noHeader: true,
                quest,
                newAnswer: draftAnswer,
                parentAnswer: answer,
                showReplyTo: true,
                replyToAnswerId: answer.id,
                user: currentUserSnapshot(),
                showUser: true,
                showUsername: false,
                onAnswerPosted: newAnswer => {
                    useStore.setState({ isModalOpen: false })
                    if (!newAnswer?.id) return

                    // Same offer from a stream card as from inside the room —
                    // the panel reads where the reply landed and shapes the
                    // pill accordingly.
                    panelContext?.offerAnswerJump?.(newAnswer.id, quest?.id)
                },
                customStyles: {
                    width: '50dvw',
                    minWidth: '600px',
                    maxWidth: '1000px',
                    maxHeight: '80dvh',
                    left: '50%',
                    transform: 'translate(-50%, 0%)',
                },
                mobileCustomStyles: {
                    width: '95dvw',
                    maxWidth: '95dvw',
                    minWidth: 'unset',
                    left: '50%',
                    transform: 'translate(-50%, 0%)',
                    top: '2%',
                },
            },
        })
    }

    // The read side of the aloud edge (quote_main): a quote line naming the
    // target. Clicking or pressing it goes to that message the same way
    // /quest/:questKey/answer/:answerId does — see planQuoteNavigation. The
    // title's quote names a message that is never a row of its own branch, so
    // pressing it opens the quoted message's thread to the right instead.
    // In the room it suppresses itself when the target is the immediately
    // previous visible row (adjacency already carries it). A branch's guest
    // rows always keep their quote line and swap the rail for a ≡ thread glyph
    // — the "originates in the main thread" marker. Opening the guest as its
    // own thread stays the body click, the same door every other row uses.
    // This row answers the row directly above it — exactly when
    // renderSentQuoteLine drops the quote as redundant. The relation is carried
    // by a line down the avatar gutter instead.
    quotesPreviousRow = () => {
        return shouldConnectAdjacentReply({
            enabled: quoteMainEnabled(),
            replyToAnswerId: this.props.answer?.reply_to_answer_id,
            prevAnswerId: this.props.prevAnswerId,
            isQuoteGuest: this.props.isQuoteGuest,
            isEmbed: this.props.isEmbed,
            isReplyPreview: this.props.isReplyPreview,
        })
    }

    // The first reply included. Its half runs from its own top edge — which is
    // the underside of the replies bar's border — down to its avatar, wholly
    // inside the reply. Nothing is drawn above that edge: no line crosses the
    // bar or the title.
    connectsToPreviousRow = () => this.quotesPreviousRow()

    // Only the trailing row has the composer as its neighbour, and only where
    // one is mounted — Quest hands the door down solely in that case. The title
    // qualifies when it IS the trailing row: on a thread with no replies the
    // composer is the next component on screen, so a modal would be showing you
    // a message you are already looking at. Under the bottom editor the
    // composer is pinned on screen at every scroll position, so every row is
    // its neighbour in the sense that matters, and none of them needs a modal.
    canQuoteInComposer = () =>
        !!this.props.onQuoteInPlace &&
        (!!this.props.isLast || !!this.props.composerAlwaysInReach) &&
        !this.props.isEmbed &&
        !this.props.isReplyPreview

    // A reply press that lands in the composer must not take the composer's
    // focus with it. On a phone the keyboard the reader is typing on would go
    // down and come back up around the quote; on a desktop the blur folds the
    // bottom editor before the click arrives, and the folded pill shifts the
    // row out from under the pointer. The reply button keeps focus where it
    // is, the way the composer's own controls do.
    replyPreservesFocus = () => this.replyLane() === 'quote-main' && this.canQuoteInComposer()

    // The pill in the composer is quoting this row, and pressing here is what
    // put it there. Both halves matter: the look belongs to the non-modal lane.
    quoteIsLiveInComposer = () =>
        this.canQuoteInComposer() &&
        !!this.props.composerQuoteAnswerId &&
        String(this.props.composerQuoteAnswerId) === String(this.props.answer?.id)

    // The composer below the thread quotes this row: the same avatar-gutter
    // line, with the composer's byline as its lower end.
    composerRepliesToThis = () => {
        if (!quoteMainEnabled()) return false
        return shouldConnectComposerToRow({
            composerQuoteAnswerId: this.props.composerQuoteAnswerId,
            answerId: this.props.answer?.id,
            isLast: this.props.isLast,
            isTitle: this.props.isTitle,
            isEmbed: this.props.isEmbed,
            isReplyPreview: this.props.isReplyPreview,
        })
    }

    // Whether the "N replies" strip renders under this title. Nothing below it
    // draws a connector, but showThreadInfo reads it and the strip's own
    // conditions are worth having in one place.
    repliesBarRenders = () =>
        !!(
            this.props.isTitle &&
            !this.blocksSystemInteractions() &&
            !this.props.isDndGhost &&
            this.childCount() > 0 &&
            (this.props.isActive || !lightweightStreamCardEnabled(this.props.inStream))
        )

    // Everything that runs the line out of this row's bottom: the next row
    // answering it, or the composer below quoting it. Both exclude the title —
    // the line never crosses it, nor the replies bar under it.
    connectsBelow = () => this.nextRowRepliesToThis() || this.composerRepliesToThis()

    nextRowRepliesToThis = () => {
        if (this.props.isTitle) return false
        const { nextAnswer, answer, quest } = this.props
        return shouldConnectAdjacentReply({
            enabled: quoteMainEnabled(),
            replyToAnswerId: nextAnswer?.reply_to_answer_id,
            prevAnswerId: answer?.id,
            isQuoteGuest:
                !!nextAnswer?.quest_id &&
                !!quest?.id &&
                String(nextAnswer.quest_id) !== String(quest.id),
            isEmbed: this.props.isEmbed,
            isReplyPreview: this.props.isReplyPreview,
        })
    }

    renderOriginMarker = () => {
        if (!quoteMainEnabled() || !this.props.isQuoteGuest) return null
        if (this.props.isEmbed || this.props.isReplyPreview) return null
        const { answer } = this.props
        if (!answer?.id) return null
        return <MainThreadOriginMarker answer={answer} />
    }

    renderSentQuoteLine = () => {
        if (!quoteMainEnabled()) return null
        const { answer } = this.props
        const targetId = answer?.reply_to_answer_id
        if (!targetId) return null
        if (this.props.isEmbed || this.props.isReplyPreview) return null

        const quest = this.props.quest
        const panelHeadAnswerId = quest?.parent_id ?? quest?.parent?.id ?? null
        const actionableStreamCard =
            !this.props.isActive &&
            !!this.props.inStream &&
            (!!this.props.filter?.notifications || !!this.props.filter?.following)
        const parentThreadHeadAnswerId = quest?.grand_parent_id ?? quest?.grand_parent?.id ?? null

        // The parent-thread title is already the card's breadcrumb. Repeating
        // it as a quote pill adds no destination, so suppress that duplicate on
        // the stream cards where quotes are otherwise actionable.
        if (
            actionableStreamCard &&
            parentThreadHeadAnswerId &&
            String(targetId) === String(parentThreadHeadAnswerId)
        ) {
            return null
        }

        // Notification and Following-feed cards keep an explicit way back to
        // the quoted message even though their thread card is not active.
        // Other stream-card quotes remain inert.
        const revealOnClick = !!this.props.isActive || actionableStreamCard

        // A main-thread guest's quote is a regular quote of the branch head: it
        // scrolls up to the title and highlights it. Its provenance lives in the
        // gutter marker (renderOriginMarker), not in the quote line.
        if (this.props.isQuoteGuest) {
            return (
                <SentReplyQuote
                    targetAnswerId={String(targetId)}
                    revealOnClick={revealOnClick}
                    panelHeadAnswerId={panelHeadAnswerId}
                    panelHeadQuestId={quest?.parent?.quest_id}
                />
            )
        }

        if (this.quotesPreviousRow()) return null
        return (
            <SentReplyQuote
                targetAnswerId={String(targetId)}
                revealOnClick={revealOnClick}
                fromPanelHead={!!this.props.isTitle || actionableStreamCard}
                panelHeadAnswerId={panelHeadAnswerId}
                panelHeadQuestId={quest?.parent?.quest_id}
            />
        )
    }

    renderAnswerControlsPopup = (
        answer,
        isBsv,
        isTitle,
        showControlButtons,
        systemQuoteNoti,
        hovered,
    ) => {
        if (!this.state.showControlsPopup || this.state.showBottomControls) return null
        if (typeof document === 'undefined') return null

        const position =
            this.state.answerControlsPopupPosition || this.getAnswerControlsPopupPosition()
        if (!position) return null

        return createPortal(
            <div
                ref={this.answerControlsPopupRef}
                className={styles.answerControlsPopupContainer}
                data-testid="answer-controls-popup"
                style={{
                    position: 'fixed',
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    width: `${position.width}px`,
                    right: 'auto',
                }}
            >
                <AnswerControls
                    answer={answer}
                    isBsv={isBsv}
                    isTitle={isTitle}
                    answerHover={hovered}
                    quest={this.props.quest}
                    filter={this.props.filter}
                    addAnswer={this.props.addAnswer}
                    removeAnswer={this.props.removeAnswer}
                    deleteQuest={this.props.deleteQuest}
                    muteQuestUser={this.props.muteQuestUser}
                    reloadQuest={this.props.reloadQuest}
                    tweet={this.props.tweet}
                    editMode={this.editMode()}
                    archiveConfirm={this.state.archiveConfirm}
                    deleteConfirm={this.state.deleteConfirm}
                    toggleEdit={this.toggleEdit}
                    toggleArchiveConfirm={this.toggleArchiveConfirm}
                    toggleDeleteConfirm={this.toggleDeleteConfirm}
                    toggleControlsPopup={this.toggleControlsPopup}
                    toggleBottomControls={this.toggleBottomControls}
                    popup
                    onPostMediaAction={this.dispatchPostMediaActionFromAnswer}
                    saving={this.state.saving}
                    savePost={this.savePost}
                    imgHandler={this.fileHandler}
                    fileHandler={this.fileHandler}
                    videoHandler={this.videoHandler}
                    mobileVideoHandler={this.fileHandler}
                    updatePost={this.updatePost}
                    showControlButtons={showControlButtons}
                    showBranching={this.props.showBranching}
                    copyContent={this.copyContent}
                    getPublicLink={this.getPublicLink}
                    content={this.getContent()}
                    selectedAnswer={this.props.selectedAnswer}
                    setImageDownload={this.props.setImageDownload}
                    showEmbedButton={this.props.showEmbedButton}
                    panel={this.props.panel}
                    systemQuoteNoti={systemQuoteNoti}
                    openShareTray={this.openShareTray}
                />
            </div>,
            document.body,
        )
    }

    render() {
        const debug = this.props.DEBUG
        const answer = this.props.answer
        const draftAnswer = this.getDraftAnswer()

        // Check if user is muted (but not if it's a system user)
        const isMuted =
            !this.isSystemUser() &&
            answer?.user?.id &&
            isUserMuted(answer.user.id) &&
            !this.state.revealMuted

        // Check if answer is deleted
        if (answer?.deleted) {
            return (
                <DeletedAnswer
                    answerId={answer.id}
                    answer={answer}
                    isTitle={this.props.isTitle}
                    onClickTitle={this.props.isTitle ? this.onClickTitle : undefined}
                    toggleSelectedAnswer={this.toggleSelectedAnswer}
                    canToggle={this.canToggle}
                    panelContext={this.props.panelContext}
                />
            )
        }

        if (isDirectMessageHeadAnswer(answer, this.props.quest)) {
            return (
                <DirectMessageHead
                    answerId={answer.id}
                    quest={this.props.quest}
                    variant={this.props.inStream ? 'compact' : 'hero'}
                    onClickTitle={this.props.isTitle ? this.onClickTitle : undefined}
                />
            )
        }

        // Persisted answers only render durable URL attachments in read mode.
        // Raw external links still produce previews while composing/editing.
        let extractedUrls = {
            nonKnovLinks: [],
            streamEmbedLinks: [],
            knovEmbedLinks: [],
            leaderboardEmbedLinks: [],
            tdpEmbedLinks: [],
        }
        let streamEmbedLinks = []
        let leaderboardEmbedLinks = []
        let tdpEmbedLinks = []
        let marketEmbedLinks = []
        const answerDeltaSummary = getDeltaSummary(answer?.delta_json)

        if (this.editMode()) {
            // Preserve code formatting while extracting URLs from the current editor value.
            const currentDelta = this.postAnswer?.current?.getDeltaJson?.() || {}
            extractedUrls = extractUrls(currentDelta)
            // Keep stream links separate from regular link previews
            streamEmbedLinks = extractedUrls.streamEmbedLinks || []
            leaderboardEmbedLinks = extractedUrls.leaderboardEmbedLinks || []
            tdpEmbedLinks = extractedUrls.tdpEmbedLinks || []
            marketEmbedLinks = this.isMarketCardAnnouncement()
                ? extractedUrls.marketEmbedLinks || []
                : []
        } else if (answer?.delta_json && !answerDeltaSummary.oversized) {
            const extractedAnswerUrls = extractUrls(answer.delta_json)
            extractedUrls = {
                ...extractedAnswerUrls,
                nonKnovLinks: getTwitterLinkPreviews(extractedAnswerUrls),
            }
            // Keep stream links separate from regular link previews
            streamEmbedLinks = extractedAnswerUrls.streamEmbedLinks || []
            leaderboardEmbedLinks = extractedAnswerUrls.leaderboardEmbedLinks || []
            tdpEmbedLinks = extractedAnswerUrls.tdpEmbedLinks || []
            marketEmbedLinks = this.isMarketCardAnnouncement()
                ? extractedAnswerUrls.marketEmbedLinks || []
                : []
        }
        const activeHiddenPreviewUrls = answerModel.mergeHiddenPreviewUrls(
            this.getHiddenPreviewUrls(),
            this.getPersistedSuppressedPreviewUrls(),
            this.state.pendingClearedPreviewUrls || [],
            this.state.pendingSuppressedPreviewUrls || [],
        )
        const isEditing = this.editMode()
        streamEmbedLinks = filterSuppressedPreviewUrls(streamEmbedLinks, activeHiddenPreviewUrls)
        leaderboardEmbedLinks = filterSuppressedPreviewUrls(
            leaderboardEmbedLinks,
            activeHiddenPreviewUrls,
        )
        tdpEmbedLinks = filterSuppressedPreviewUrls(tdpEmbedLinks, activeHiddenPreviewUrls)
        marketEmbedLinks = filterSuppressedPreviewUrls(marketEmbedLinks, activeHiddenPreviewUrls)
        const attachmentState = answerModel.getComposeAttachmentState({
            answer,
            draftAnswer,
            isEditing,
            extractedUrls,
            hiddenPreviewUrls: activeHiddenPreviewUrls,
            resolveEmbedAnswer: embed => cache.getCachedAnswer(embed?.id) || embed,
        })
        const isStreamInviteAnswer = !!(
            answer?.delta_json?.stream_invite || answer?.delta_json?.host_invite
        )
        const linkPreviews = isStreamInviteAnswer ? [] : attachmentState.linkPreviews
        const attachedUrl = isStreamInviteAnswer ? null : attachmentState.attachedUrl

        const quest = this.props.quest
        //console.log('ANSWER RENDERING', answer.id, answer)
        const postClass = answer?.is_post ? 'post' : ''
        const videoId = this.getYouTubeVideoId()
        const vidLoaded = this.state.youtubeLoadedVideoId === videoId
        const childCount = this.childCount()
        const jobStatus = this?.props?.answerJobs?.[answer?.id]
        const isAiRunJobStatus = !!(jobStatus?.ai_run_id || jobStatus?.run_id)
        const showJobStatus = jobStatus && !isAiRunJobStatus

        let mobileSelectedStyles
        const isAnswerSelected = this.isSelected()
        if (isAnswerSelected && isMobile) mobileSelectedStyles = styles.mobileSelected

        const fileAnswer = attachmentState.attachmentAnswer

        const imageUrl =
            attachmentState.imageUrl &&
            (isEditing
                ? attachmentState.imageUrl
                : `${window.knovApiUrl}/api/v1/answers/${answer?.id}/image`)
        const fileUrl = attachmentState.fileUrl
        const videoUrl = attachmentState.videoUrl
        const uploadProgress =
            /** @type {{ recording: number | null, files: Record<string, number> }} */ (
                this.state.uploadProgress || { recording: null, files: {} }
            )
        const recordingProgress = uploadProgress.recording
        const fileProgressMap = uploadProgress.files || {}
        const firstFileName =
            fileAnswer?.file_urls?.[0]?.name || fileAnswer?.files?.[0]?.name || null
        const fileProgress = firstFileName != null ? fileProgressMap[firstFileName] : null
        const firstFileUrl = fileAnswer?.file_urls?.[0]?.url || null
        const hasPendingRecording =
            isEditing && draftAnswer?.recording && this.isPendingUploadValue(draftAnswer.recording)
        const hasPendingFiles =
            isEditing &&
            Array.isArray(draftAnswer?.files) &&
            draftAnswer.files.some(file => this.isPendingUploadValue(file))
        const hasUploadingRecording = recordingProgress != null && recordingProgress < 100
        const hasUploadingFiles = Object.values(fileProgressMap).some(
            pct => pct != null && pct < 100,
        )
        const uploadBlocked =
            isEditing &&
            (hasPendingRecording || hasPendingFiles || hasUploadingRecording || hasUploadingFiles)
        const showRecordingCancel =
            isEditing && (hasPendingRecording || hasUploadingRecording || !!videoUrl)
        const showFileCancel = isEditing && (hasPendingFiles || hasUploadingFiles || !!fileUrl)
        const showImageCancel = isEditing && !!fileAnswer?.answer_image_url

        const systemUser = this.isSystemUser()
        const systemQuoteNoti = this.isSystemQuoteNoti()
        // Artifact wrapper cards render the artwork flush to the card's top
        // edge (the ArtifactBadge header owns all title chrome above the
        // controls row), so the standard content top padding is dropped.
        const isArtifactWrapper = isArtifactWrapperAnswer(answer)
        const answerContentContainerStyle = systemQuoteNoti
            ? styles.systemUserContentContainer
            : styles.answerContentContainer
        const isTitle = this.props.isTitle
        const stylesTitleContentContainer = isTitle ? styles.titleContentContainer : null
        const isEmbed = this.props.isEmbed
        const answerContentContainerGeometryStyle =
            !systemQuoteNoti && !isEmbed && !isArtifactWrapper
                ? { paddingTop: ANSWER_CONTENT_TOP_PADDING_PX }
                : null
        const stylesEmbed = isEmbed ? styles.embed : null
        const stylesEmbedStatusContainer = isEmbed ? styles.embedStatusContainer : null
        const stylesTitleYouTubeSpacer = isTitle ? styles.titleYouTubeSpacer : null
        const hasAnnotation = answerModel.hasAnnotation(this.props.answer, this.getEditor())
        const showPostAnswer = !(systemUser && answer.action_tags?.quoted)
        const storyClass =
            this.props.isStory && !this.props.isMobile ? 'desktop-story' : 'mobile-story'
        const storyStyles = this.props.isStory ? styles.story : null
        const isActive = this.props.isActive
        const payloadContainerStyles = isMobile
            ? cn(
                  styles.mobilePayloadContainer,
                  isNativeIOSApp() && styles.nativeIOSPayloadContainer,
              )
            : styles.payloadContainer

        const streamImagePayloadContainerStyles = isActive
            ? null
            : styles.streamImagePayloadContainer
        const embedImagePayloadStyles = this.props.isEmbed ? styles.embedImagePayload : null

        const showVideo = fileAnswer?.mp4_recording_url || fileAnswer?.recording_url
        const aiRunPayloadItems = this.buildAiRunPayloadItems(answer)
        const aiRunOutputUrls = aiRunPayloadItems.map(item => item.url).filter(Boolean)
        const legacyPayloadItems = this.buildLegacyPayloadItems({
            fileAnswer,
            showVideo,
            showImageCancel,
            showRecordingCancel,
            showFileCancel,
            videoUrl,
            excludedUrls: aiRunOutputUrls,
        })
        const allPayloadItems = this.applyAiActionInputLabels(
            answer,
            sortMediaByCreation([...aiRunPayloadItems, ...legacyPayloadItems]),
        )
        // AI-action inputs are provenance: they render as the compact thumbnail strip above
        // the payload instead of full media tiles, and they don't consume output tile slots.
        // Mirrored by the estimators (answerGeometryProjection aiInputMediaCount +
        // answerPlaceholderHeight BUCKETS_AI_INPUT_STRIP_HEIGHT) — keep both sides aligned.
        const { aiInputMediaItems, payloadItems } = partitionAiInputMediaItems(allPayloadItems)
        const aiInputItemMatches = predicate =>
            allPayloadItems.some(item => item.aiInputRole && item.url && predicate(item))
        const fallbackImageIsAiInput = aiInputItemMatches(item =>
            String(item.id || '').startsWith('answer-image'),
        )
        const recordingIsAiInput = aiInputItemMatches(item => item.id === 'answer-video')
        const showAiInputMediaStrip = aiInputMediaItems.length > 0 && !this.props.isReplyPreview
        const showPayloads = payloadItems.length > 0 && !this.props.isReplyPreview
        const singleActiveStatusPayload = isSingleActiveStatusPayload(payloadItems)
        const hasCreatorFailurePreviewPayload = payloadItems.some(
            item => item.status === 'failed' && item.creatorFailurePreview,
        )
        const canRetryAiGeneration = this.isCurrentUserAnswerOwner(answer)
        const combinedPayloadPair = combinedMediaEnabled()
            ? combinedMediaPair(payloadItems, item => mediaKindFromUrl(item))
            : null
        const attributedPayloadItems = combinedPayloadPair
            ? [combinedPayloadPair.image, combinedPayloadPair.audio]
            : payloadItems.length === 1 && (payloadItems[0]?.url || payloadItems[0]?.render)
            ? [payloadItems[0]]
            : []
        const singlePayloadModelLabel =
            Array.from(
                new Set(
                    attributedPayloadItems
                        .map(
                            item =>
                                item?.modelLabel ||
                                (item?.tagPreview ? item?.label || item?.tagLabel : null),
                        )
                        .filter(Boolean),
                ),
            ).join(' · ') || null
        const singlePayloadMetadata = Array.from(
            new Set(
                attributedPayloadItems.flatMap(item =>
                    Array.isArray(item?.metadata) ? item.metadata.filter(Boolean) : [],
                ),
            ),
        )
        const singleSelfFramedPayload =
            payloadItems.length === 1 &&
            (payloadItems[0]?.url || payloadItems[0]?.render) &&
            ['audio', 'file'].includes(payloadItems[0]?.kind)
        const payloadRowKinds = new Set(
            payloadItems
                .map(item => payloadRowKind(item))
                .filter(kind => ['visual', 'audio', 'file'].includes(kind)),
        )
        const multiMediaTypePayload = !combinedPayloadPair && payloadRowKinds.size > 1
        const mixedFilePayload = multiMediaTypePayload && payloadRowKinds.has('file')
        // Multi-item grids draw a hairline per tile (PayloadContainer tile rings), so the
        // outer answer frame goes bare — one border per media, no frame-within-frame.
        // Single-media payloads keep the framed container: there the container is the
        // border, not the media (its tile ring is suppressed in
        // payload-container.module.scss).
        const bareMediaPayload = payloadItems.length > 1
        const standaloneFileUrl = typeof fileUrl === 'string' ? fileUrl : fileUrl?.url
        const standaloneFileKind = fileUrl ? this.aiRunOutputKind(fileUrl) : null
        const showStandaloneFile =
            fileUrl &&
            standaloneFileKind !== 'audio' &&
            !aiInputItemMatches(item =>
                item.url && standaloneFileUrl
                    ? areSamePreviewUrl(item.url, standaloneFileUrl)
                    : false,
            ) &&
            !payloadItems.some(
                item =>
                    item.url && standaloneFileUrl && areSamePreviewUrl(item.url, standaloneFileUrl),
            )

        const activeTitleStyles = isActive && isTitle ? styles.activeTitleStyles : null

        const isCenter = isPanelCenter(this.props.panelContext.panelId)
        const isCenterActiveTitle = isActive && isTitle && isCenter

        // Apply hover ONLY if the state.hover is true and it's not the centered active title.
        const hovered = this.isHovered()
        const hoverClass =
            hovered && !isCenterActiveTitle && !this.state.moneyControlHover ? styles.hover : null

        const mobileStyles = isMobile && !isTitle && !isEmbed ? styles.mobileAnswerComp : null
        const chatGptStyles =
            answer.message_type === 'chatgpt' || answer.message_type === 'agent'
                ? styles.chatGpt
                : null
        const isPollAnswer = answer.message_type === 'poll'

        const showSystemQuotedHeader = systemUser && answer.action_tags?.['quoted']
        let embedContainerStyles
        if (isTitle) embedContainerStyles = styles.titleEmbedContainer
        else if (showSystemQuotedHeader) embedContainerStyles = styles.systemQuotedEmbedContainer
        else embedContainerStyles = styles.embedContainer
        // Static geometry pins each in-content embed family (stream/leaderboard/TDP widgets +
        // the text-derived tweet preview) to a fixed-height window so rendered height ≡ the
        // constant the estimator reserves (see estimateContentEmbedsHeight in
        // lib/answerPlaceholderHeight) even as the widgets hydrate/grow. Flag-off rendering is
        // untouched.
        const staticContentEmbedWindows = mediaBucketsEnabled()
        const contentEmbedWindowStyle = windowPx =>
            staticContentEmbedWindows ? { height: `${windowPx}px` } : null
        const contentEmbedWindowClass = staticContentEmbedWindows ? styles.contentEmbedWindow : null
        const embedOpenMode = resolvePostEmbedOpenMode({
            isSystemUser: systemUser,
            isSystemQuest: !!this.props.quest?.is_system,
            isQuoteNotification: !!showSystemQuotedHeader,
            isLeaderboardAnnouncement: this.isLeaderboardAnnouncement(),
        })
        // The station's "Added from" reply points at the thread a song came
        // from. A full embed there twins the card the room is already headed
        // by, so it names the origin in one row instead.
        // The song thread's airplay notice points at the latest play room the
        // same way: one row, not a twin of that room's own card.
        const thinEmbeds = Boolean(
            answer?.action_tags?.radio_origin ||
                answer?.action_tags?.media_announcement_origin ||
                answer?.action_tags?.radio_airplay,
        )

        const urlAnswerContainerStyles = isEmbed
            ? styles.embedUrlAnswerContainer
            : styles.urlAnswerContainer

        const noSelectClass = isMobile && !this.editMode() && !isTitle ? 'noselect' : ''

        let mobileControlContainerStyles
        let controlContainerStyles
        if (isMobile && this.editMode())
            controlContainerStyles = styles.mobileEditAnswerControlContainer
        else if (isMobile) controlContainerStyles = styles.mobileAnswerControlContainer

        const embeds = attachmentState.embeds
        const visibleKnovEmbedTargets = getKnovEmbedTargets(
            filterSuppressedPreviewUrls(
                extractedUrls.knovEmbedLinks || [],
                activeHiddenPreviewUrls,
            ),
        )
        const embedsFingerprint = generateFingerprint(
            getMatchedKnovEmbedTargetKeys(
                embeds || [],
                visibleKnovEmbedTargets,
                embed => cache.getCachedAnswer(embed?.id) || embed,
            ),
        )
        const attachedUrlAddress = attachmentState.attachedUrlAddress
        const isAttachedLeaderboardEmbed = !!extractLeaderboardEmbedConfig(attachedUrlAddress)
        const isAttachedTdpEmbed = !!extractTdpEmbedConfig(attachedUrlAddress)
        const readOnlyYouTubeUrl = !isEditing ? answer?.url || null : null
        const readOnlyYouTubeVideoId =
            !isEditing && answer?.yt_app ? getUrlModelYoutubeVideoId(readOnlyYouTubeUrl) : null
        const showReadOnlyYouTubePlayer =
            !!readOnlyYouTubeVideoId && this.state.youtubeLoadedVideoId === readOnlyYouTubeVideoId

        const emptyPayload = !(!!readOnlyYouTubeVideoId || showPayloads || embeds?.length > 0)
        const emptyContentStyles =
            !answerDeltaSummary.hasText &&
            !answerDeltaSummary.hasImage &&
            !this.editMode() &&
            emptyPayload
                ? styles.emptyContent
                : null

        const blocksSystemInteractions = this.blocksSystemInteractions()
        const showQuestControls = isTitle && (!blocksSystemInteractions || systemQuoteNoti)
        //const showControls = this.editMode() && (isTitle && (!systemUser || systemQuoteNoti)) // We need this separated out in the case of PostEmbed.
        const showControlButtons = true //this.props.isMobile || this.state.showControlButtons

        const titleControlsContainerStyles = isTitle ? styles.titleControlsContainer : null

        const b = this.props.CONTROL_BUTTONS
        let questionControlButtons = b ? [b.filter, b.sort, b.edit, b.copy, b.play, b.collapse] : []
        if (b && !isMobile) questionControlButtons.push(b.search)
        const isBsv = this.props.isBsv
        const answerSignalsEnabled =
            answer?.upvalues_enabled === true || answer?.free_reactions_enabled === true
        const privateQuestSignalsEnabled =
            quest &&
            questModel.isPrivate(quest) &&
            (quest?.upvalues_enabled !== false || quest?.free_reactions_enabled !== false)
        const usesHybridSignalControls = isBsv || answerSignalsEnabled || privateQuestSignalsEnabled

        // Reply-card anatomy is shared by stream post-lens titles and, behind
        // thread_reply_cards, non-title posts in the active thread. postLens remains the
        // navigation semantic; replyCard only changes presentation.
        const postLens = !!this.props.postLens
        const replyCard = postLens || !!this.props.replyCard
        // dm_chat: this message's place in its run ('head' carries the byline,
        // 'cont' only the body), and the day divider above it.
        const dmChatRun = this.props.dmChatRun || null
        const replyMediaEstimatedWidth =
            mobileReplyMediaEstimatedWidth({
                postLens: replyCard,
                cardWidth: this.props.estimatedWidth,
            }) ?? this.props.estimatedWidth

        // Reply-card anatomy owns its avatar and content columns. Applying the legacy indent
        // preference at the same time would offset the avatar without offsetting the body.
        const isIndentLayout =
            !replyCard &&
            !this.props.isTitle &&
            gon?.currentUser?.space_options?.layout === 'indent'

        const { showTranslation, translationLoading, userPreferedLanguage } = this.state

        const Comp = (
            <div
                ref={this.containerRef}
                id={`answer-${answer?.id}`}
                data-testid="answer-root"
                className={cn(
                    noSelectClass,
                    styles.answerComp,
                    this.props.isQuoteGuest && styles.quoteGuestAnswer,
                    activeTitleStyles,
                    //isCenterActiveTitle && styles.centerActiveTitle,
                    isCenterActiveTitle && 'center-active-title',
                    !isActive && isTitle && styles.streamAnswer,
                    replyCard && styles.replyCardAnswer,
                    replyCard && !isMobile && styles.replyCardAnswerDesktop,
                    dmChatRun && styles.dmChatRow,
                    dmChatRun === 'cont' && styles.dmChatCont,
                    replyCard &&
                        isMobile &&
                        replyMediaEnabled() &&
                        styles.replyCardAnswerMobileMedia,
                    mobileStyles,
                    mobileSelectedStyles,
                    chatGptStyles,
                    storyStyles,
                    isIndentLayout && styles.indentLayout,
                    isTitle ? 'is-title-answer' : '',
                    `answer comp answer-comp partial ${postClass} ${storyClass}`,
                )}
                data-answer-id={answer?.id}
                // dm_chat: opening the conversation rests this row at the bottom
                // (lib/answerTargetScroll).
                data-dm-chat-last={dmChatRun && this.props.isLast ? 'true' : undefined}
                data-connects-up={this.connectsToPreviousRow() ? 'true' : undefined}
                data-connects-down={this.connectsBelow() ? 'true' : undefined}
                data-panel-id={this.props.panelContext.panelId}
                data-quest-id={this.props.answer?.quest_id}
                data-edit-mode={this.editMode()}
                role={postLens ? 'link' : undefined}
                tabIndex={postLens ? 0 : undefined}
                aria-label={
                    postLens
                        ? `Open post${answer?.user?.name ? ` by ${answer.user.name}` : ''}`
                        : undefined
                }
                onPointerDownCapture={this.onExternalInteractionStart}
                onMouseDownCapture={this.onExternalInteractionStart}
                onTouchStartCapture={this.onExternalInteractionStart}
                onClick={ev => {
                    if (isTitle) this.onClickTitle(ev)
                    else this.squashEventInEditMode(ev)
                    this.onExternalTap(ev)
                }}
                onKeyDown={ev => {
                    if (
                        postLens &&
                        ev.target === ev.currentTarget &&
                        (ev.key === 'Enter' || ev.key === ' ')
                    ) {
                        this.onClickTitle(ev)
                    }
                }}
            >
                {!isActive && isTitle && <div className={styles.rightBorder} />}
                {/*<div className={`acted-noti ${actedClass}`} />*/}
                {dmChatRun && this.props.dmChatDay && (
                    <div className={styles.dmChatDay} role="separator">
                        <span>{this.props.dmChatDay}</span>
                    </div>
                )}

                {this.showInsert() && (
                    <div className={styles.insertContainer}>
                        <div className={styles.insertAnswerBtn} onClick={this.insertAnswer}>
                            <i className="fa fa-plus" />
                        </div>
                    </div>
                )}

                {this.props.answer?.archive && (
                    <div className={cn(styles.statusContainer, stylesEmbedStatusContainer)}>
                        <i className="fa fa-folder" /> archived
                    </div>
                )}

                {this.renderOriginMarker()}

                <div
                    ref={this.answerContentContainerRef}
                    style={answerContentContainerGeometryStyle}
                    className={cn(
                        answerContentContainerStyle,
                        stylesTitleContentContainer,
                        stylesEmbed,
                        isArtifactWrapper && styles.artifactWrapperContentContainer,
                    )}
                >
                    <ArtifactBadge answer={answer} />

                    {debug && (
                        <Debug
                            quest={quest}
                            answer={answer}
                            draftAnswer={this.getDraftAnswer()}
                            position={answer.position}
                            ix={this.props.ix}
                            answerUpdatedAt={answer.updated_at?.toString()}
                        />
                    )}

                    {showPostAnswer && (
                        <PostAnswer
                            key={`post-answer-${answer.id}-${this.editMode()}`}
                            userHasParagraphFeature={this.props.userHasParagraphFeature}
                            newUserReplying={this.props.newUserReplying}
                            isIndentLayout={isIndentLayout}
                            isBsv={isBsv}
                            isMuted={isMuted}
                            onRevealMuted={() => this.setState({ revealMuted: true })}
                            ref={this.postAnswer}
                            // postLens keeps the Answer-level title machinery (click nav,
                            // controls, borders) but renders the post body in reply anatomy.
                            isTitle={isTitle && !postLens}
                            postLens={postLens}
                            replyCard={replyCard}
                            dmChatRun={this.props.dmChatRun || null}
                            isTail={this.props.isTail}
                            answer={answer}
                            draftAnswer={this.editMode() ? draftAnswer : null}
                            quest={quest}
                            sentQuoteLine={this.renderSentQuoteLine()}
                            textIsCollapsed={this.props.textIsCollapsed}
                            setTextIsCollapsed={this.props.setTextIsCollapsed}
                            COLLAPSE_POST_LENGTH={this.props.COLLAPSE_POST_LENGTH}
                            showTranslation={showTranslation}
                            translationLoading={translationLoading}
                            translationError={this.state.translationError}
                            translationText={this.getTranslationText()}
                            translationTargetLang={userPreferedLanguage}
                            onToggleTranslation={this.handleToggleTranslation}
                            isEmbed={isEmbed}
                            value={
                                this.editMode()
                                    ? draftAnswer?.delta_json || answer.delta_json
                                    : this.isMarketCardAnnouncement() && marketEmbedLinks.length > 0
                                    ? MARKET_CARD_EMPTY_DELTA
                                    : this.state.value || answer.delta_json
                            }
                            editMode={this.editMode()}
                            savePost={this.savePost}
                            showVotes={this.props.showVotes}
                            answerHover={hovered}
                            updateAnswer={this.props.updateAnswer}
                            removeAnswer={this.props.removeAnswer}
                            saving={this.state.saving}
                            updatePost={this.updatePost}
                            imgHandler={this.fileHandler}
                            showBranching={this.props.showBranching}
                            selectedAnswer={this.props.selectedAnswer}
                            isAnswerSelected={this.isSelected()}
                            setImageDownload={this.setImageDownload}
                            showEmbedButton={this.props.showEmbedButton}
                            setEmbeds={this.setEmbeds}
                            setEmbedsLoading={this.setEmbedsLoading}
                            setLinkPreviews={this.setLinkPreviews}
                            embedsFingerprint={embedsFingerprint}
                            onActionTokenPromptChange={this.handleActionTokenPromptChange}
                            embeds={embeds}
                            panel={this.props.panel}
                            copyContent={this.copyContent}
                            getPublicLink={this.getPublicLink}
                            openShareTray={this.openShareTray}
                            getContent={this.getContent}
                            blog={this.props.blog}
                            showKnovigator={
                                this.state.showKnovigator ||
                                // System posts that bring their own persona
                                // (virtual_user_name/icon: "Treechat Market",
                                // "Treechat Radio") keep it instead of the
                                // generic space branding.
                                (answer.is_system &&
                                    !this.isMarketCardAnnouncement() &&
                                    !answer.virtual_user_icon_url)
                            }
                            toggleShowKnovigator={this.toggleShowKnovigator}
                            uiId={this.uiId}
                            openRight={this.props.openRight}
                            systemUser={systemUser}
                            isStory={this.props.isStory}
                            isReplyPreview={this.props.isReplyPreview}
                            maxCharacters={this.props.maxCharacters}
                            // TopAnswerControls props.
                            toggleEdit={this.toggleEdit}
                            showControlsPopup={this.state.showControlsPopup}
                            toggleControlsPopup={ev => this.setState({ showControlsPopup: true })}
                            archiveConfirm={this.state.archiveConfirm}
                            toggleArchiveConfirm={this.toggleArchiveConfirm}
                            isUserSignedIn={this.isUserSignedIn()}
                            uploadBlocked={uploadBlocked}
                            dragConnector={this.props.dragConnector}
                            showDragHandle={this.props.showDragHandle}
                            dragHandleListeners={this.props.dragHandleListeners}
                            dragHandleRefSetter={this.props.dragHandleRefSetter}
                        />
                    )}

                    {!this.editMode() && isPollAnswer && (
                        <PollAnswer answer={answer} isUserSignedIn={this.isUserSignedIn()} />
                    )}
                </div>

                {!this.editMode() &&
                    !isMuted &&
                    !this.props.isReplyPreview &&
                    answer.payment_receipts?.length > 0 && (
                        <PaymentReceipts
                            answerId={answer.id}
                            receipts={answer.payment_receipts}
                            isLocal={answer.isLocal}
                            replyCard={replyCard}
                            onLayout={this.handleContentLayoutSettled}
                        />
                    )}

                {showAiInputMediaStrip && (
                    <AiInputMediaStrip
                        items={aiInputMediaItems}
                        className={styles.aiInputMediaStrip}
                    />
                )}

                {showPayloads && (
                    <>
                        <div
                            className={cn(
                                payloadContainerStyles,
                                styles.imagePayloadContainer,
                                singleActiveStatusPayload &&
                                    styles.singleActiveStatusPayloadContainer,
                                hasCreatorFailurePreviewPayload &&
                                    styles.creatorFailurePayloadContainer,
                                singleSelfFramedPayload && styles.singleSelfFramedPayloadContainer,
                                multiMediaTypePayload && styles.multiMediaTypePayloadContainer,
                                mixedFilePayload && styles.mixedFilePayloadContainer,
                                bareMediaPayload && styles.bareMediaPayloadContainer,
                                streamImagePayloadContainerStyles,
                                embedImagePayloadStyles,
                            )}
                            onClick={suppressMediaPayloadContentClick}
                        >
                            <ImageRetryButton
                                answerId={answer?.id}
                                answer={answer}
                                quest={this.props.quest}
                            >
                                {({ onClick, isRetrying, retryingAiRunId }) => (
                                    <PayloadContainer
                                        hideSingleModelLabel
                                        onMediaLayoutSettled={this.handleContentLayoutSettled}
                                        postLens={replyCard}
                                        embed={isEmbed}
                                        estimatedWidth={this.props.estimatedWidth}
                                        // Attached media rides the X-style carousel, AI
                                        // generations the grid; the dropdown flips either. The
                                        // embed estimator has no answer id to read an override
                                        // from, so nested embeds pin to the default.
                                        mediaLayout="auto"
                                        mediaLayoutKey={
                                            isEmbed || answer?.id == null ? null : String(answer.id)
                                        }
                                        onMediaLayoutChange={this.handlePayloadMediaLayoutChange}
                                        items={payloadItems.map(item => {
                                            const itemWithRetry =
                                                item.status === 'failed' && canRetryAiGeneration
                                                    ? {
                                                          ...item,
                                                          onRetry: ev =>
                                                              onClick(ev, {
                                                                  aiRunId: item.runId,
                                                                  actionTag: item.actionTag,
                                                                  action: item.action,
                                                                  durationSeconds:
                                                                      item.durationSeconds,
                                                              }),
                                                          isRetrying:
                                                              isRetrying &&
                                                              retryingAiRunId === item.runId,
                                                      }
                                                    : item

                                            const presentedItem = presentRetryingAiRunPayload(
                                                itemWithRetry,
                                                retryingAiRunId,
                                            )

                                            return item.kind === 'image' && item.url
                                                ? {
                                                      ...presentedItem,
                                                      onClick: ev => {
                                                          ev.preventDefault()
                                                          ev.stopPropagation()
                                                          this.openPayloadImageLightbox(
                                                              payloadItems,
                                                              item,
                                                          )
                                                      },
                                                  }
                                                : presentedItem
                                        })}
                                    />
                                )}
                            </ImageRetryButton>
                        </div>
                        {(singlePayloadModelLabel || singlePayloadMetadata.length > 0) && (
                            <div className={styles.externalPayloadModelLabel}>
                                {singlePayloadModelLabel && (
                                    <span className={styles.externalPayloadModelName}>
                                        {singlePayloadModelLabel}
                                    </span>
                                )}
                                {singlePayloadMetadata.map(value => (
                                    <span
                                        className={styles.externalPayloadMetadataChip}
                                        key={value}
                                    >
                                        {value}
                                    </span>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {!this.isYouTube() &&
                    fileAnswer?.answer_image_url &&
                    !fallbackImageIsAiInput &&
                    !showPayloads &&
                    !this.props.isReplyPreview && (
                        <div
                            className={cn(
                                payloadContainerStyles,
                                styles.imagePayloadContainer,
                                streamImagePayloadContainerStyles,
                                embedImagePayloadStyles,
                            )}
                            onClick={suppressMediaPayloadContentClick}
                        >
                            {showImageCancel && (
                                <PreviewCancelButton
                                    title="Remove image"
                                    ariaLabel="Remove image"
                                    testId="edit-answer-image-remove-button"
                                    onClick={this.cancelDraftImage}
                                />
                            )}
                            <AnswerImage
                                ref={this.answerImageRef}
                                uiId={this.uiId}
                                answer={answer}
                                quest={this.props.quest}
                                openRight={this.props.openRight}
                                sourcePanel={this.props.panelContext?.panel}
                                draftAnswer={this.editMode() ? draftAnswer : null}
                                imageSize={this.props.imageSize}
                                isTitle={this.props.isTitle}
                                postLens={replyCard}
                                estimatedWidth={this.props.estimatedWidth}
                                isActive={isActive}
                                path={fileAnswer?.path}
                                downloading={this.state.imageDownload}
                                isEmbed={isEmbed}
                                showDownload={
                                    isMobile && !isEmbed && this.isCurrentUserAnswerOwner(answer)
                                }
                                onMediaLayoutSettled={this.handleContentLayoutSettled}
                                toggle={clickedImageUrl => {
                                    if (clickedImageUrl) {
                                        this.openLightbox(clickedImageUrl)
                                    }
                                }}
                            />
                        </div>
                    )}

                {!!readOnlyYouTubeVideoId && !this.props.isReplyPreview && (
                    <AnswerYouTubePlayer
                        ref={this.youtubePlayerRef}
                        answer={answer}
                        containerClassName={cn(
                            payloadContainerStyles,
                            this.props.isTitle && styles.titlePayloadContainer,
                            styles.youtubeSpacer,
                            stylesTitleYouTubeSpacer,
                        )}
                        isLoaded={showReadOnlyYouTubePlayer}
                        isReplyPreview={this.props.isReplyPreview}
                        onLoadedChange={this.handleYouTubeLoadedChange}
                        url={readOnlyYouTubeUrl}
                        vidStart={this.state.vidStart}
                        videoId={readOnlyYouTubeVideoId}
                    />
                )}

                {showVideo &&
                    !recordingIsAiInput &&
                    !showPayloads &&
                    !this.props.isReplyPreview && (
                        <div
                            className={cn(
                                payloadContainerStyles,
                                styles.videoContainer,
                                this.props.isTitle && styles.titlePayloadContainer,
                            )}
                            onClick={suppressMediaPayloadContentClick}
                        >
                            {showRecordingCancel && videoUrl && (
                                <PreviewCancelButton
                                    title="Cancel video upload"
                                    ariaLabel="Cancel video upload"
                                    testId="edit-answer-video-remove-button"
                                    onClick={this.cancelDraftRecording}
                                />
                            )}
                            <Video
                                //editedVideoUrl={editedVideoUrl}
                                //editedVideoType={this.state.video?.type}
                                answer={fileAnswer}
                                refObj={{
                                    spinnerRef: this.spinnerRef,
                                    downloadRef: this.downloadRef,
                                    videoTagRef: this.videoTagRef,
                                }}
                                isTitle={this.props.isTitle}
                                postLens={replyCard}
                                estimatedWidth={this.props.estimatedWidth}
                                quest={this.props.quest}
                                openRight={this.props.openRight}
                                sourcePanel={this.props.panelContext?.panel}
                                showDownload={this.showDownload}
                                editMode={isEditing}
                                onMediaLayoutSettled={this.handleContentLayoutSettled}
                                onLoadedMetadata={
                                    isEditing ? this.startVideoUploadIfPending : undefined
                                }
                            />
                        </div>
                    )}

                {showStandaloneFile && (
                    <div
                        key={`files-${this.props.panel}-${this.props.answer?.id}`}
                        className={cn(styles.filesContainer)}
                    >
                        {showFileCancel && (
                            <PreviewCancelButton
                                title="Cancel file upload"
                                ariaLabel="Cancel file upload"
                                testId="edit-answer-file-remove-button"
                                onClick={this.cancelDraftFile}
                            />
                        )}
                        <FileEmbed
                            panel={this.props.panel}
                            file={fileUrl}
                            answerId={isEditing ? draftAnswer?.id : answer?.id}
                            canDelete={gon.currentUser?.id === answer?.user_id}
                            showDelete={gon.currentUser?.id === answer?.user_id && isEditing}
                            deleteFile={this.deleteFile}
                            hover={hovered}
                            processingPercent={
                                isEditing ? this.state.modelPreviewProgress : undefined
                            }
                            onModelPreviewProgress={
                                isEditing ? this.handleDraftModelPreviewProgress : undefined
                            }
                            onModelPreviewCaptureStart={
                                isEditing ? this.handleDraftModelPreviewCaptureStart : undefined
                            }
                            onModelPreviewCaptureEnd={
                                isEditing ? this.handleDraftModelPreviewCaptureEnd : undefined
                            }
                            onModelPreviewGenerated={
                                isEditing ? this.handleDraftModelPreviewGenerated : undefined
                            }
                        />
                    </div>
                )}

                {isEditing && fileProgress != null && (
                    <div className={styles.uploadProgressContainer}>
                        <UploadProgress
                            inline={true}
                            percent={fileProgress || 0}
                            message={fileProgress >= 100 ? 'Processing complete' : 'Processing…'}
                            onCancel={this.cancelDraftFile}
                        />
                    </div>
                )}

                {isEditing && recordingProgress != null && (
                    <div className={styles.uploadProgressContainer}>
                        <UploadProgress
                            inline={true}
                            percent={recordingProgress || 0}
                            message={
                                recordingProgress >= 100 ? 'Processing complete' : 'Processing…'
                            }
                            onCancel={this.cancelDraftRecording}
                        />
                    </div>
                )}

                {showJobStatus && <JobStatus jobStatus={jobStatus} />}

                {answer?.dice && (
                    <div className={cn(styles.raffleContainer)}>
                        <Raffle
                            key={`raffle-${answer.id}`}
                            mode={answer.dice.status || 'preview'}
                            upvalues={answer.dice.upvalues || []}
                            titleUserId={answer.dice.title_user_id}
                            answerId={answer.id}
                            diceData={answer.dice}
                        />
                    </div>
                )}

                {!this.props.isEmbed &&
                    !!embeds?.length &&
                    // Treechat Radio cards render the embedded song as the
                    // artwork-style audio card themselves (below).
                    !answer?.action_tags?.radio_play &&
                    !answer?.action_tags?.radio_attribution &&
                    embeds.map((embed, index) => {
                        return (
                            <div
                                // Private placeholders all share the id '__PRIVATE__'.
                                key={embed.id === '__PRIVATE__' ? `__PRIVATE__-${index}` : embed.id}
                                className={cn(embedContainerStyles)}
                                style={{ position: 'relative' }}
                            >
                                {isEditing && (
                                    <PreviewCancelButton
                                        title="Remove embed"
                                        ariaLabel="Remove embed"
                                        testId="edit-answer-embed-remove-button"
                                        onClick={() => {
                                            const targetUrl = (
                                                extractUrls(this.getDeltaJson() || {})
                                                    .knovEmbedLinks || []
                                            ).find(href =>
                                                answerMatchesKnovEmbedTarget(
                                                    cache.getCachedAnswer(embed?.id) || embed,
                                                    extractKnovEmbedTarget(href),
                                                ),
                                            )
                                            this.hidePreviewUrl(targetUrl)
                                        }}
                                    />
                                )}
                                {showSystemQuotedHeader && (
                                    <div className={styles.embedHeaderContainer}>
                                        <div className={cn(styles.knovIcon, styles.notiIcon)}>
                                            {true && <i className="fa fa-bell" />}
                                            {false && <img src={KnovLogo} />}
                                        </div>

                                        <div className={styles.embedHeader}>
                                            This message was quoted.
                                        </div>
                                    </div>
                                )}

                                <PostEmbed
                                    label="QUOTE"
                                    isQuote
                                    panel={this.props.panel}
                                    answer={embed}
                                    parentAnswer={this.props.answer}
                                    containerQuest={this.props.quest}
                                    containerAnswer={this.props.answer}
                                    openMode={embedOpenMode}
                                    editMode={this.editMode()}
                                    delete={this.deleteEmbed}
                                    // Passed down from Quest if Answer is a parent answer and the head/title of a quest.
                                    isTitle={this.props.isTitle}
                                    // Title.
                                    onHeadEnter={this.props.onHeadEnter}
                                    onHeadLeave={this.props.onHeadLeave}
                                    // Answer.
                                    onMouseEnter={this.onMouseEnter}
                                    onMouseLeave={this.onMouseLeave}
                                    onContentLayoutSettled={this.handleContentLayoutSettled}
                                    estimatedWidth={replyMediaEstimatedWidth}
                                    staticGeometry={this.props.staticGeometry}
                                    thin={thinEmbeds}
                                />
                            </div>
                        )
                    })}

                {/* Render stream embeds in both edit and read modes */}
                {streamEmbedLinks &&
                    streamEmbedLinks.length > 0 &&
                    streamEmbedLinks.map((href, i) => {
                        const streamEmbed = extractStreamEmbedConfig(href)
                        if (!streamEmbed) return null
                        return (
                            <div
                                key={`stream-${streamEmbed.id}-${href || i}`}
                                className={cn(styles.embedContainer, contentEmbedWindowClass)}
                                style={{
                                    marginTop: '15px',
                                    position: 'relative',
                                    ...contentEmbedWindowStyle(STREAM_EMBED_WINDOW_PX),
                                }}
                            >
                                {isEditing && (
                                    <PreviewCancelButton
                                        title="Remove channel preview"
                                        ariaLabel="Remove channel preview"
                                        testId="edit-answer-stream-preview-remove-button"
                                        onClick={() => {
                                            const targetUrl = (
                                                extractUrls(this.getDeltaJson() || {})
                                                    .streamEmbedLinks || []
                                            ).find(currentHref => {
                                                const currentStreamEmbed =
                                                    extractStreamEmbedConfig(currentHref)
                                                return (
                                                    areSamePreviewUrl(currentHref, href) ||
                                                    String(currentStreamEmbed?.id || '') ===
                                                        String(streamEmbed.id)
                                                )
                                            })
                                            this.hidePreviewUrl(targetUrl)
                                        }}
                                    />
                                )}
                                <StreamEmbed
                                    streamId={streamEmbed.id}
                                    filter={streamEmbed.filter}
                                    className={
                                        staticContentEmbedWindows
                                            ? styles.contentWindowStreamEmbed
                                            : null
                                    }
                                    onMouseEnter={this.onMouseEnter}
                                    onMouseLeave={this.onMouseLeave}
                                    onContentLayoutSettled={this.handleContentLayoutSettled}
                                />
                            </div>
                        )
                    })}

                {leaderboardEmbedLinks &&
                    leaderboardEmbedLinks.length > 0 &&
                    leaderboardEmbedLinks.map((leaderboardEmbed, i) => {
                        const previewKey =
                            typeof leaderboardEmbed === 'string'
                                ? leaderboardEmbed
                                : leaderboardEmbed.previewUrl ||
                                  getLeaderboardEmbedPreviewKey(leaderboardEmbed)
                        return (
                            <div
                                key={`leaderboard-${previewKey || i}`}
                                className={cn(styles.embedContainer, contentEmbedWindowClass)}
                                style={{
                                    marginTop: '15px',
                                    position: 'relative',
                                    ...contentEmbedWindowStyle(LEADERBOARD_EMBED_WINDOW_PX),
                                }}
                            >
                                {isEditing && (
                                    <PreviewCancelButton
                                        title="Remove leaderboard preview"
                                        ariaLabel="Remove leaderboard preview"
                                        testId="edit-answer-leaderboard-preview-remove-button"
                                        onClick={() => this.hidePreviewUrl(previewKey)}
                                    />
                                )}
                                <LeaderboardMessageEmbed
                                    {...(typeof leaderboardEmbed === 'string'
                                        ? { url: leaderboardEmbed }
                                        : { config: leaderboardEmbed })}
                                    className={
                                        staticContentEmbedWindows
                                            ? styles.contentWindowLeaderboardEmbed
                                            : null
                                    }
                                    onContentLayoutSettled={this.handleContentLayoutSettled}
                                    onShare={
                                        gon?.currentUser && canShareAnswer(this.props.answer)
                                            ? this.launchEmbedShare
                                            : undefined
                                    }
                                    onJoin={gon?.currentUser ? undefined : this.launchEmbedJoin}
                                />
                            </div>
                        )
                    })}

                {tdpEmbedLinks &&
                    tdpEmbedLinks.length > 0 &&
                    tdpEmbedLinks.map((tdpEmbed, i) => {
                        const previewKey =
                            typeof tdpEmbed === 'string'
                                ? tdpEmbed
                                : tdpEmbed.previewUrl || getTdpEmbedPreviewKey(tdpEmbed)
                        return (
                            <div
                                key={`tdp-${previewKey || i}`}
                                className={cn(styles.embedContainer, contentEmbedWindowClass)}
                                style={{
                                    marginTop: '15px',
                                    position: 'relative',
                                    ...contentEmbedWindowStyle(TDP_EMBED_WINDOW_PX),
                                }}
                            >
                                {isEditing && (
                                    <PreviewCancelButton
                                        title="Remove TDP preview"
                                        ariaLabel="Remove TDP preview"
                                        testId="edit-answer-tdp-preview-remove-button"
                                        onClick={() => this.hidePreviewUrl(previewKey)}
                                    />
                                )}
                                <TdpMessageEmbed
                                    {...(typeof tdpEmbed === 'string'
                                        ? { url: tdpEmbed }
                                        : { config: tdpEmbed })}
                                    className={
                                        staticContentEmbedWindows
                                            ? styles.contentWindowTdpEmbed
                                            : null
                                    }
                                    onContentLayoutSettled={this.handleContentLayoutSettled}
                                    onShare={
                                        gon?.currentUser && canShareAnswer(this.props.answer)
                                            ? this.launchEmbedShare
                                            : undefined
                                    }
                                    onJoin={gon?.currentUser ? undefined : this.launchEmbedJoin}
                                />
                            </div>
                        )
                    })}

                {marketEmbedLinks &&
                    marketEmbedLinks.length > 0 &&
                    marketEmbedLinks.map((marketEmbed, i) => {
                        const previewKey = marketEmbed?.previewUrl
                        return (
                            <div
                                key={`market-${previewKey || i}`}
                                className={cn(styles.embedContainer, contentEmbedWindowClass)}
                                style={{
                                    marginTop: '15px',
                                    position: 'relative',
                                    ...contentEmbedWindowStyle(MARKET_EMBED_WINDOW_PX),
                                }}
                            >
                                {isEditing && (
                                    <PreviewCancelButton
                                        title="Remove market preview"
                                        ariaLabel="Remove market preview"
                                        testId="edit-answer-market-preview-remove-button"
                                        onClick={() => this.hidePreviewUrl(previewKey)}
                                    />
                                )}
                                <MarketCardEmbed
                                    config={marketEmbed}
                                    answer={answer}
                                    onShare={
                                        canShareAnswer(answer) ? this.launchMarketCardShare : null
                                    }
                                    onContentLayoutSettled={this.handleContentLayoutSettled}
                                />
                            </div>
                        )
                    })}

                {/* Render link previews (non-stream URLs) only when no image is attached */}
                {!attachedUrlAddress &&
                    linkPreviews &&
                    linkPreviews.length > 0 &&
                    !fileAnswer?.answer_image_url &&
                    linkPreviews
                        .filter(lp => {
                            // don't show link previews if the answer already has a url for this address
                            if (areSamePreviewUrl(this?.props?.answer?.url?.address, lp))
                                return false
                            if (extractLeaderboardEmbedConfig(lp)) return false
                            if (extractTdpEmbedConfig(lp)) return false
                            return true
                        })
                        .slice(0, 1)
                        .map(href => (
                            <div
                                key={href}
                                className={cn(
                                    styles.embedContainer,
                                    // Tweet cards load their data client-side, so only the
                                    // tweet family gets the fixed window (read mode is
                                    // twitter-only; edit-mode generic previews keep their own
                                    // flag-pinned card height inside LinkPreview).
                                    twitterRegexp.test(href) && contentEmbedWindowClass,
                                )}
                                data-testid="answer-link-preview-card"
                                style={{
                                    position: 'relative',
                                    ...(twitterRegexp.test(href)
                                        ? contentEmbedWindowStyle(TEXT_LINK_PREVIEW_WINDOW_PX)
                                        : null),
                                }}
                            >
                                {isEditing && (
                                    <PreviewCancelButton
                                        title="Remove link preview"
                                        ariaLabel="Remove link preview"
                                        testId="edit-answer-link-preview-remove-button"
                                        onClick={() => this.hidePreviewUrl(href)}
                                    />
                                )}
                                <LinkPreview
                                    url={href}
                                    urlModel={this.props.answer.url}
                                    answerId={this.props.answer.id}
                                    inViewCallback={this.props.inViewCallback}
                                    estimatedWidth={replyMediaEstimatedWidth}
                                    // Title.
                                    onHeadEnter={this.props.onHeadEnter}
                                    onHeadLeave={this.props.onHeadLeave}
                                    // Answer.
                                    onMouseEnterAnswer={this.onMouseEnter}
                                    onMouseLeaveAnswer={this.onMouseLeave}
                                />
                            </div>
                        ))}

                {attachedUrlAddress &&
                    // Artifact wrapper cards render their artwork in the
                    // ArtifactBadge header; the generic url card would double it.
                    !isArtifactWrapper &&
                    !activeHiddenPreviewUrls.some(url =>
                        areSamePreviewUrl(url, attachedUrlAddress),
                    ) &&
                    (!isEditing ||
                        linkPreviews.some(url => areSamePreviewUrl(url, attachedUrlAddress)) ||
                        areSamePreviewUrl(fileAnswer?.url?.address, attachedUrlAddress)) &&
                    !getKnovLinkRegexp().test(attachedUrlAddress) &&
                    !attachedUrlAddress?.match(getStreamLinkRegexp()) &&
                    !isAttachedLeaderboardEmbed &&
                    !isAttachedTdpEmbed &&
                    !fileAnswer?.answer_image_url &&
                    (isEditing ? (
                        <div
                            className={styles.linkPreviewsContainer}
                            data-testid="answer-link-preview-card"
                        >
                            <div style={{ marginTop: '15px', position: 'relative' }}>
                                <PreviewCancelButton
                                    title="Remove link preview"
                                    ariaLabel="Remove link preview"
                                    testId="edit-answer-link-preview-remove-button"
                                    onClick={() => this.clearAttachedUrl(attachedUrlAddress)}
                                />
                                <LinkPreview
                                    url={attachedUrlAddress}
                                    urlModel={attachedUrl}
                                    inViewCallback={this.props.inViewCallback}
                                    estimatedWidth={replyMediaEstimatedWidth}
                                    onPreviewClick={() =>
                                        this.handleAttachedUrlPreviewClick(attachedUrlAddress)
                                    }
                                    answerId={fileAnswer?.id || answer?.id}
                                />
                            </div>
                        </div>
                    ) : (
                        <div
                            className={cn(
                                urlAnswerContainerStyles,
                                this.props.isTitle && styles.titleUrlAnswerContainer,
                            )}
                            data-testid="answer-link-preview-card"
                        >
                            <UrlAnswer
                                ref={el => (this.urlAnswer = el)}
                                isTitle={isTitle}
                                answer={fileAnswer?.url ? fileAnswer : answer}
                                onPreviewClick={undefined}
                                // Title.
                                onHeadEnter={this.props.onHeadEnter}
                                onHeadLeave={this.props.onHeadLeave}
                                // Answer.
                                onMouseEnter={this.onMouseEnter}
                                onMouseLeave={this.onMouseLeave}
                                filter={this.props.filter}
                                panel={this.props.panel}
                                hasAnnotation={hasAnnotation}
                                command={this.state.command}
                                vidStart={this.state.vidStart}
                                vidLoop={this.state.vidLoop}
                                quest={this.props.quest}
                                getYtPlayer={this.getYtPlayer}
                                editMode={this.editMode()}
                                vidLoaded={vidLoaded}
                                loadVid={this.loadAndPlay}
                                showVotes={
                                    this.props.showVotes && !hasAnnotation && !this.editMode()
                                }
                                isEmbed={this.props.isEmbed}
                                answerHover={hovered}
                                updateAnswer={this.props.updateAnswer}
                                removeAnswer={this.props.removeAnswer}
                                showEmbedButton={this.props.showEmbedButton}
                                updatePost={this.updatePost}
                                copyContent={this.copyContent}
                                getContent={this.getContent}
                                setImageDownload={this.setImageDownload}
                                showKnovigator={this.state.showKnovigator}
                                toggleShowKnovigator={this.toggleShowKnovigator}
                                uiId={this.uiId}
                                isYoutube={this.isYouTube()}
                                inViewCallback={this.props.inViewCallback}
                                estimatedWidth={replyMediaEstimatedWidth}
                            />
                        </div>
                    ))}

                {this.state.showBottomControls && ( //showControls && (!isTitle || this.editMode()) && (
                    <div
                        className={cn(
                            styles.answerControlContainer,
                            mobileControlContainerStyles,
                            titleControlsContainerStyles,
                            emptyContentStyles,
                        )}
                    >
                        <AnswerControls
                            answer={answer}
                            isTitle={isTitle}
                            answerHover={hovered}
                            quest={this.props.quest}
                            filter={this.props.filter}
                            addAnswer={this.props.addAnswer}
                            removeAnswer={this.props.removeAnswer}
                            deleteQuest={this.props.deleteQuest}
                            muteQuestUser={this.props.muteQuestUser}
                            reloadQuest={this.props.reloadQuest}
                            tweet={this.props.tweet}
                            editMode={this.editMode()}
                            archiveConfirm={this.state.archiveConfirm}
                            toggleArchiveConfirm={this.toggleArchiveConfirm}
                            deleteConfirm={this.state.deleteConfirm}
                            toggleEdit={this.toggleEdit}
                            toggleDeleteConfirm={this.toggleDeleteConfirm}
                            toggleControlsPopup={this.toggleControlsPopup}
                            toggleBottomControls={this.toggleBottomControls}
                            saving={this.state.saving}
                            savePost={this.savePost}
                            imgHandler={this.fileHandler}
                            fileHandler={this.fileHandler}
                            videoHandler={this.videoHandler}
                            mobileVideoHandler={this.fileHandler}
                            updatePost={this.updatePost}
                            showControlButtons={showControlButtons}
                            showBranching={this.props.showBranching}
                            copyContent={this.copyContent}
                            getPublicLink={this.getPublicLink}
                            content={this.getContent()}
                            selectedAnswer={this.props.selectedAnswer}
                            setImageDownload={this.props.setImageDownload}
                            showEmbedButton={this.props.showEmbedButton}
                            panel={this.props.panel}
                            systemQuoteNoti={systemQuoteNoti}
                        />
                    </div>
                )}

                {!this.state.archiveConfirm &&
                    !this.state.deleteConfirm &&
                    !this.props.isEmbed &&
                    // Artifact wrapper titles keep the standard controls row;
                    // their upvalues pay the ordinal's current owner, so the
                    // system-answer interaction block is lifted for them.
                    (!this.blocksSystemInteractions() ||
                        isArtifactWrapperAnswer(this.props.answer)) && (
                        <div
                            ref={this.controlsRowRef}
                            className={cn(
                                styles.voteControlsContainer,
                                this.props.isTitle && styles.titleVoteControlsContainer,
                                this.props.isActive &&
                                    this.props.isTitle &&
                                    styles.activeTitleVoteControlsContainer,
                            )}
                        >
                            {usesHybridSignalControls ? (
                                <AnswerButtons
                                    answer={answer}
                                    quest={quest}
                                    highlight={!isTitle || this.props.inStream}
                                    onReplyClick={
                                        this.canReplyToThisAnswer()
                                            ? this.handleReplyClick
                                            : undefined
                                    }
                                    quoteActive={this.quoteIsLiveInComposer()}
                                    replyPreservesFocus={this.replyPreservesFocus()}
                                    childCount={childCount}
                                    thin
                                    onHoverChange={this.setMoneyControlHover}
                                />
                            ) : (
                                <VoteControl
                                    answer={answer}
                                    highlight={!isTitle || this.props.inStream}
                                    isUserSignedIn={this.isUserSignedIn()}
                                    onReplyClick={
                                        this.canReplyToThisAnswer()
                                            ? this.handleReplyClick
                                            : undefined
                                    }
                                    quoteActive={this.quoteIsLiveInComposer()}
                                    replyPreservesFocus={this.replyPreservesFocus()}
                                    childCount={childCount}
                                />
                            )}
                        </div>
                    )}
            </div>
        )

        const selectableClickHandler = this.props.isTitle
            ? this.onClickTitle
            : this.toggleSelectedAnswer
        const useLightweightStreamCard = lightweightStreamCardEnabled(this.props.inStream)
        // The ThreadInfo strip (reply count/filter) renders only when reply rows
        // exist — an empty thread's "0 replies" line said nothing, on any surface.
        const showThreadInfo = this.repliesBarRenders()
        // Whether this stream title is the card's bottom row. Off-flag, reply previews
        // render whenever children exist; lightweight cards can suppress previews, so
        // follow the rendered row list (isLast) instead of the child count.
        const streamTitleIsCardBottom = useLightweightStreamCard
            ? !!this.props.isLast
            : childCount === 0
        const showTitleAnswerBottomBorder = shouldShowTitleAnswerBottomBorder({
            isCardBottom: streamTitleIsCardBottom,
            lightweightStreamCard: useLightweightStreamCard,
            // bottom_editor: no composer row sits under the thread.
            composerFloats: !!this.props.composerAlwaysInReach,
        })
        const threadInfoProps = showThreadInfo
            ? (() => {
                  const allTailAnswers = questModel.showUnarchivedAnswers(quest)
                  const visibleTailAnswers = getVisibleTailAnswers(this.props.tailAnswers)
                  const replyAnswers =
                      isActive && visibleTailAnswers ? visibleTailAnswers : allTailAnswers

                  return {
                      replyAnswers,
                      showActiveFilterButton:
                          isActive && (replyAnswers.length > 0 || allTailAnswers.length > 0),
                  }
              })()
            : null

        const Selectable = this.props.selectable ? (
            <div
                className={cn('selected-container')}
                data-answer-role={this.props.isTitle ? 'title' : 'answer'}
                data-click-intent={this.props.isTitle ? 'center-panel' : 'open-child-quest'}
                data-title-navigable={
                    this.props.isTitle && this.titleNavigates() ? 'true' : undefined
                }
                {...this.props.openRightPrefetch}
                onPointerDownCapture={ev => {
                    this.pressStartedOnControl = this.isControlsRowControl(ev.target)
                }}
                onClick={selectableClickHandler}
                onMouseDown={ev => {
                    //console.log('onmousedown')
                    const hasSelection = window.getSelection().toString().length > 0
                    // <WithSelection> prevents the event so this wont fire and wont be set.
                    // We set this so we know to clear the selection in this.toggleSelectedAnswer()
                    if (hasSelection) this.clickOutsideSelection = true
                }}
            >
                {/*<div className="selected-frame" />*/}
                <div
                    className={cn(
                        `answer-highlight`,
                        `answer-highlight-${answer?.id}`,
                        styles.answerHighlight,
                        isAnswerSelected && styles.selected,
                        !this.blocksSystemInteractions() &&
                            isTitle &&
                            showTitleAnswerBottomBorder &&
                            styles.titleAnswerHighlight,
                        // dm_chat rows run together like a chat: no card seams.
                        !this.props.dmChatRun &&
                            shouldShowFirstAnswerTopBorder(this.props) &&
                            styles.firstAnswer,
                        // The boundary with the row above, when the connector is
                        // not there to carry it. Flag-gated: without quote_main
                        // no row answers another, and the list has always run
                        // together.
                        quoteMainEnabled() &&
                            !this.props.dmChatRun &&
                            shouldShowReplySeparatorTopBorder({
                                isTitle: this.props.isTitle,
                                isFirst: this.props.isFirst,
                                hasPreviousRow: !!this.props.prevAnswerId,
                                connectsUp: this.connectsToPreviousRow(),
                            }) &&
                            styles.replySeparator,
                        // The seam between the thread and the composer, owned by
                        // the border or by the connector running through it —
                        // never both, never neither.
                        !this.props.dmChatRun &&
                            shouldShowLastAnswerBottomBorder({
                                isTitle: this.props.isTitle,
                                isLast: this.props.isLast,
                                composerConnectsBelow: this.composerRepliesToThis(),
                            }) &&
                            styles.lastAnswer,
                        hoverClass,
                        isTitle &&
                            !isActive &&
                            streamTitleIsCardBottom &&
                            styles.bottomAnswerRadius,
                    )}
                    data-answer-hovered={hoverClass ? 'true' : undefined}
                    onMouseEnter={this.onMouseEnter}
                    onMouseLeave={this.onMouseLeave}
                >
                    {Comp}

                    {this.showGate() && (
                        <div
                            className={cn(
                                styles.answerGateContainer,
                                this.props.isTitle && styles.titleAnswerGateContainer,
                                isTitle &&
                                    !blocksSystemInteractions &&
                                    !showThreadInfo &&
                                    styles.noThreadInfoAnswerGateContainer,
                            )}
                        >
                            <AnswerGate
                                childCount={childCount}
                                hover={hovered}
                                isSelected={this.isSelected()}
                            />
                        </div>
                    )}
                </div>

                {this.renderAnswerControlsPopup(
                    answer,
                    isBsv,
                    isTitle,
                    showControlButtons,
                    systemQuoteNoti,
                    hovered,
                )}
                <AnswerShareTray
                    answer={answer}
                    copyContent={this.copyContent}
                    isOpen={this.state.showShareTray}
                    onDismiss={this.closeShareTray}
                    shareUrl={this.state.shareTrayUrl}
                    rewardEnabled={this.state.shareRewardEnabled}
                    linkReady={this.state.shareLinkReady}
                />

                {showThreadInfo && (
                    <div
                        className={cn(
                            styles.threadInfoContainer,
                            isActive && isCenter && styles.isActiveThreadInfoContainer,
                        )}
                        onClick={ev => {
                            ev.stopPropagation()
                            ev.preventDefault()
                            this.onClickTitle(ev)
                        }}
                    >
                        <ThreadInfo
                            quest={quest}
                            {...threadInfoProps}
                            query={this.props.query}
                            isActive={isActive}
                            answerFilter={this.props.answerFilter}
                            setAnswerFilter={this.props.setAnswerFilter}
                            answerFilterOptions={this.props.answerFilterOptions}
                        />
                    </div>
                )}
            </div>
        ) : (
            <>
                {Comp}

                {isActive && showQuestControls && isTitle && (
                    <div className={cn(styles.questionControlsContainer)}>
                        {questionControlButtons.length > 0 && (
                            <QuestControls
                                quest={quest}
                                toggleEdit={this.toggleEdit}
                                editMode={this.editMode()}
                                addAnswer={this.props.addAnswer}
                                controlButtons={questionControlButtons}
                                isMobile={isMobile}
                                content={this.getContent()}
                                filter={this.props.answerFilter}
                                setShowFilterDropdown={this.props.setShowFilterDropdown}
                                showFilterDropdown={this.props.showFilterDropdown}
                                setShowSortColDropdown={this.props.setShowSortColDropdown}
                                showSortColDropdown={this.props.showSortColDropdown}
                                setShowExtendedOptions={this.props.setShowExtendedOptions}
                                showExtendedOptions={this.props.showExtendedOptions}
                                collapseHandler={this.props.collapseHandler}
                                copyContent={this.copyContent}
                                getPublicLink={this.getPublicLink}
                                CB={this.props.CONTROL_BUTTONS}
                                showEmbedButton={this.props.showEmbedButton}
                                panel={this.props.panel}
                            />
                        )}
                    </div>
                )}
            </>
        )

        return (
            <>
                {Selectable}
                <AppLightbox
                    open={this.state.lightboxOpen}
                    onClose={this.closeLightbox}
                    slides={this.state.lightboxSlides}
                    index={this.state.lightboxIndex}
                    bottomNavigation={this.state.lightboxBottomNavigation}
                />
            </>
        )
    }
}

export default Connector(stateSelector, props => ({
    // Subscribe at the row boundary: hydrating the next post need not change
    // the container's compact list, but must update this connector's upper half.
    nextAnswer: useCachedAnswer(props.nextAnswer?.id) || props.nextAnswer,
    openRight: useOpenRight().openRight,
    // Only known destinations are speculative: resolving/creating a missing child
    // thread remains a click action.
    openRightPrefetch: useOpenRightPrefetch(
        !props.isTitle && props.selectable ? props.answer?.child_quests?.[0]?.id : null,
        props.answer?.id,
    ),
    panelContext: usePanelContext(),
    highlightAnswer: useAnswerHighlight(),
    userHasParagraphFeature: useUserHasFeature('paragraph'),
}))(
    React.forwardRef(
        /** @returns {React.ReactElement} */
        (/** @type {Record<string, any>} */ props, ref) => (
            <ErrorBoundary label={`Answer ${props.answer?.id}`}>
                <Answer ref={ref} {...props} />
            </ErrorBoundary>
        ),
    ),
)

export { AnswerImage }
